import React, { useEffect, useMemo, useRef, useState, type JSX } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import type {
  AdminIncidentLogArtifactAccessAction,
  AdminMediaArtifactAccessAction,
} from "../../contracts/admin-read-models";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import { AdminLocalizedLink } from "../../shared/locale/admin-dashboard-localized-link";
import { withAdminDashboardLocaleSearchParams } from "../../shared/locale/admin-dashboard-locale-routing";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { AdminDataTable } from "../../shared/ui/admin-data-table";
import { AdminDetailPanel } from "../../shared/ui/admin-detail-panel";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminErrorState } from "../../shared/ui/admin-error-state";
import { AdminOperatorExplanationBlock } from "../../shared/ui/admin-operator-explanation-block";
import {
  buildAdminStateLookupMeta,
  buildAdminStateRouteMeta,
} from "../../shared/ui/admin-screen-state-meta";
import {
  AdminStatusBadge,
  toAdminFreshnessBadgeValue,
  toAdminRiskBadgeValue,
} from "../../shared/ui/admin-status-badge";
import {
  appendUserIncidentWorkspaceFocus,
  readUserIncidentWorkspaceFocus,
} from "./incident-workspace-focus";
import {
  appendUserMediaWorkspaceLookupContext,
  appendUserMediaWorkspaceFocus,
  resolveContextualUserMediaWorkspaceSelection,
  readUserMediaWorkspaceFocus,
  resolveUserMediaWorkspaceSelection,
  type UserMediaWorkspaceScenarioKey,
} from "./media-workspace-focus";
import {
  appendUserSameDeviceWorkspaceContext,
  readUserSameDeviceWorkspaceContext,
} from "./same-device-workspace-context";
import { useUserDataDetailScreenModel } from "./use-user-data-detail-screen-model";

export type UserDataDetailScreenProps = {
  dependencies: AdminDashboardDependencies;
};

export function buildUserDetailReopenRoute(input: {
  internalUserId?: string;
  locale: "ko" | "en";
  params: URLSearchParams;
  incidentArtifactIdOverride?: string;
}): string {
  const route = input.internalUserId
    ? `/admin/users/${input.internalUserId}`
    : "/admin/users/:internalUserId";
  const focus = readUserMediaWorkspaceFocus(input.params);
  const incidentFocusFromParams = readUserIncidentWorkspaceFocus(input.params);
  const incidentFocus = input.incidentArtifactIdOverride
    ? {
        artifactId: input.incidentArtifactIdOverride,
      }
    : incidentFocusFromParams;
  const sameDeviceContext = readUserSameDeviceWorkspaceContext(input.params);
  const requestId = input.params.get("requestId")?.trim();
  const correlationId = input.params.get("correlationId")?.trim();

  const contextualized = appendUserSameDeviceWorkspaceContext(
    appendUserIncidentWorkspaceFocus(
      appendUserMediaWorkspaceLookupContext(
        new URLSearchParams(),
        {
          requestId,
          correlationId,
        },
        focus,
      ),
      incidentFocus ?? {},
    ),
    sameDeviceContext ?? {},
  );
  const localized = withAdminDashboardLocaleSearchParams(contextualized, input.locale);
  const suffix = localized.toString();

  return suffix.length > 0 ? `${route}?${suffix}` : route;
}

const cardStyle = {
  padding: "20px",
  borderRadius: "22px",
  background: "rgba(255, 255, 255, 0.82)",
  border: "1px solid rgba(24, 33, 43, 0.08)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
} as const;

const actionButtonStyle = {
  minHeight: "38px",
  padding: "0 14px",
  borderRadius: "999px",
  border: "none",
  background: "#0f766e",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
} as const;

const secondaryActionButtonStyle = {
  ...actionButtonStyle,
  background: "#fff",
  color: "#18212b",
  border: "1px solid rgba(148, 163, 184, 0.45)",
} as const;

type MediaAccessState = {
  action?: AdminMediaArtifactAccessAction;
  artifactId?: string;
  expiresAt?: string;
  issuedAt?: string;
  requestId?: string;
  result?: string;
  status: "idle" | "loading" | "ready" | "error";
  url?: string;
};

type IncidentLogAccessState = {
  action?: AdminIncidentLogArtifactAccessAction;
  artifactId?: string;
  expiresAt?: string;
  issuedAt?: string;
  requestId?: string;
  result?: string;
  status: "idle" | "loading" | "ready" | "error";
  url?: string;
};

type IncidentEvidenceSelectedArtifact = {
  artifactId: string;
  sourceScenarioKey: string;
  sourceScenarioLabel: string;
  relationTypeKey: "direct" | "contextual";
  relationTypeLabel: string;
  contentTypeBucketLabel: string;
  sizeBucketLabel: string;
  uploadedAtLabel: string;
  previewAvailability?: string;
  downloadAvailability?: string;
  previewAvailabilityLabel: string;
  downloadAvailabilityLabel: string;
  binaryReviewPathLabel: string;
  binaryReviewHint: string;
  archiveStateLabel: string;
  artifactRoleLabel: string;
  incidentAttachmentStateLabel: string;
  incidentAttachmentHint: string;
  fallbackReasonLabel: string;
  latestRequestId?: string;
  latestRequestIdLabel: string;
  latestCorrelationId?: string;
  latestCorrelationIdLabel: string;
  requestTraceDrilldownTo?: string;
};

type IncidentEvidenceIncidentLog = {
  artifactId: string;
  deviceInstallId: string;
  releaseLabel: string;
  appVersionLabel: string;
  platformLabel: string;
  runtimeSummaryLabel: string;
  captureTriggerLabel: string;
  captureWindowLabel: string;
  capturedAtBucketLabel: string;
  capturedAtOperatorValueLabel: string;
  supportabilityStateLabel: string;
  excerptLineCountBucketLabel: string;
  previewReadinessLabel: string;
  downloadReadiness: string;
  downloadReadinessLabel: string;
  handoffReadinessLabel: string;
  bundleHashPresenceLabel: string;
  manifestVersionLabel: string;
  redactionVersionLabel: string;
  previewHeadlineLabel: string;
  previewTopCategoryLabel: string;
  primaryFailureSignalLabel: string;
  diagnosisSummaryLabel: string;
  diagnosisConfidenceLabel: string;
  recommendedActionLabel: string;
  deliveryPathStateLabel: string;
  producerPathNextActionLabel: string;
  latestErrorCodeLabel: string;
  latestRequestId?: string;
  latestRequestIdLabel: string;
  latestCorrelationId?: string;
  latestCorrelationIdLabel: string;
  previewLines: Array<{
    timestampLabel: string;
    levelLabel: string;
    categoryLabel: string;
    messageKeyLabel: string;
    redactedContextLabel: string;
    requestIdLabel: string;
    correlationIdLabel: string;
    scenarioKeyLabel?: string;
    errorCodeLabel: string;
  }>;
  requestTraceDrilldownTo?: string;
};

type IncidentSameDeviceHeaderRow = {
  key:
    | "artifactId"
    | "deviceInstallId"
    | "release"
    | "appVersion"
    | "platform"
    | "runtimeSummary"
    | "capturedAt"
    | "captureTrigger"
    | "captureWindow"
    | "bundleHash"
    | "manifestVersion";
  label: string;
  value: string;
};

type IncidentPhotoCompareRow = {
  key:
    | "artifactId"
    | "sourceScenario"
    | "relationType"
    | "artifactRole"
    | "contentType"
    | "sizeBucket"
    | "uploadedAt"
    | "previewAvailability"
    | "downloadAvailability"
    | "binaryReviewPath"
    | "archiveState"
    | "incidentAttachment"
    | "latestTrace";
  label: string;
  value: string;
};

type IncidentPhotoAccessIssuanceRow = {
  key: "accessResult" | "requestId" | "issuedAt" | "expiresAt";
  label: string;
  value: string;
};

type IncidentExcerptReplayLine = {
  timestampLabel: string;
  levelLabel: string;
  categoryLabel: string;
  messageKeyLabel: string;
  redactedContextLabel: string;
  requestIdLabel: string;
  correlationIdLabel: string;
  scenarioKeyLabel?: string;
};

type IncidentManifestFieldRow = {
  key:
    | "artifactId"
    | "deviceInstallId"
    | "release"
    | "appVersion"
    | "platform"
    | "capturedAt"
    | "captureWindow"
    | "captureTrigger"
    | "bundleHash"
    | "redactionVersion"
    | "manifestVersion"
    | "requestId"
    | "correlationId"
    | "lineCount"
    | "truncated"
    | "retentionExpiresAt";
  label: string;
  value: string;
};

type IncidentBundleContractSummary = {
  bundleBaseName: string;
  manifestFilename: string;
  payloadFilename: string;
  compareSummaryFilename: string;
  payloadFormatLabel: string;
  manifestFieldRows: IncidentManifestFieldRow[];
  lineSchemaLabels: string[];
};

type IncidentRootCauseWorkspaceRow = {
  key: "photoEvidence" | "requestTrace" | "inlineLogPreview" | "photoDownload" | "logDownload" | "parityDecision";
  label: string;
  value: string;
};

type IncidentRootCauseDetailRow = {
  key: string;
  label: string;
  value: string;
};

type PrimaryPhotoWorkspaceArtifact = {
  artifactId: string;
  sourceScenarioKey: string;
  sourceScenarioLabel: string;
  linkedEntityTypeLabel: string;
  relationTypeLabel: string;
  artifactRoleLabel: string;
  contentTypeBucketLabel: string;
  sizeBucketLabel: string;
  uploadedAtLabel: string;
  storageStatusLabel: string;
  previewAvailability?: string;
  previewAvailabilityLabel: string;
  downloadAvailability?: string;
  downloadAvailabilityLabel: string;
  binaryReviewPathLabel: string;
  binaryReviewHint: string;
  latestRequestId?: string;
  latestTraceLabel: string;
  fallbackReasonLabel: string;
  archiveStateLabel: string;
  archiveStateHint: string;
  incidentAttachmentStateLabel: string;
  incidentAttachmentHint: string;
};

type IncidentRootCauseSourceSurfaceRow = {
  key: string;
  label: string;
  value: string;
};

type IncidentRootCauseMissingSourceRow = {
  key: string;
  label: string;
  value: string;
};

type IncidentRootCauseWorkspaceSummary = {
  readingOrderRows: IncidentRootCauseWorkspaceRow[];
  routeRows: IncidentRootCauseDetailRow[];
  photoReviewRows: IncidentPhotoCompareRow[];
  photoAccessIssuanceRows: IncidentPhotoAccessIssuanceRow[];
  sameDeviceLogRows: IncidentRootCauseDetailRow[];
  sameDeviceLogAccessIssuanceRows: IncidentPhotoAccessIssuanceRow[];
  sourceSurfaceRows: IncidentRootCauseSourceSurfaceRow[];
  missingSourceRows: IncidentRootCauseMissingSourceRow[];
  photoLinkageStateLabel: string;
  photoLinkageHint: string;
  parityDecisionLabel: string;
  photoLinkageNextActionLabel: string;
  photoCompareReadyLabel: string;
  photoDeliverySourceLabel: string;
  photoOriginalDownloadIssueLabel: string;
  deliveryPathStateLabel: string;
  bundleDeliverySourceLabel: string;
  producerPathNextActionLabel: string;
  fallbackNextActionLabel: string;
};

export function buildSelectedIncidentWorkspaceProvenanceRows(input: {
  labels: {
    deviceInstallId: string;
    release: string;
    appVersion: string;
    platform: string;
    runtimeSummary: string;
    capturedAt: string;
    captureTrigger: string;
    captureWindow: string;
    deliveryPathState: string;
    supportability: string;
    manifestVersion: string;
    redactionVersion: string;
    bundleHash: string;
    deliverySource: string;
  };
  source: {
    deviceInstallId: string;
    releaseLabel: string;
    appVersionLabel: string;
    platformLabel: string;
    runtimeSummaryLabel: string;
    capturedAtOperatorValueLabel: string;
    captureTriggerLabel: string;
    captureWindowLabel: string;
    deliveryPathStateLabel: string;
    supportabilityStateLabel: string;
    manifestVersionLabel: string;
    redactionVersionLabel: string;
    bundleHashPresenceLabel: string;
  };
  deliverySourceLabel: string;
}): IncidentRootCauseDetailRow[] {
  return [
    { key: "deviceInstallId", label: input.labels.deviceInstallId, value: input.source.deviceInstallId },
    { key: "release", label: input.labels.release, value: input.source.releaseLabel },
    { key: "appVersion", label: input.labels.appVersion, value: input.source.appVersionLabel },
    { key: "platform", label: input.labels.platform, value: input.source.platformLabel },
    { key: "runtimeSummary", label: input.labels.runtimeSummary, value: input.source.runtimeSummaryLabel },
    { key: "capturedAt", label: input.labels.capturedAt, value: input.source.capturedAtOperatorValueLabel },
    { key: "captureTrigger", label: input.labels.captureTrigger, value: input.source.captureTriggerLabel },
    { key: "captureWindow", label: input.labels.captureWindow, value: input.source.captureWindowLabel },
    { key: "deliveryPathState", label: input.labels.deliveryPathState, value: input.source.deliveryPathStateLabel },
    { key: "supportability", label: input.labels.supportability, value: input.source.supportabilityStateLabel },
    { key: "manifestVersion", label: input.labels.manifestVersion, value: input.source.manifestVersionLabel },
    { key: "redactionVersion", label: input.labels.redactionVersion, value: input.source.redactionVersionLabel },
    { key: "bundleHash", label: input.labels.bundleHash, value: input.source.bundleHashPresenceLabel },
    { key: "deliverySource", label: input.labels.deliverySource, value: input.deliverySourceLabel },
  ];
}

type IncidentBrowserDirectEvidenceRow = {
  key:
    | "host"
    | "route"
    | "reopenRoute"
    | "locale"
    | "release"
    | "bundleFingerprint"
    | "account"
    | "accountCue"
    | "providerCue"
    | "linkedProviders"
    | "internalUserId"
    | "representativeArtifactId"
    | "selectedArtifactId"
    | "photoPreviewArtifactId"
    | "photoDownloadArtifactId"
    | "photoLogRelation"
    | "sameDeviceLogArtifactId"
    | "deliveryPathState"
    | "bundleDeliverySource";
  label: string;
  value: string;
};

type IncidentBrowserDirectTraceLinkageRow = {
  key: "requestId" | "correlationId" | "failedFlowLink";
  label: string;
  value: string;
};

type IncidentManifestCompareRow = {
  key: string;
  label: string;
  value: string;
};

type IncidentManifestCompareSummary = {
  parityRows: IncidentManifestCompareRow[];
  compareReadyLabel: string;
  compareNextActionLabel?: string;
  deliverySourceLabel: string;
};

type IncidentPhotoCompareSummary = {
  parityRows: IncidentPhotoCompareRow[];
  accessIssuanceRows: IncidentPhotoAccessIssuanceRow[];
  compareReadyLabel: string;
  compareNextActionLabel?: string;
  deliverySourceLabel: string;
  originalDownloadIssueLabel: string;
};

type UploadedPhotoReviewTimelineRow = {
  artifactId: string;
  sourceScenarioKey: string;
  sourceScenarioLabel: string;
  relationTypeLabel: string;
  artifactRoleLabel: string;
  uploadedAtLabel: string;
  previewAvailabilityLabel: string;
  downloadAvailabilityLabel: string;
  binaryReviewPathLabel: string;
  latestTraceLabel: string;
  isRepresentativeEntry: boolean;
  isLatestForScenario: boolean;
  requestTraceDrilldownTo?: string;
};

type UploadedPhotoReviewPackageExport = {
  schemaVersion: "admin_uploaded_photo_review.v1";
  exportedAt: string;
  route: string;
  reopenRoute: string;
  lookup: {
    internalUserId?: string;
    representativeArtifactId?: string;
    selectedArtifactId?: string;
    selectedScenarioKey?: string;
  };
  evidenceSummary: {
    representativeArtifactId?: string;
    representativeSelectionParityLabel: string;
    selectedArtifact?:
      | {
          artifactId: string;
          sourceScenarioKey: string;
          sourceScenarioLabel: string;
          relationTypeLabel: string;
          artifactRoleLabel: string;
          uploadedAtLabel: string;
          previewAvailabilityLabel: string;
          downloadAvailabilityLabel: string;
          binaryReviewPathLabel: string;
          latestTraceLabel: string;
          fallbackReasonLabel: string;
          reviewNextActionLabel: string;
          compareReadyLabel: string;
          deliverySourceLabel: string;
          originalDownloadIssueLabel: string;
          photoCompareRows: IncidentPhotoCompareRow[];
          photoAccessIssuanceRows: IncidentPhotoAccessIssuanceRow[];
        }
      | undefined;
    selectedMatchesRepresentative: boolean;
    selectedMatchesLatestUpload: boolean;
    selectedScenarioSummary?:
      | {
          scenarioKey: string;
          label: string;
          artifactCountLabel: string;
          representativeArtifactIdLabel: string;
          latestUploadAtLabel: string;
          sourceConnectionLabel: string;
          coverageStateLabel: string;
          fallbackSummaryLabel: string;
          latestTraceLabel: string;
        }
      | undefined;
    timelineRows: UploadedPhotoReviewTimelineRow[];
    requestTraceDrilldownTo?: string;
    linkedFailedFlowDrilldownTo?: string;
    linkedLifecycleDrilldownTo?: string;
    incidentCompareArtifactId?: string;
    nextActionLabel: string;
  };
};

function buildIncidentPhotoAccessIssuanceRows(input: {
  labels: {
    accessResult: string;
    requestId: string;
    issuedAt: string;
    expiresAt: string;
  };
  source: Pick<MediaAccessState, "requestId" | "issuedAt" | "expiresAt" | "result">;
  locale: "ko" | "en";
}): IncidentPhotoAccessIssuanceRow[] {
  const accessResultLabel = input.source.result
    ? formatIncidentPhotoAccessResultLabel(input.source.result, input.locale)
    : pickAdminDashboardLocaleText(input.locale, {
        en: "Not issued yet",
        ko: "아직 발급되지 않음",
      });

  return [
    { key: "accessResult", label: input.labels.accessResult, value: accessResultLabel },
    { key: "requestId", label: input.labels.requestId, value: input.source.requestId ?? "-" },
    { key: "issuedAt", label: input.labels.issuedAt, value: input.source.issuedAt ?? "-" },
    { key: "expiresAt", label: input.labels.expiresAt, value: input.source.expiresAt ?? "-" },
  ];
}

function buildIncidentLogAccessIssuanceRows(input: {
  locale: "ko" | "en";
  source: Pick<IncidentLogAccessState, "requestId" | "issuedAt" | "expiresAt" | "result">;
}): IncidentPhotoAccessIssuanceRow[] {
  return buildIncidentPhotoAccessIssuanceRows({
    labels: {
      accessResult: pickAdminDashboardLocaleText(input.locale, {
        en: "Bundle issuance",
        ko: "번들 발급 상태",
      }),
      requestId: pickAdminDashboardLocaleText(input.locale, { en: "Access request", ko: "접근 요청" }),
      issuedAt: pickAdminDashboardLocaleText(input.locale, { en: "Issued at", ko: "발급 시각" }),
      expiresAt: pickAdminDashboardLocaleText(input.locale, { en: "Expires at", ko: "만료 시각" }),
    },
    source: input.source,
    locale: input.locale,
  });
}

function formatIncidentPhotoAccessResultLabel(
  result: Exclude<MediaAccessState["result"], undefined>,
  locale: "ko" | "en",
): string {
  switch (result) {
    case "allowed":
      return pickAdminDashboardLocaleText(locale, { en: "Issued", ko: "발급됨" });
    case "missing":
      return pickAdminDashboardLocaleText(locale, { en: "Missing", ko: "누락됨" });
    case "expired":
      return pickAdminDashboardLocaleText(locale, { en: "Expired", ko: "만료됨" });
    case "denied_policy":
      return pickAdminDashboardLocaleText(locale, { en: "Denied by policy", ko: "정책으로 차단됨" });
    case "denied_role":
      return pickAdminDashboardLocaleText(locale, { en: "Denied by role", ko: "권한으로 차단됨" });
    case "unknown":
      return pickAdminDashboardLocaleText(locale, { en: "Unknown", ko: "알 수 없음" });
  }
  return pickAdminDashboardLocaleText(locale, { en: "Unknown", ko: "알 수 없음" });
}

type IncidentBrowserDirectAcceptanceSummary = {
  evidencePackRows: IncidentBrowserDirectEvidenceRow[];
  photoReviewRows: IncidentPhotoCompareRow[];
  photoAccessIssuanceRows: IncidentPhotoAccessIssuanceRow[];
  sameDeviceLogRows: IncidentRootCauseDetailRow[];
  sameDeviceLogAccessIssuanceRows: IncidentPhotoAccessIssuanceRow[];
  sameDeviceHeaderSummaryRows: IncidentSameDeviceHeaderRow[];
  requestTraceLinkageRows: IncidentBrowserDirectTraceLinkageRow[];
  artifactId: string;
  deviceInstallId: string;
  excerptReplayLines: IncidentExcerptReplayLine[];
  captureTriggerLabel: string;
  captureWindowLabel: string;
  capturedAtBucketLabel: string;
  capturedAtOperatorValueLabel: string;
  manifestVersionLabel: string;
  redactionVersionLabel: string;
  bundleHashPresenceLabel: string;
  bundleDeliverySourceLabel: string;
  photoArtifactId: string;
  photoSourceScenarioLabel: string;
  photoArtifactRoleLabel: string;
  photoArchiveStateLabel: string;
  photoIncidentAttachmentStateLabel: string;
  photoLinkageStateLabel: string;
  photoLinkageHint: string;
  photoCompareReadyLabel: string;
  photoDeliverySourceLabel: string;
  photoOriginalDownloadIssueLabel: string;
  fallbackReasonLabel: string;
  reviewNextActionLabel: string;
  supportabilityStateLabel: string;
  previewReadinessLabel: string;
  previewTopCategoryLabel: string;
  excerptLineCountBucketLabel: string;
  previewHeadlineLabel: string;
  primaryFailureSignalLabel: string;
  diagnosisSummaryLabel: string;
  diagnosisConfidenceLabel: string;
  latestErrorCodeLabel: string;
  replayStateLabel: string;
  replayNextActionLabel: string;
  downloadReadinessLabel: string;
  fullBundleDownloadIssueLabel: string;
  bundleContractSummary: IncidentBundleContractSummary;
  deliveryPathStateLabel: string;
  producerPathNextActionLabel: string;
  latestRequestIdLabel: string;
  latestCorrelationIdLabel: string;
  photoLinkageNextActionLabel: string;
  handoffParityResultLabel: string;
};

export type UserDetailIncidentEvidenceWorkspace = {
  selectedIncidentLog?: IncidentEvidenceIncidentLog;
  matchedByRequestId: boolean;
  matchedByCorrelationId: boolean;
  primaryTraceDrilldownTo?: string;
  primaryTraceRequestId?: string;
  primaryTraceCorrelationId?: string;
  photoLinkageState:
    | "same_device_aligned"
    | "contextual_link_only"
    | "photo_only_pending_log"
    | "log_only_missing_photo"
    | "empty";
  traceJoinKey: "request_id" | "correlation_id" | "contextual" | "none";
  traceJoinState: "joined" | "photo_only" | "log_only" | "split" | "empty";
};

export type UserDetailIncidentHandoffPackagePreview = {
  status: "ready" | "needs_trace_join" | "needs_photo" | "needs_log" | "empty";
  relation: "direct_trace" | "contextual" | "unknown";
  photoLinkageState: UserDetailIncidentEvidenceWorkspace["photoLinkageState"];
  photoLinkageStateLabel: string;
  photoLinkageHint: string;
  photoLinkageNextActionLabel: string;
  photoArtifact:
    | {
        artifactId: string;
        sourceScenarioKey: string;
        sourceScenarioLabel: string;
        relationTypeLabel: string;
        artifactRoleLabel: string;
        contentTypeBucketLabel: string;
        sizeBucketLabel: string;
        uploadedAtLabel: string;
        previewAvailability?: string;
        previewAvailabilityLabel: string;
        downloadAvailability?: string;
        downloadAvailabilityLabel: string;
        binaryReviewPathLabel: string;
        archiveStateLabel: string;
        incidentAttachmentStateLabel: string;
        fallbackReasonLabel: string;
        reviewNextActionLabel: string;
        compareReadyLabel: string;
        deliverySourceLabel: string;
        originalDownloadIssueLabel: string;
        latestTraceLabel: string;
        accessIssuanceRows: IncidentPhotoAccessIssuanceRow[];
        downloadIssue:
          | {
              state: "issued" | "issuing" | "failed" | "ready_to_issue" | "blocked";
              requestId?: string;
              issuedAt?: string;
              expiresAt?: string;
              result?: string;
            }
          | undefined;
      }
    | undefined;
  sameDeviceLog:
      | {
        artifactId: string;
        deviceInstallId: string;
        releaseLabel: string;
        appVersionLabel: string;
        platformLabel: string;
        runtimeSummaryLabel: string;
        captureTriggerLabel: string;
        captureWindowLabel: string;
        capturedAtBucketLabel: string;
        capturedAtOperatorValueLabel: string;
        supportabilityStateLabel: string;
        excerptLineCountBucketLabel: string;
        previewReadinessLabel: string;
        previewTopCategoryLabel: string;
        previewHeadlineLabel: string;
        primaryFailureSignalLabel: string;
        diagnosisSummaryLabel: string;
        diagnosisConfidenceLabel: string;
        recommendedActionLabel: string;
        deliveryPathStateLabel: string;
        producerPathNextActionLabel: string;
        latestErrorCodeLabel: string;
        downloadReadinessLabel: string;
        handoffReadinessLabel: string;
        manifestVersionLabel: string;
        redactionVersionLabel: string;
        bundleHashPresenceLabel: string;
        latestRequestIdLabel: string;
        latestCorrelationIdLabel: string;
        bundleDeliverySourceLabel: string;
        excerptReplayLines: IncidentExcerptReplayLine[];
        replayStateLabel: string;
        replayNextActionLabel: string;
        bundleContractSummary: IncidentBundleContractSummary;
        accessIssuanceRows: IncidentPhotoAccessIssuanceRow[];
        downloadIssue:
          | {
              state: "issued" | "issuing" | "failed" | "ready_to_issue" | "blocked";
              requestId?: string;
              issuedAt?: string;
              expiresAt?: string;
              result?: string;
            }
          | undefined;
      }
    | undefined;
  requestTrace:
    | {
        requestId?: string;
        correlationId?: string;
        joinKey?: "request_id" | "correlation_id" | "contextual" | "none";
        joinKeyLabel?: string;
        drilldownTo?: string;
      }
    | undefined;
};

export type UserDetailIncidentHandoffPackageExport = {
  schemaVersion: "admin_incident_handoff.v1";
  exportedAt: string;
  route: string;
  reopenRoute: string;
  lookup: {
    internalUserId?: string;
  };
  evidenceSummary: {
    status: UserDetailIncidentHandoffPackagePreview["status"];
    statusLabel: string;
    nextActionLabel: string;
    relation: UserDetailIncidentHandoffPackagePreview["relation"];
    relationLabel: string;
    photoLinkageState: UserDetailIncidentHandoffPackagePreview["photoLinkageState"];
    photoLinkageStateLabel: string;
    photoLinkageHint: string;
    photoLinkageNextActionLabel: string;
    failedFlowLink?: string;
    photoArtifact?:
      | {
          artifactId: string;
          sourceScenarioKey: string;
          sourceScenarioLabel: string;
          relationTypeLabel: string;
          artifactRoleLabel: string;
          contentTypeBucketLabel: string;
          sizeBucketLabel: string;
          uploadedAtLabel: string;
          previewAvailabilityLabel: string;
          downloadAvailabilityLabel: string;
          binaryReviewPathLabel: string;
          archiveStateLabel: string;
          incidentAttachmentStateLabel: string;
          fallbackReasonLabel: string;
          reviewNextActionLabel: string;
          compareReadyLabel: string;
          deliverySourceLabel: string;
          latestTraceLabel: string;
          photoCompareRows: IncidentPhotoCompareRow[];
          photoAccessIssuanceRows: IncidentPhotoAccessIssuanceRow[];
          originalDownloadIssue: IncidentHandoffDownloadIssue;
          originalDownloadIssueLabel: string;
        }
      | undefined;
    requestTrace?: UserDetailIncidentHandoffPackagePreview["requestTrace"];
    sameDeviceLogManifestSummary?:
      | {
          artifactId: string;
          deviceInstallId: string;
          releaseLabel: string;
          appVersionLabel: string;
          platformLabel: string;
          runtimeSummaryLabel: string;
          captureTriggerLabel: string;
          captureWindowLabel: string;
          capturedAtBucketLabel: string;
          capturedAtOperatorValueLabel: string;
          supportabilityStateLabel: string;
          excerptLineCountBucketLabel: string;
          previewReadinessLabel: string;
          previewTopCategoryLabel: string;
          previewHeadlineLabel: string;
          primaryFailureSignalLabel: string;
          diagnosisSummaryLabel: string;
          diagnosisConfidenceLabel: string;
          latestErrorCodeLabel: string;
          manifestVersionLabel: string;
          redactionVersionLabel: string;
          bundleHashPresenceLabel: string;
          deliveryPathStateLabel: string;
          producerPathNextActionLabel: string;
          downloadReadinessLabel: string;
          handoffReadinessLabel: string;
          handoffParityResultLabel: string;
          latestRequestIdLabel: string;
          latestCorrelationIdLabel: string;
          bundleDeliverySourceLabel: string;
          sameDeviceHeaderRows: IncidentSameDeviceHeaderRow[];
          excerptReplayLines: IncidentExcerptReplayLine[];
          replayStateLabel: string;
          replayNextActionLabel: string;
          bundleContractSummary: IncidentBundleContractSummary;
          accessIssuanceRows: IncidentPhotoAccessIssuanceRow[];
          fullBundleDownloadIssue: IncidentHandoffDownloadIssue;
          fullBundleDownloadIssueLabel: string;
        }
      | undefined;
    verificationChecklist: UserDetailIncidentHandoffChecklistItem[];
    bundleHashVerificationHint?: string;
    operatorNote?: string;
    rootCauseWorkspaceSummary?: IncidentRootCauseWorkspaceSummary;
    browserDirectAcceptanceSummary?: IncidentBrowserDirectAcceptanceSummary;
  };
  workspace: UserDetailIncidentHandoffPackagePreview;
};

type IncidentHandoffSourceSurfaceMatrixRow = {
  scenarioKey: string;
  label: string;
  targetLinkedEntityLabel: string;
  sourceConnectionLabel: string;
  baselineStatusLabel: string;
  baselineSummaryLabel: string;
  currentCoverageStateLabel: string;
  previewReadyCountLabel: string;
  downloadReadyCountLabel: string;
  blockedCountLabel: string;
  binaryReviewPathLabel: string;
  operatorHint: string;
};

type IncidentHandoffMissingSourceDisclosureRow = {
  scenarioKey: string;
  label: string;
  sourceConnectionLabel: string;
  statusLabel: string;
  reasonLabel: string;
  nextActionHint: string;
};

type UserDetailIncidentHandoffChecklistItem = {
  id:
    | "verify_photo_artifact_identity"
    | "verify_photo_download_issue_state"
    | "verify_trace_linkage"
    | "verify_same_device_header_parity"
    | "verify_log_download_issue_state";
  label: string;
};

type IncidentHandoffDownloadIssueState =
  | "issued"
  | "issuing"
  | "failed"
  | "ready_to_issue"
  | "blocked";

type IncidentHandoffDownloadIssue =
  | {
      state: IncidentHandoffDownloadIssueState;
      requestId?: string;
      issuedAt?: string;
      expiresAt?: string;
      result?: string;
    }
  | undefined;

function resolveIncidentHandoffDownloadIssue(input: {
  accessState:
    | Pick<
        MediaAccessState,
        "artifactId" | "action" | "status" | "requestId" | "issuedAt" | "expiresAt" | "result"
      >
    | Pick<
        IncidentLogAccessState,
        "artifactId" | "action" | "status" | "requestId" | "issuedAt" | "expiresAt" | "result"
      >;
  artifactId: string | undefined;
  action: "download";
  canIssue: boolean;
}): IncidentHandoffDownloadIssue {
  const { accessState, artifactId, action, canIssue } = input;

  if (!artifactId) {
    return undefined;
  }

  if (accessState.artifactId === artifactId && accessState.action === action) {
    return {
      state:
        accessState.status === "ready"
          ? "issued"
          : accessState.status === "loading"
            ? "issuing"
            : accessState.status === "error"
              ? "failed"
              : canIssue
                ? "ready_to_issue"
                : "blocked",
      requestId: accessState.requestId,
      issuedAt: accessState.issuedAt,
      expiresAt: accessState.expiresAt,
      result: accessState.result,
    };
  }

  return {
    state: canIssue ? "ready_to_issue" : "blocked",
    requestId: undefined,
    issuedAt: undefined,
    expiresAt: undefined,
    result: undefined,
  };
}

export function pickRecommendedIncidentLogArtifactId(
  selectedArtifact: IncidentEvidenceSelectedArtifact | undefined,
  incidentLogArtifacts: readonly IncidentEvidenceIncidentLog[],
  preferredSameDeviceContext?: {
    deviceInstallId?: string;
    release?: string;
    appVersion?: string;
    platform?: string;
  },
): string | undefined {
  if (incidentLogArtifacts.length === 0) {
    return undefined;
  }

  const selectedArtifactRequestId = selectedArtifact?.latestRequestId;
  if (selectedArtifactRequestId) {
    const exactMatch = incidentLogArtifacts.find(
      (artifact) => artifact.latestRequestId === selectedArtifactRequestId,
    );
    if (exactMatch) {
      return exactMatch.artifactId;
    }
  }

  if (
    preferredSameDeviceContext?.deviceInstallId ||
    preferredSameDeviceContext?.release ||
    preferredSameDeviceContext?.appVersion ||
    preferredSameDeviceContext?.platform
  ) {
    const sameDeviceMatch = incidentLogArtifacts.find((artifact) => {
      if (
        preferredSameDeviceContext.deviceInstallId &&
        artifact.deviceInstallId !== preferredSameDeviceContext.deviceInstallId
      ) {
        return false;
      }

      if (
        preferredSameDeviceContext.release &&
        artifact.releaseLabel !== preferredSameDeviceContext.release
      ) {
        return false;
      }

      if (
        preferredSameDeviceContext.appVersion &&
        artifact.appVersionLabel !== preferredSameDeviceContext.appVersion
      ) {
        return false;
      }

      if (
        preferredSameDeviceContext.platform &&
        artifact.platformLabel.toLowerCase() !== preferredSameDeviceContext.platform.toLowerCase()
      ) {
        return false;
      }

      return true;
    });

    if (sameDeviceMatch) {
      return sameDeviceMatch.artifactId;
    }
  }

  const traceReadyArtifact = incidentLogArtifacts.find(
    (artifact) => typeof artifact.requestTraceDrilldownTo === "string",
  );
  return traceReadyArtifact?.artifactId ?? incidentLogArtifacts[0]?.artifactId;
}

export function resolveUserDetailIncidentEvidenceWorkspace(input: {
  selectedArtifact: IncidentEvidenceSelectedArtifact | undefined;
  selectedIncidentLog: IncidentEvidenceIncidentLog | undefined;
}): UserDetailIncidentEvidenceWorkspace {
  const { selectedArtifact, selectedIncidentLog } = input;
  const matchedByRequestId =
    Boolean(selectedArtifact?.latestRequestId) &&
    Boolean(selectedIncidentLog?.latestRequestId) &&
    selectedArtifact?.latestRequestId === selectedIncidentLog?.latestRequestId;
  const matchedByCorrelationId =
    Boolean(selectedArtifact?.latestCorrelationId) &&
    Boolean(selectedIncidentLog?.latestCorrelationId) &&
    selectedArtifact?.latestCorrelationId === selectedIncidentLog?.latestCorrelationId;
  const primaryTraceDrilldownTo =
    selectedIncidentLog?.requestTraceDrilldownTo ?? selectedArtifact?.requestTraceDrilldownTo;
  const primaryTraceRequestId =
    selectedIncidentLog?.latestRequestId ?? selectedArtifact?.latestRequestId;
  const primaryTraceCorrelationId =
    selectedIncidentLog?.latestCorrelationId ?? selectedArtifact?.latestCorrelationId;

  let traceJoinState: UserDetailIncidentEvidenceWorkspace["traceJoinState"] = "empty";
  let photoLinkageState: UserDetailIncidentEvidenceWorkspace["photoLinkageState"] = "empty";
  let traceJoinKey: UserDetailIncidentEvidenceWorkspace["traceJoinKey"] = "none";
  if (selectedArtifact && selectedIncidentLog) {
    traceJoinState = matchedByRequestId || matchedByCorrelationId ? "joined" : "split";
    traceJoinKey = matchedByRequestId
      ? "request_id"
      : matchedByCorrelationId
        ? "correlation_id"
        : "contextual";
    photoLinkageState =
      traceJoinState === "joined" && selectedArtifact.relationTypeKey === "direct"
        ? "same_device_aligned"
        : "contextual_link_only";
  } else if (selectedArtifact) {
    traceJoinState = "photo_only";
    photoLinkageState = "photo_only_pending_log";
  } else if (selectedIncidentLog) {
    traceJoinState = "log_only";
    photoLinkageState = "log_only_missing_photo";
  }

  return {
    selectedIncidentLog,
    matchedByRequestId,
    matchedByCorrelationId,
    primaryTraceDrilldownTo,
    primaryTraceRequestId,
    primaryTraceCorrelationId,
    photoLinkageState,
    traceJoinKey,
    traceJoinState,
  };
}

export function buildUserDetailIncidentHandoffPackagePreview(input: {
  incidentEvidenceWorkspace: UserDetailIncidentEvidenceWorkspace;
  incidentAccessState: IncidentLogAccessState;
  locale: "ko" | "en";
  mediaAccessState: MediaAccessState;
  selectedArtifact: IncidentEvidenceSelectedArtifact | undefined;
}): UserDetailIncidentHandoffPackagePreview {
  const { incidentEvidenceWorkspace, incidentAccessState, locale, mediaAccessState, selectedArtifact } = input;
  const selectedIncidentLog = incidentEvidenceWorkspace.selectedIncidentLog;
  const activePhotoDownloadIssue = resolveIncidentHandoffDownloadIssue({
    accessState: mediaAccessState,
    artifactId: selectedArtifact?.artifactId,
    action: "download",
    canIssue: selectedArtifact?.downloadAvailability === "available",
  });
  const activePhotoDeliverySourceLabel = selectedArtifact
    ? formatIncidentPhotoDeliverySourceLabel({
        locale,
        accessState: mediaAccessState,
        artifactId: selectedArtifact.artifactId,
        downloadIssue: activePhotoDownloadIssue,
      })
    : undefined;
  const activePhotoCompareReadyLabel = selectedArtifact
    ? formatIncidentPhotoCompareReadyLabel({
        locale,
        downloadIssue: activePhotoDownloadIssue,
        downloadAvailabilityLabel: selectedArtifact.downloadAvailabilityLabel,
      })
    : undefined;
  const activePhotoDownloadIssueLabel = selectedArtifact
    ? formatIncidentHandoffDownloadIssueLabel(
        activePhotoDownloadIssue,
        locale,
        selectedArtifact.downloadAvailabilityLabel,
      )
    : undefined;
  const activeLogDownloadIssue = resolveIncidentHandoffDownloadIssue({
    accessState: incidentAccessState,
    artifactId: selectedIncidentLog?.artifactId,
    action: "download",
    canIssue: selectedIncidentLog?.downloadReadiness === "ready",
  });

  return {
    status:
      incidentEvidenceWorkspace.traceJoinState === "joined"
        ? "ready"
        : incidentEvidenceWorkspace.traceJoinState === "split"
          ? "needs_trace_join"
          : incidentEvidenceWorkspace.traceJoinState === "photo_only"
            ? "needs_log"
            : incidentEvidenceWorkspace.traceJoinState === "log_only"
              ? "needs_photo"
              : "empty",
    relation:
      incidentEvidenceWorkspace.traceJoinState === "joined"
        ? "direct_trace"
        : selectedArtifact
          ? "contextual"
          : "unknown",
    photoLinkageState: incidentEvidenceWorkspace.photoLinkageState,
    photoLinkageStateLabel: formatPhotoLinkedIncidentStateLabel(
      incidentEvidenceWorkspace.photoLinkageState,
      locale,
    ),
    photoLinkageHint:
      selectedArtifact?.incidentAttachmentHint ??
      pickAdminDashboardLocaleText(locale, {
        en: "Keep the relation label visible when photo and same-device evidence are only contextually connected.",
        ko: "사진과 same-device 증거가 문맥으로만 연결될 때는 관계 라벨이 계속 보이게 유지하세요.",
      }),
    photoLinkageNextActionLabel: formatPhotoLinkedIncidentNextAction(
      incidentEvidenceWorkspace,
      selectedArtifact,
      locale,
    ),
    photoArtifact: selectedArtifact
      ? {
          artifactId: selectedArtifact.artifactId,
          sourceScenarioKey: selectedArtifact.sourceScenarioKey,
          sourceScenarioLabel: selectedArtifact.sourceScenarioLabel,
          relationTypeLabel: selectedArtifact.relationTypeLabel,
          artifactRoleLabel: selectedArtifact.artifactRoleLabel,
          contentTypeBucketLabel: selectedArtifact.contentTypeBucketLabel,
          sizeBucketLabel: selectedArtifact.sizeBucketLabel,
          uploadedAtLabel: selectedArtifact.uploadedAtLabel,
          previewAvailability: selectedArtifact.previewAvailability,
          previewAvailabilityLabel: selectedArtifact.previewAvailabilityLabel,
          downloadAvailability: selectedArtifact.downloadAvailability,
          downloadAvailabilityLabel: selectedArtifact.downloadAvailabilityLabel,
          binaryReviewPathLabel: selectedArtifact.binaryReviewPathLabel,
          archiveStateLabel: selectedArtifact.archiveStateLabel,
          incidentAttachmentStateLabel: selectedArtifact.incidentAttachmentStateLabel,
          fallbackReasonLabel: selectedArtifact.fallbackReasonLabel,
          reviewNextActionLabel: selectedArtifact.binaryReviewHint,
          compareReadyLabel:
            activePhotoCompareReadyLabel ??
            pickAdminDashboardLocaleText(locale, { en: "No photo compare state", ko: "사진 비교 상태 없음" }),
          deliverySourceLabel:
            activePhotoDeliverySourceLabel ??
            pickAdminDashboardLocaleText(locale, {
              en: "Issue the original download to classify the delivery source",
              ko: "원본 다운로드를 발급해 전달 source를 판정하세요",
            }),
          originalDownloadIssueLabel:
            activePhotoDownloadIssueLabel ??
            pickAdminDashboardLocaleText(locale, { en: "Unavailable", ko: "없음" }),
          latestTraceLabel: `${selectedArtifact.latestRequestIdLabel} / ${selectedArtifact.latestCorrelationIdLabel}`,
          accessIssuanceRows: buildIncidentPhotoAccessIssuanceRows({
            labels: {
              accessResult: pickAdminDashboardLocaleText(locale, {
                en: "Download issuance",
                ko: "다운로드 발급 상태",
              }),
              requestId: pickAdminDashboardLocaleText(locale, { en: "Access request", ko: "접근 요청" }),
              issuedAt: pickAdminDashboardLocaleText(locale, { en: "Issued at", ko: "발급 시각" }),
              expiresAt: pickAdminDashboardLocaleText(locale, { en: "Expires at", ko: "만료 시각" }),
            },
            source: mediaAccessState,
            locale,
          }),
          downloadIssue: activePhotoDownloadIssue,
        }
      : undefined,
    sameDeviceLog: selectedIncidentLog
      ? {
          artifactId: selectedIncidentLog.artifactId,
          deviceInstallId: selectedIncidentLog.deviceInstallId,
          releaseLabel: selectedIncidentLog.releaseLabel,
          appVersionLabel: selectedIncidentLog.appVersionLabel,
          platformLabel: selectedIncidentLog.platformLabel,
          runtimeSummaryLabel: selectedIncidentLog.runtimeSummaryLabel,
          captureTriggerLabel: selectedIncidentLog.captureTriggerLabel,
          captureWindowLabel: selectedIncidentLog.captureWindowLabel,
          capturedAtBucketLabel: selectedIncidentLog.capturedAtBucketLabel,
          capturedAtOperatorValueLabel: selectedIncidentLog.capturedAtOperatorValueLabel,
          supportabilityStateLabel: selectedIncidentLog.supportabilityStateLabel,
          excerptLineCountBucketLabel: selectedIncidentLog.excerptLineCountBucketLabel,
          previewReadinessLabel: selectedIncidentLog.previewReadinessLabel,
          previewHeadlineLabel: selectedIncidentLog.previewHeadlineLabel,
          previewTopCategoryLabel: selectedIncidentLog.previewTopCategoryLabel,
          primaryFailureSignalLabel: selectedIncidentLog.primaryFailureSignalLabel,
          diagnosisSummaryLabel: selectedIncidentLog.diagnosisSummaryLabel,
          diagnosisConfidenceLabel: selectedIncidentLog.diagnosisConfidenceLabel,
          recommendedActionLabel: selectedIncidentLog.recommendedActionLabel,
          deliveryPathStateLabel: selectedIncidentLog.deliveryPathStateLabel,
          producerPathNextActionLabel: selectedIncidentLog.producerPathNextActionLabel,
          latestErrorCodeLabel: selectedIncidentLog.latestErrorCodeLabel,
          downloadReadinessLabel: selectedIncidentLog.downloadReadinessLabel,
          handoffReadinessLabel: selectedIncidentLog.handoffReadinessLabel,
          manifestVersionLabel: selectedIncidentLog.manifestVersionLabel,
          redactionVersionLabel: selectedIncidentLog.redactionVersionLabel,
          bundleHashPresenceLabel: selectedIncidentLog.bundleHashPresenceLabel,
          latestRequestIdLabel: selectedIncidentLog.latestRequestIdLabel,
          latestCorrelationIdLabel: selectedIncidentLog.latestCorrelationIdLabel,
          bundleDeliverySourceLabel: formatIncidentLogBundleDeliverySourceLabel({
            locale,
            accessState: incidentAccessState,
            artifactId: selectedIncidentLog.artifactId,
            downloadIssue: activeLogDownloadIssue,
          }),
          excerptReplayLines: buildIncidentExcerptReplayLines(selectedIncidentLog.previewLines),
          replayStateLabel: formatIncidentReplayStateLabel({
            downloadIssue: activeLogDownloadIssue,
            locale,
            previewReadinessLabel: selectedIncidentLog.previewReadinessLabel,
          }),
          replayNextActionLabel: buildIncidentReplayNextActionLabel({
            locale,
            producerPathNextActionLabel: selectedIncidentLog.producerPathNextActionLabel,
            downloadIssue: activeLogDownloadIssue,
          }) ?? selectedIncidentLog.producerPathNextActionLabel,
          accessIssuanceRows: buildIncidentLogAccessIssuanceRows({
            locale,
            source: incidentAccessState,
          }),
          bundleContractSummary: buildIncidentBundleContractSummary({
            locale,
            sameDeviceLog: {
              artifactId: selectedIncidentLog.artifactId,
              deviceInstallId: selectedIncidentLog.deviceInstallId,
              releaseLabel: selectedIncidentLog.releaseLabel,
              appVersionLabel: selectedIncidentLog.appVersionLabel,
              platformLabel: selectedIncidentLog.platformLabel,
              capturedAtOperatorValueLabel: selectedIncidentLog.capturedAtOperatorValueLabel,
              captureWindowLabel: selectedIncidentLog.captureWindowLabel,
              captureTriggerLabel: selectedIncidentLog.captureTriggerLabel,
              bundleHashPresenceLabel: selectedIncidentLog.bundleHashPresenceLabel,
              manifestVersionLabel: selectedIncidentLog.manifestVersionLabel,
              redactionVersionLabel: selectedIncidentLog.redactionVersionLabel,
              latestRequestIdLabel: selectedIncidentLog.latestRequestIdLabel,
              latestCorrelationIdLabel: selectedIncidentLog.latestCorrelationIdLabel,
              excerptLineCountBucketLabel: selectedIncidentLog.excerptLineCountBucketLabel,
            },
          }),
          downloadIssue: activeLogDownloadIssue,
        }
      : undefined,
    requestTrace: incidentEvidenceWorkspace.primaryTraceRequestId || incidentEvidenceWorkspace.primaryTraceDrilldownTo
      ? {
          requestId: incidentEvidenceWorkspace.primaryTraceRequestId,
          correlationId: incidentEvidenceWorkspace.primaryTraceCorrelationId,
          joinKey: incidentEvidenceWorkspace.traceJoinKey,
          joinKeyLabel: formatIncidentTraceJoinLabel(
            incidentEvidenceWorkspace,
            locale,
            pickAdminDashboardLocaleText(locale, {
              en: "Contextual trace fallback",
              ko: "문맥 추적 fallback",
            }),
          ),
          drilldownTo: incidentEvidenceWorkspace.primaryTraceDrilldownTo,
        }
      : undefined,
  };
}

export function buildUserDetailIncidentHandoffPackageExport(input: {
  exportedAt: string;
  handoffPackagePreview: UserDetailIncidentHandoffPackagePreview;
  internalUserId?: string;
  route?: string;
  reopenRoute?: string;
  locale: "ko" | "en";
  photoSourceSurfaceMatrix: IncidentHandoffSourceSurfaceMatrixRow[];
  missingPhotoSourceDisclosures: IncidentHandoffMissingSourceDisclosureRow[];
  browserDirectContext?: {
    host: string;
    route?: string;
    reopenRoute?: string;
    release: string;
    bundleFingerprint: string;
    accountEmail: string;
    accountIdentityLabel?: string;
    providerIdentityLabel?: string;
    linkedProviderCountLabel?: string;
    representativeArtifactId?: string;
  };
}): UserDetailIncidentHandoffPackageExport {
  const photoArtifact = input.handoffPackagePreview.photoArtifact;
  const requestTrace = input.handoffPackagePreview.requestTrace;
  const sameDeviceLog = input.handoffPackagePreview.sameDeviceLog;
  const relationLabel = formatIncidentHandoffRelationLabel(input.handoffPackagePreview.relation, input.locale);
  const canonicalRoute =
    input.route ??
    input.reopenRoute ??
    (input.internalUserId ? `/admin/users/${input.internalUserId}` : "/admin/users/:internalUserId");
  const canonicalReopenRoute =
    input.reopenRoute ??
    input.route ??
    (input.internalUserId ? `/admin/users/${input.internalUserId}` : "/admin/users/:internalUserId");
  const verificationChecklist = buildIncidentHandoffVerificationChecklist({
    locale: input.locale,
    photoArtifact,
    requestTrace,
    sameDeviceLog,
  });

  return {
    schemaVersion: "admin_incident_handoff.v1",
    exportedAt: input.exportedAt,
    route: canonicalRoute,
    reopenRoute: canonicalReopenRoute,
    lookup: {
      internalUserId: input.internalUserId,
    },
    evidenceSummary: {
      status: input.handoffPackagePreview.status,
      statusLabel: formatIncidentHandoffStatusLabel(input.handoffPackagePreview.status, input.locale),
      nextActionLabel: formatIncidentHandoffNextActionLabel({
        locale: input.locale,
        preview: input.handoffPackagePreview,
      }),
      relation: input.handoffPackagePreview.relation,
      relationLabel,
      photoLinkageState: input.handoffPackagePreview.photoLinkageState,
      photoLinkageStateLabel: input.handoffPackagePreview.photoLinkageStateLabel,
      photoLinkageHint: input.handoffPackagePreview.photoLinkageHint,
      photoLinkageNextActionLabel: input.handoffPackagePreview.photoLinkageNextActionLabel,
      failedFlowLink: requestTrace?.drilldownTo,
      photoArtifact: photoArtifact
        ? {
            artifactId: photoArtifact.artifactId,
            sourceScenarioKey: photoArtifact.sourceScenarioKey,
            sourceScenarioLabel: photoArtifact.sourceScenarioLabel,
            relationTypeLabel: photoArtifact.relationTypeLabel,
            artifactRoleLabel: photoArtifact.artifactRoleLabel,
            contentTypeBucketLabel: photoArtifact.contentTypeBucketLabel,
            sizeBucketLabel: photoArtifact.sizeBucketLabel,
            uploadedAtLabel: photoArtifact.uploadedAtLabel,
            previewAvailabilityLabel: photoArtifact.previewAvailabilityLabel,
            downloadAvailabilityLabel: photoArtifact.downloadAvailabilityLabel,
            binaryReviewPathLabel: photoArtifact.binaryReviewPathLabel,
            archiveStateLabel: photoArtifact.archiveStateLabel,
            incidentAttachmentStateLabel: photoArtifact.incidentAttachmentStateLabel,
            fallbackReasonLabel: photoArtifact.fallbackReasonLabel,
            reviewNextActionLabel: photoArtifact.reviewNextActionLabel,
            compareReadyLabel: photoArtifact.compareReadyLabel,
            deliverySourceLabel: photoArtifact.deliverySourceLabel,
            latestTraceLabel: photoArtifact.latestTraceLabel,
            photoCompareRows: buildIncidentPhotoCompareRows({
              labels: {
                artifactId: pickAdminDashboardLocaleText(input.locale, { en: "Artifact ID", ko: "Artifact ID" }),
                sourceScenario: pickAdminDashboardLocaleText(input.locale, { en: "Source", ko: "Source" }),
                relationType: pickAdminDashboardLocaleText(input.locale, { en: "Relation", ko: "Relation" }),
                artifactRole: pickAdminDashboardLocaleText(input.locale, { en: "Role", ko: "역할" }),
                contentType: pickAdminDashboardLocaleText(input.locale, { en: "Content Type", ko: "콘텐츠 유형" }),
                sizeBucket: pickAdminDashboardLocaleText(input.locale, { en: "Size", ko: "크기" }),
                uploadedAt: pickAdminDashboardLocaleText(input.locale, { en: "Created", ko: "생성 시각" }),
                previewAvailability: pickAdminDashboardLocaleText(input.locale, { en: "Preview", ko: "미리보기" }),
                downloadAvailability: pickAdminDashboardLocaleText(input.locale, { en: "Download", ko: "다운로드" }),
                binaryReviewPath: pickAdminDashboardLocaleText(input.locale, {
                  en: "Binary review path",
                  ko: "binary review 경로",
                }),
                archiveState: pickAdminDashboardLocaleText(input.locale, {
                  en: "Archive state",
                  ko: "archive 상태",
                }),
                incidentAttachment: pickAdminDashboardLocaleText(input.locale, {
                  en: "Incident attachment",
                  ko: "incident 연결",
                }),
                latestTrace: pickAdminDashboardLocaleText(input.locale, { en: "Latest trace", ko: "최근 추적" }),
              },
              source: photoArtifact,
            }),
            photoAccessIssuanceRows: photoArtifact.accessIssuanceRows,
            originalDownloadIssue: photoArtifact.downloadIssue,
            originalDownloadIssueLabel: photoArtifact.originalDownloadIssueLabel,
          }
        : undefined,
      requestTrace,
      sameDeviceLogManifestSummary: sameDeviceLog
        ? {
            artifactId: sameDeviceLog.artifactId,
            deviceInstallId: sameDeviceLog.deviceInstallId,
            releaseLabel: sameDeviceLog.releaseLabel,
            appVersionLabel: sameDeviceLog.appVersionLabel,
            platformLabel: sameDeviceLog.platformLabel,
            runtimeSummaryLabel: sameDeviceLog.runtimeSummaryLabel,
            captureTriggerLabel: sameDeviceLog.captureTriggerLabel,
            captureWindowLabel: sameDeviceLog.captureWindowLabel,
            capturedAtBucketLabel: sameDeviceLog.capturedAtBucketLabel,
            capturedAtOperatorValueLabel: sameDeviceLog.capturedAtOperatorValueLabel,
            supportabilityStateLabel: sameDeviceLog.supportabilityStateLabel,
            excerptLineCountBucketLabel: sameDeviceLog.excerptLineCountBucketLabel,
            previewReadinessLabel: sameDeviceLog.previewReadinessLabel,
            previewTopCategoryLabel: sameDeviceLog.previewTopCategoryLabel,
            previewHeadlineLabel: sameDeviceLog.previewHeadlineLabel,
            primaryFailureSignalLabel: sameDeviceLog.primaryFailureSignalLabel,
            diagnosisSummaryLabel: sameDeviceLog.diagnosisSummaryLabel,
            diagnosisConfidenceLabel: sameDeviceLog.diagnosisConfidenceLabel,
            latestErrorCodeLabel: sameDeviceLog.latestErrorCodeLabel,
            manifestVersionLabel: sameDeviceLog.manifestVersionLabel,
            redactionVersionLabel: sameDeviceLog.redactionVersionLabel,
            bundleHashPresenceLabel: sameDeviceLog.bundleHashPresenceLabel,
            deliveryPathStateLabel: sameDeviceLog.deliveryPathStateLabel,
            producerPathNextActionLabel: sameDeviceLog.producerPathNextActionLabel,
            downloadReadinessLabel: sameDeviceLog.downloadReadinessLabel,
            handoffReadinessLabel: sameDeviceLog.handoffReadinessLabel,
            handoffParityResultLabel: sameDeviceLog.handoffReadinessLabel,
            latestRequestIdLabel: sameDeviceLog.latestRequestIdLabel,
            latestCorrelationIdLabel: sameDeviceLog.latestCorrelationIdLabel,
            bundleDeliverySourceLabel: sameDeviceLog.bundleDeliverySourceLabel,
            sameDeviceHeaderRows: buildIncidentSameDeviceHeaderRows({
              labels: {
                artifactId: pickAdminDashboardLocaleText(input.locale, { en: "Artifact ID", ko: "Artifact ID" }),
                deviceInstallId: pickAdminDashboardLocaleText(input.locale, { en: "Device install", ko: "기기 설치본" }),
                release: pickAdminDashboardLocaleText(input.locale, { en: "Release", ko: "릴리스" }),
                appVersion: pickAdminDashboardLocaleText(input.locale, { en: "App version", ko: "앱 버전" }),
                platform: pickAdminDashboardLocaleText(input.locale, { en: "Platform", ko: "플랫폼" }),
                runtimeSummary: pickAdminDashboardLocaleText(input.locale, { en: "Runtime summary", ko: "런타임 요약" }),
                capturedAt: pickAdminDashboardLocaleText(input.locale, { en: "Captured at", ko: "수집 시각" }),
                captureTrigger: pickAdminDashboardLocaleText(input.locale, { en: "Capture trigger", ko: "수집 트리거" }),
                captureWindow: pickAdminDashboardLocaleText(input.locale, { en: "Capture window", ko: "수집 구간" }),
                bundleHash: pickAdminDashboardLocaleText(input.locale, { en: "Bundle hash", ko: "Bundle hash" }),
                manifestVersion: pickAdminDashboardLocaleText(input.locale, { en: "Manifest", ko: "Manifest" }),
              },
              source: sameDeviceLog,
            }),
            excerptReplayLines: sameDeviceLog.excerptReplayLines,
            replayStateLabel: sameDeviceLog.replayStateLabel,
            replayNextActionLabel: sameDeviceLog.replayNextActionLabel,
            bundleContractSummary: sameDeviceLog.bundleContractSummary,
            accessIssuanceRows: sameDeviceLog.accessIssuanceRows,
            fullBundleDownloadIssue: sameDeviceLog.downloadIssue,
            fullBundleDownloadIssueLabel: formatIncidentHandoffDownloadIssueLabel(
              sameDeviceLog.downloadIssue,
              input.locale,
              sameDeviceLog.downloadReadinessLabel,
            ),
          }
        : undefined,
      verificationChecklist,
      bundleHashVerificationHint: buildIncidentHandoffBundleHashVerificationHint(sameDeviceLog, input.locale),
      operatorNote: buildIncidentHandoffOperatorNote(sameDeviceLog, input.locale),
      rootCauseWorkspaceSummary: buildIncidentRootCauseWorkspaceSummary({
        locale: input.locale,
        photoArtifact,
        requestTrace,
        sameDeviceLog,
        route: canonicalRoute,
        reopenRoute: canonicalReopenRoute,
        photoSourceSurfaceMatrix: input.photoSourceSurfaceMatrix,
        missingPhotoSourceDisclosures: input.missingPhotoSourceDisclosures,
        relationLabel,
        photoLinkageStateLabel: input.handoffPackagePreview.photoLinkageStateLabel,
        photoLinkageHint: input.handoffPackagePreview.photoLinkageHint,
        photoLinkageNextActionLabel: input.handoffPackagePreview.photoLinkageNextActionLabel,
        photoCompareReadyLabel: photoArtifact
          ? photoArtifact.compareReadyLabel
          : pickAdminDashboardLocaleText(input.locale, { en: "No photo compare state", ko: "사진 비교 상태 없음" }),
        photoDeliverySourceLabel: photoArtifact
          ? photoArtifact.deliverySourceLabel
          : pickAdminDashboardLocaleText(input.locale, {
              en: "Issue the original download to classify the delivery source",
              ko: "원본 다운로드를 발급해 전달 source를 판정하세요",
            }),
        photoOriginalDownloadIssueLabel: photoArtifact
          ? formatIncidentHandoffDownloadIssueLabel(
              photoArtifact.downloadIssue,
              input.locale,
              photoArtifact.downloadAvailabilityLabel,
            )
          : pickAdminDashboardLocaleText(input.locale, { en: "Photo unavailable", ko: "사진 없음" }),
        photoDownloadLabel: photoArtifact
          ? formatIncidentHandoffDownloadIssueLabel(
              photoArtifact.downloadIssue,
              input.locale,
              photoArtifact.downloadAvailabilityLabel,
            )
          : pickAdminDashboardLocaleText(input.locale, { en: "Photo unavailable", ko: "사진 없음" }),
        logDownloadLabel: sameDeviceLog
          ? formatIncidentHandoffDownloadIssueLabel(
              sameDeviceLog.downloadIssue,
              input.locale,
              sameDeviceLog.downloadReadinessLabel,
            )
          : pickAdminDashboardLocaleText(input.locale, { en: "Log unavailable", ko: "로그 없음" }),
        traceJoinLabel:
          requestTrace?.joinKeyLabel ??
          pickAdminDashboardLocaleText(input.locale, { en: "No trace join", ko: "추적 정렬 없음" }),
        fallbackNextActionLabel: formatIncidentHandoffNextActionLabel({
          locale: input.locale,
          preview: input.handoffPackagePreview,
        }),
      }),
      browserDirectAcceptanceSummary: input.browserDirectContext
        ? buildIncidentBrowserDirectAcceptanceSummary({
            locale: input.locale,
            host: input.browserDirectContext.host,
            route: input.browserDirectContext.route ?? canonicalRoute,
            reopenRoute:
              input.browserDirectContext.reopenRoute ??
              canonicalReopenRoute,
            release: input.browserDirectContext.release,
            bundleFingerprint: input.browserDirectContext.bundleFingerprint,
            accountEmail: input.browserDirectContext.accountEmail,
            accountIdentityLabel: input.browserDirectContext.accountIdentityLabel,
            providerIdentityLabel: input.browserDirectContext.providerIdentityLabel,
            linkedProviderCountLabel: input.browserDirectContext.linkedProviderCountLabel,
            internalUserId: input.internalUserId,
            representativeArtifactId: input.browserDirectContext.representativeArtifactId,
            photoArtifact,
            requestTrace,
            sameDeviceLog,
            relationLabel,
            photoLinkageStateLabel: input.handoffPackagePreview.photoLinkageStateLabel,
            photoLinkageHint: input.handoffPackagePreview.photoLinkageHint,
            photoLinkageNextActionLabel: input.handoffPackagePreview.photoLinkageNextActionLabel,
            status: input.handoffPackagePreview.status,
          })
        : undefined,
    },
    workspace: input.handoffPackagePreview,
  };
}

function renderSectionTitle(title: string, body: string): JSX.Element {
  return (
    <div style={{ display: "grid", gap: "4px" }}>
      <h3 style={{ margin: 0, fontSize: "1.02rem", letterSpacing: "-0.02em" }}>{title}</h3>
      <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>{body}</p>
    </div>
  );
}

function buildUserDataDetailStateMeta(
  locale: "ko" | "en",
  internalUserId: string | undefined,
) {
  const lookupSummary = internalUserId ? `internalUserId=${internalUserId}` : undefined;
  const route = internalUserId ? `/admin/users/${internalUserId}` : "/admin/users/:internalUserId";

  return [
    buildAdminStateLookupMeta(locale, lookupSummary),
    buildAdminStateRouteMeta(locale, route),
  ].filter((item): item is NonNullable<typeof item> => item !== undefined);
}

function canIssueMediaAccess(availability: string): boolean {
  return availability === "available";
}

function openGrantedArtifact(url: string | undefined): void {
  if (typeof window === "undefined" || typeof url !== "string" || url.length === 0) {
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
}

function downloadJsonArtifact(filename: string, value: unknown): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const blob = new Blob([JSON.stringify(value, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  anchor.click();
  window.URL.revokeObjectURL(url);
}

function buildUploadedPhotoReviewPackageExport(input: {
  exportedAt: string;
  route: string;
  reopenRoute: string;
  internalUserId?: string;
  representativeArtifactId?: string;
  selectedArtifact?: {
    artifactId: string;
    sourceScenarioKey: string;
    sourceScenarioLabel: string;
    relationTypeLabel: string;
    artifactRoleLabel: string;
    uploadedAtLabel: string;
    previewAvailabilityLabel: string;
    downloadAvailabilityLabel: string;
    binaryReviewPathLabel: string;
    binaryReviewHint: string;
    latestTraceLabel: string;
    fallbackReasonLabel: string;
    requestTraceDrilldownTo?: string;
    linkedFailedFlowDrilldownTo?: string;
    linkedLifecycleDrilldownTo?: string;
    matchesScenarioRepresentative: boolean;
    matchesScenarioLatestUpload: boolean;
  };
  selectedArtifactPhotoCompareSummary?: IncidentPhotoCompareSummary;
  selectedScenarioSummary?: UploadedPhotoReviewPackageExport["evidenceSummary"]["selectedScenarioSummary"];
  representativeSelectionParityLabel?: string;
  timelineRows: UploadedPhotoReviewTimelineRow[];
  incidentCompareArtifactId?: string;
  nextActionLabel?: string;
}): UploadedPhotoReviewPackageExport {
  return {
    schemaVersion: "admin_uploaded_photo_review.v1",
    exportedAt: input.exportedAt,
    route: input.route,
    reopenRoute: input.reopenRoute,
    lookup: {
      internalUserId: input.internalUserId,
      representativeArtifactId: input.representativeArtifactId,
      selectedArtifactId: input.selectedArtifact?.artifactId,
      selectedScenarioKey: input.selectedArtifact?.sourceScenarioKey,
    },
    evidenceSummary: {
      representativeArtifactId: input.representativeArtifactId,
      representativeSelectionParityLabel:
        input.representativeSelectionParityLabel ??
        "Representative parity must be checked from the selected uploaded-photo workspace.",
      selectedArtifact: input.selectedArtifact
        ? {
            artifactId: input.selectedArtifact.artifactId,
            sourceScenarioKey: input.selectedArtifact.sourceScenarioKey,
            sourceScenarioLabel: input.selectedArtifact.sourceScenarioLabel,
            relationTypeLabel: input.selectedArtifact.relationTypeLabel,
            artifactRoleLabel: input.selectedArtifact.artifactRoleLabel,
            uploadedAtLabel: input.selectedArtifact.uploadedAtLabel,
            previewAvailabilityLabel: input.selectedArtifact.previewAvailabilityLabel,
            downloadAvailabilityLabel: input.selectedArtifact.downloadAvailabilityLabel,
            binaryReviewPathLabel: input.selectedArtifact.binaryReviewPathLabel,
            latestTraceLabel: input.selectedArtifact.latestTraceLabel,
            fallbackReasonLabel: input.selectedArtifact.fallbackReasonLabel,
            reviewNextActionLabel: input.selectedArtifact.binaryReviewHint,
            compareReadyLabel:
              input.selectedArtifactPhotoCompareSummary?.compareReadyLabel ??
              input.selectedArtifact.downloadAvailabilityLabel,
            deliverySourceLabel:
              input.selectedArtifactPhotoCompareSummary?.deliverySourceLabel ??
              input.selectedArtifact.binaryReviewPathLabel,
            originalDownloadIssueLabel:
              input.selectedArtifactPhotoCompareSummary?.originalDownloadIssueLabel ??
              input.selectedArtifact.downloadAvailabilityLabel,
            photoCompareRows: input.selectedArtifactPhotoCompareSummary?.parityRows ?? [],
            photoAccessIssuanceRows: input.selectedArtifactPhotoCompareSummary?.accessIssuanceRows ?? [],
          }
        : undefined,
      selectedMatchesRepresentative: input.selectedArtifact?.matchesScenarioRepresentative ?? false,
      selectedMatchesLatestUpload: input.selectedArtifact?.matchesScenarioLatestUpload ?? false,
      selectedScenarioSummary: input.selectedScenarioSummary,
      timelineRows: input.timelineRows,
      requestTraceDrilldownTo: input.selectedArtifact?.requestTraceDrilldownTo,
      linkedFailedFlowDrilldownTo: input.selectedArtifact?.linkedFailedFlowDrilldownTo,
      linkedLifecycleDrilldownTo: input.selectedArtifact?.linkedLifecycleDrilldownTo,
      incidentCompareArtifactId: input.incidentCompareArtifactId,
      nextActionLabel:
        input.nextActionLabel ??
        input.selectedArtifactPhotoCompareSummary?.compareNextActionLabel ??
        input.selectedArtifact?.binaryReviewHint ??
        "Re-open the selected uploaded photo workspace and compare the latest trace before exporting.",
    },
  };
}

function formatIncidentHandoffDownloadIssueLabel(
  issue: IncidentHandoffDownloadIssue,
  locale: "ko" | "en",
  fallback: string,
): string {
  if (!issue) {
    return fallback;
  }

  switch (issue.state) {
    case "issued":
      return pickAdminDashboardLocaleText(locale, { en: "Issued", ko: "발급됨" });
    case "issuing":
      return pickAdminDashboardLocaleText(locale, { en: "Issuing", ko: "발급 중" });
    case "failed":
      return pickAdminDashboardLocaleText(locale, { en: "Failed", ko: "실패" });
    case "ready_to_issue":
      return pickAdminDashboardLocaleText(locale, { en: "Ready to issue", ko: "발급 가능" });
    case "blocked":
      return pickAdminDashboardLocaleText(locale, { en: "Blocked", ko: "차단됨" });
  }
}

function formatIncidentTraceJoinLabel(
  workspace: UserDetailIncidentEvidenceWorkspace,
  locale: "ko" | "en",
  contextualFallback: string,
): string {
  if (workspace.traceJoinState !== "joined") {
    return contextualFallback;
  }

  if (workspace.traceJoinKey === "request_id") {
    return pickAdminDashboardLocaleText(locale, {
      en: "Aligned by request ID",
      ko: "request ID로 정렬됨",
    });
  }

  if (workspace.traceJoinKey === "correlation_id") {
    return pickAdminDashboardLocaleText(locale, {
      en: "Aligned by correlation ID",
      ko: "correlation ID로 정렬됨",
    });
  }

  return contextualFallback;
}

function formatIncidentHandoffRelationLabel(
  relation: UserDetailIncidentHandoffPackagePreview["relation"],
  locale: "ko" | "en",
): string {
  switch (relation) {
    case "direct_trace":
      return pickAdminDashboardLocaleText(locale, {
        en: "Direct trace join",
        ko: "직접 추적 결합",
      });
    case "contextual":
      return pickAdminDashboardLocaleText(locale, {
        en: "Contextual relation",
        ko: "문맥 연결",
      });
    case "unknown":
      return pickAdminDashboardLocaleText(locale, {
        en: "Unknown relation",
        ko: "관계 미확인",
      });
  }
}

function formatIncidentHandoffVerificationChecklistItem(
  item: UserDetailIncidentHandoffChecklistItem["id"],
  locale: "ko" | "en",
): string {
  switch (item) {
    case "verify_photo_artifact_identity":
      return pickAdminDashboardLocaleText(locale, {
        en: "Verify photo artifact identity",
        ko: "사진 artifact identity 확인",
      });
    case "verify_photo_download_issue_state":
      return pickAdminDashboardLocaleText(locale, {
        en: "Verify photo download issue state",
        ko: "사진 다운로드 상태 확인",
      });
    case "verify_trace_linkage":
      return pickAdminDashboardLocaleText(locale, {
        en: "Verify trace linkage",
        ko: "추적 연결 확인",
      });
    case "verify_same_device_header_parity":
      return pickAdminDashboardLocaleText(locale, {
        en: "Verify same-device header parity",
        ko: "동일 기기 header parity 확인",
      });
    case "verify_log_download_issue_state":
      return pickAdminDashboardLocaleText(locale, {
        en: "Verify log download issue state",
        ko: "로그 다운로드 상태 확인",
      });
    default:
      return item;
  }
}

function buildIncidentHandoffVerificationChecklist(input: {
  locale: "ko" | "en";
  photoArtifact: UserDetailIncidentHandoffPackagePreview["photoArtifact"];
  requestTrace: UserDetailIncidentHandoffPackagePreview["requestTrace"];
  sameDeviceLog: UserDetailIncidentHandoffPackagePreview["sameDeviceLog"];
}): UserDetailIncidentHandoffChecklistItem[] {
  const ids: Array<UserDetailIncidentHandoffChecklistItem["id"] | undefined> = [
    input.photoArtifact ? "verify_photo_artifact_identity" : undefined,
    input.photoArtifact?.downloadIssue ? "verify_photo_download_issue_state" : undefined,
    input.requestTrace?.requestId || input.requestTrace?.correlationId ? "verify_trace_linkage" : undefined,
    input.sameDeviceLog ? "verify_same_device_header_parity" : undefined,
    input.sameDeviceLog?.downloadIssue ? "verify_log_download_issue_state" : undefined,
  ];

  return ids
    .filter((value): value is UserDetailIncidentHandoffChecklistItem["id"] => typeof value === "string")
    .map((id) => ({
      id,
      label: formatIncidentHandoffVerificationChecklistItem(id, input.locale),
    }));
}

function buildIncidentHandoffBundleHashVerificationHint(
  sameDeviceLog: UserDetailIncidentHandoffPackagePreview["sameDeviceLog"],
  locale: "ko" | "en",
): string | undefined {
  if (!sameDeviceLog) {
    return undefined;
  }

  return pickAdminDashboardLocaleText(locale, {
    en: "Confirm the downloaded bundle manifest keeps the same same-device header, manifest version, redaction version, and bundle hash presence shown in this export.",
    ko: "다운로드한 bundle manifest가 이 export에 표시된 동일 기기 header, manifest 버전, redaction 버전, bundle hash 존재 여부를 그대로 유지하는지 확인하세요.",
  });
}

function buildIncidentSameDeviceHeaderRows(input: {
  labels: {
    artifactId: string;
    deviceInstallId: string;
    release: string;
    appVersion: string;
    platform: string;
    runtimeSummary: string;
    capturedAt: string;
    captureTrigger: string;
    captureWindow: string;
    bundleHash: string;
    manifestVersion: string;
  };
  source: {
    artifactId: string;
    deviceInstallId: string;
    releaseLabel: string;
    appVersionLabel: string;
    platformLabel: string;
    runtimeSummaryLabel: string;
    capturedAtOperatorValueLabel: string;
    captureTriggerLabel: string;
    captureWindowLabel: string;
    bundleHashPresenceLabel: string;
    manifestVersionLabel: string;
  };
}): IncidentSameDeviceHeaderRow[] {
  return [
    { key: "artifactId", label: input.labels.artifactId, value: input.source.artifactId },
    { key: "deviceInstallId", label: input.labels.deviceInstallId, value: input.source.deviceInstallId },
    { key: "release", label: input.labels.release, value: input.source.releaseLabel },
    { key: "appVersion", label: input.labels.appVersion, value: input.source.appVersionLabel },
    { key: "platform", label: input.labels.platform, value: input.source.platformLabel },
    { key: "runtimeSummary", label: input.labels.runtimeSummary, value: input.source.runtimeSummaryLabel },
    { key: "capturedAt", label: input.labels.capturedAt, value: input.source.capturedAtOperatorValueLabel },
    { key: "captureTrigger", label: input.labels.captureTrigger, value: input.source.captureTriggerLabel },
    { key: "captureWindow", label: input.labels.captureWindow, value: input.source.captureWindowLabel },
    { key: "bundleHash", label: input.labels.bundleHash, value: input.source.bundleHashPresenceLabel },
    { key: "manifestVersion", label: input.labels.manifestVersion, value: input.source.manifestVersionLabel },
  ];
}

function buildIncidentPhotoCompareRows(input: {
  labels: {
    artifactId: string;
    sourceScenario: string;
    relationType: string;
    artifactRole: string;
    contentType: string;
    sizeBucket: string;
    uploadedAt: string;
    previewAvailability: string;
    downloadAvailability: string;
    binaryReviewPath: string;
    archiveState: string;
    incidentAttachment: string;
    latestTrace: string;
  };
  source: {
    artifactId: string;
    sourceScenarioLabel: string;
    relationTypeLabel: string;
    artifactRoleLabel: string;
    contentTypeBucketLabel: string;
    sizeBucketLabel: string;
    uploadedAtLabel: string;
    previewAvailabilityLabel: string;
    downloadAvailabilityLabel: string;
    binaryReviewPathLabel: string;
    archiveStateLabel: string;
    incidentAttachmentStateLabel: string;
    latestTraceLabel: string;
  };
}): IncidentPhotoCompareRow[] {
  return [
    { key: "artifactId", label: input.labels.artifactId, value: input.source.artifactId },
    { key: "sourceScenario", label: input.labels.sourceScenario, value: input.source.sourceScenarioLabel },
    { key: "relationType", label: input.labels.relationType, value: input.source.relationTypeLabel },
    { key: "artifactRole", label: input.labels.artifactRole, value: input.source.artifactRoleLabel },
    { key: "contentType", label: input.labels.contentType, value: input.source.contentTypeBucketLabel },
    { key: "sizeBucket", label: input.labels.sizeBucket, value: input.source.sizeBucketLabel },
    { key: "uploadedAt", label: input.labels.uploadedAt, value: input.source.uploadedAtLabel },
    {
      key: "previewAvailability",
      label: input.labels.previewAvailability,
      value: input.source.previewAvailabilityLabel,
    },
    {
      key: "downloadAvailability",
      label: input.labels.downloadAvailability,
      value: input.source.downloadAvailabilityLabel,
    },
    {
      key: "binaryReviewPath",
      label: input.labels.binaryReviewPath,
      value: input.source.binaryReviewPathLabel,
    },
    {
      key: "archiveState",
      label: input.labels.archiveState,
      value: input.source.archiveStateLabel,
    },
    {
      key: "incidentAttachment",
      label: input.labels.incidentAttachment,
      value: input.source.incidentAttachmentStateLabel,
    },
    { key: "latestTrace", label: input.labels.latestTrace, value: input.source.latestTraceLabel },
  ];
}

function buildIncidentExcerptReplayLines(
  previewLines: IncidentEvidenceIncidentLog["previewLines"],
): IncidentExcerptReplayLine[] {
  return previewLines.map((line) => ({
    timestampLabel: line.timestampLabel,
    levelLabel: line.levelLabel,
    categoryLabel: line.categoryLabel,
    messageKeyLabel: line.messageKeyLabel,
    redactedContextLabel: line.redactedContextLabel,
    requestIdLabel: line.requestIdLabel,
    correlationIdLabel: line.correlationIdLabel,
    scenarioKeyLabel: line.scenarioKeyLabel,
  }));
}

function buildIncidentBundleContractSummary(input: {
  locale: "ko" | "en";
  sameDeviceLog: {
    artifactId: string;
    deviceInstallId: string;
    releaseLabel: string;
    appVersionLabel: string;
    platformLabel: string;
    capturedAtOperatorValueLabel: string;
    captureWindowLabel: string;
    captureTriggerLabel: string;
    bundleHashPresenceLabel: string;
    manifestVersionLabel: string;
    redactionVersionLabel: string;
    latestRequestIdLabel: string;
    latestCorrelationIdLabel: string;
    excerptLineCountBucketLabel: string;
  };
}): IncidentBundleContractSummary {
  const bundleBaseName = `${input.sameDeviceLog.artifactId}__same_device_bundle`;

  return {
    bundleBaseName,
    manifestFilename: "manifest.json",
    payloadFilename: "logs.ndjson",
    compareSummaryFilename: "compare-summary.json",
    payloadFormatLabel: pickAdminDashboardLocaleText(input.locale, {
      en: "NDJSON lines: timestamp, level, category, messageKey, redactedContext, requestId, correlationId, scenarioKey, errorCode",
      ko: "NDJSON 라인: timestamp, level, category, messageKey, redactedContext, requestId, correlationId, scenarioKey, errorCode",
    }),
    manifestFieldRows: [
      { key: "artifactId", label: "artifactId", value: input.sameDeviceLog.artifactId },
      { key: "deviceInstallId", label: "deviceInstallId", value: input.sameDeviceLog.deviceInstallId },
      { key: "release", label: "release", value: input.sameDeviceLog.releaseLabel },
      { key: "appVersion", label: "appVersion", value: input.sameDeviceLog.appVersionLabel },
      { key: "platform", label: "platform", value: input.sameDeviceLog.platformLabel },
      { key: "capturedAt", label: "capturedAt", value: input.sameDeviceLog.capturedAtOperatorValueLabel },
      { key: "captureWindow", label: "captureWindow", value: input.sameDeviceLog.captureWindowLabel },
      { key: "captureTrigger", label: "captureTrigger", value: input.sameDeviceLog.captureTriggerLabel },
      { key: "bundleHash", label: "bundleHash", value: input.sameDeviceLog.bundleHashPresenceLabel },
      { key: "redactionVersion", label: "redactionVersion", value: input.sameDeviceLog.redactionVersionLabel },
      { key: "manifestVersion", label: "manifestVersion", value: input.sameDeviceLog.manifestVersionLabel },
      { key: "requestId", label: "requestId", value: input.sameDeviceLog.latestRequestIdLabel },
      { key: "correlationId", label: "correlationId", value: input.sameDeviceLog.latestCorrelationIdLabel },
      { key: "lineCount", label: "lineCount", value: input.sameDeviceLog.excerptLineCountBucketLabel },
      { key: "truncated", label: "truncated", value: pickAdminDashboardLocaleText(input.locale, { en: "Not recorded", ko: "기록 없음" }) },
      {
        key: "retentionExpiresAt",
        label: "retentionExpiresAt",
        value: pickAdminDashboardLocaleText(input.locale, { en: "Not recorded", ko: "기록 없음" }),
      },
    ],
    lineSchemaLabels: [
      "timestamp",
      "level",
      "category",
      "messageKey",
      "redactedContext",
      "requestId",
      "correlationId",
      "scenarioKey",
      "errorCode",
    ],
  };
}

function buildIncidentRootCauseWorkspaceSummary(input: {
  locale: "ko" | "en";
  photoArtifact: UserDetailIncidentHandoffPackagePreview["photoArtifact"];
  requestTrace: UserDetailIncidentHandoffPackagePreview["requestTrace"];
  sameDeviceLog: UserDetailIncidentHandoffPackagePreview["sameDeviceLog"];
  route: string;
  reopenRoute: string;
  photoSourceSurfaceMatrix: IncidentHandoffSourceSurfaceMatrixRow[];
  missingPhotoSourceDisclosures: IncidentHandoffMissingSourceDisclosureRow[];
  relationLabel: string;
  photoLinkageStateLabel: string;
  photoLinkageHint: string;
  photoLinkageNextActionLabel: string;
  photoCompareReadyLabel: string;
  photoDeliverySourceLabel: string;
  photoOriginalDownloadIssueLabel: string;
  photoDownloadLabel: string;
  logDownloadLabel: string;
  traceJoinLabel: string;
  fallbackNextActionLabel: string;
}): IncidentRootCauseWorkspaceSummary | undefined {
  if (!input.photoArtifact && !input.sameDeviceLog && !input.requestTrace) {
    return undefined;
  }

  const readingOrderRows: IncidentRootCauseWorkspaceRow[] = [
    {
      key: "photoEvidence",
      label: pickAdminDashboardLocaleText(input.locale, { en: "1. Photo evidence", ko: "1. 사진 증거" }),
      value: input.photoArtifact
        ? `${input.photoArtifact.artifactId} / ${input.photoArtifact.sourceScenarioLabel} / ${input.photoArtifact.relationTypeLabel} / ${input.photoArtifact.artifactRoleLabel} / ${input.photoArtifact.binaryReviewPathLabel} / ${input.photoArtifact.archiveStateLabel} / ${input.photoArtifact.incidentAttachmentStateLabel}`
        : pickAdminDashboardLocaleText(input.locale, { en: "No photo pinned", ko: "고정된 사진 없음" }),
    },
    {
      key: "requestTrace",
      label: pickAdminDashboardLocaleText(input.locale, { en: "2. Request trace", ko: "2. 요청 추적" }),
      value: input.requestTrace
        ? `${input.requestTrace.requestId ?? "Unavailable"} / ${input.requestTrace.correlationId ?? "Unavailable"} / ${input.traceJoinLabel}`
        : pickAdminDashboardLocaleText(input.locale, { en: "No trace pinned", ko: "고정된 추적 없음" }),
    },
    {
      key: "inlineLogPreview",
      label: pickAdminDashboardLocaleText(input.locale, { en: "3. Inline log preview", ko: "3. inline 로그 미리보기" }),
      value: input.sameDeviceLog
        ? `${input.sameDeviceLog.artifactId} / ${input.sameDeviceLog.previewHeadlineLabel} / ${input.sameDeviceLog.previewReadinessLabel}`
        : pickAdminDashboardLocaleText(input.locale, { en: "No same-device log pinned", ko: "고정된 same-device 로그 없음" }),
    },
    {
      key: "photoDownload",
      label: pickAdminDashboardLocaleText(input.locale, { en: "4. Photo download", ko: "4. 사진 다운로드" }),
      value: input.photoArtifact
        ? `${input.photoCompareReadyLabel} / ${input.photoOriginalDownloadIssueLabel} / ${input.photoDeliverySourceLabel}`
        : pickAdminDashboardLocaleText(input.locale, { en: "Photo unavailable", ko: "사진 없음" }),
    },
    {
      key: "logDownload",
      label: pickAdminDashboardLocaleText(input.locale, { en: "5. Log download", ko: "5. 로그 다운로드" }),
      value: input.sameDeviceLog ? input.logDownloadLabel : pickAdminDashboardLocaleText(input.locale, { en: "Log unavailable", ko: "로그 없음" }),
    },
    {
      key: "parityDecision",
      label: pickAdminDashboardLocaleText(input.locale, { en: "6. Parity decision", ko: "6. parity 판정" }),
      value: `${input.relationLabel} / ${input.photoLinkageStateLabel}`,
    },
  ];

  const routeRows: IncidentRootCauseDetailRow[] = [
    {
      key: "route",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Canonical route", ko: "기준 경로" }),
      value: input.route,
    },
    {
      key: "reopenRoute",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Reopen route", ko: "재진입 경로" }),
      value: input.reopenRoute,
    },
  ];

  const photoReviewRows = input.photoArtifact
    ? buildIncidentPhotoCompareRows({
        labels: {
          artifactId: pickAdminDashboardLocaleText(input.locale, { en: "Artifact ID", ko: "Artifact ID" }),
          sourceScenario: pickAdminDashboardLocaleText(input.locale, { en: "Source", ko: "Source" }),
          relationType: pickAdminDashboardLocaleText(input.locale, { en: "Relation", ko: "Relation" }),
          artifactRole: pickAdminDashboardLocaleText(input.locale, { en: "Role", ko: "역할" }),
          contentType: pickAdminDashboardLocaleText(input.locale, { en: "Content Type", ko: "콘텐츠 유형" }),
          sizeBucket: pickAdminDashboardLocaleText(input.locale, { en: "Size", ko: "크기" }),
          uploadedAt: pickAdminDashboardLocaleText(input.locale, { en: "Created", ko: "생성 시각" }),
          previewAvailability: pickAdminDashboardLocaleText(input.locale, { en: "Preview", ko: "미리보기" }),
          downloadAvailability: pickAdminDashboardLocaleText(input.locale, { en: "Download", ko: "다운로드" }),
          binaryReviewPath: pickAdminDashboardLocaleText(input.locale, {
            en: "Binary review path",
            ko: "binary review 경로",
          }),
          archiveState: pickAdminDashboardLocaleText(input.locale, {
            en: "Archive state",
            ko: "archive 상태",
          }),
          incidentAttachment: pickAdminDashboardLocaleText(input.locale, {
            en: "Incident attachment",
            ko: "incident 연결",
          }),
          latestTrace: pickAdminDashboardLocaleText(input.locale, { en: "Latest trace", ko: "최근 추적" }),
        },
        source: input.photoArtifact,
      })
    : [];
  const photoAccessIssuanceRows = input.photoArtifact ? input.photoArtifact.accessIssuanceRows : [];

  const sameDeviceLogRows = buildIncidentMinimumSameDeviceLogRows({
    locale: input.locale,
    source: input.sameDeviceLog,
  });
  const sameDeviceLogAccessIssuanceRows = input.sameDeviceLog ? input.sameDeviceLog.accessIssuanceRows : [];

  const sourceSurfaceRows: IncidentRootCauseSourceSurfaceRow[] = input.photoSourceSurfaceMatrix.map((entry) => ({
    key: entry.scenarioKey,
    label: entry.label,
    value:
      `${entry.targetLinkedEntityLabel} / ${entry.sourceConnectionLabel} / ${entry.baselineStatusLabel} / ` +
      `${entry.currentCoverageStateLabel} / ${entry.previewReadyCountLabel} / ${entry.downloadReadyCountLabel} / ` +
      `${entry.blockedCountLabel} / ${entry.binaryReviewPathLabel} / ${entry.baselineSummaryLabel}`,
  }));

  const missingSourceRows: IncidentRootCauseMissingSourceRow[] = input.missingPhotoSourceDisclosures.map((entry) => ({
    key: entry.scenarioKey,
    label: entry.label,
    value: `${entry.sourceConnectionLabel} / ${entry.statusLabel} / ${entry.reasonLabel} / ${entry.nextActionHint}`,
  }));

  return {
    readingOrderRows,
    routeRows,
    photoReviewRows,
    photoAccessIssuanceRows,
    sameDeviceLogRows,
    sameDeviceLogAccessIssuanceRows,
    sourceSurfaceRows,
    missingSourceRows,
    photoLinkageStateLabel: input.photoLinkageStateLabel,
    photoLinkageHint: input.photoLinkageHint,
    parityDecisionLabel: `${input.relationLabel} / ${input.photoLinkageStateLabel}`,
    photoLinkageNextActionLabel: input.photoLinkageNextActionLabel,
    photoCompareReadyLabel: input.photoCompareReadyLabel,
    photoDeliverySourceLabel: input.photoDeliverySourceLabel,
    photoOriginalDownloadIssueLabel: input.photoOriginalDownloadIssueLabel,
    deliveryPathStateLabel:
      input.sameDeviceLog?.deliveryPathStateLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "Delivery path unavailable",
        ko: "전달 경로 상태 없음",
      }),
    bundleDeliverySourceLabel:
      input.sameDeviceLog?.bundleDeliverySourceLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "Issue bundle download to classify delivery source",
        ko: "bundle 다운로드를 발급해 전달 source를 판정하세요",
      }),
    producerPathNextActionLabel:
      input.sameDeviceLog?.producerPathNextActionLabel ?? input.fallbackNextActionLabel,
    fallbackNextActionLabel: input.fallbackNextActionLabel,
  };
}

function buildIncidentMinimumSameDeviceLogRows(input: {
  locale: "ko" | "en";
  source: UserDetailIncidentHandoffPackagePreview["sameDeviceLog"];
}): IncidentRootCauseDetailRow[] {
  if (!input.source) {
    return [];
  }

  return [
    {
      key: "artifactId",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Artifact ID", ko: "Artifact ID" }),
      value: input.source.artifactId,
    },
    {
      key: "deviceInstallId",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Device install", ko: "기기 설치본" }),
      value: input.source.deviceInstallId,
    },
    {
      key: "release",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Release", ko: "릴리스" }),
      value: input.source.releaseLabel,
    },
    {
      key: "appVersion",
      label: pickAdminDashboardLocaleText(input.locale, { en: "App version", ko: "앱 버전" }),
      value: input.source.appVersionLabel,
    },
    {
      key: "platform",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Platform", ko: "플랫폼" }),
      value: input.source.platformLabel,
    },
    {
      key: "capturedAt",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Captured at", ko: "수집 시각" }),
      value: input.source.capturedAtOperatorValueLabel,
    },
    {
      key: "captureWindow",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Capture window", ko: "수집 구간" }),
      value: input.source.captureWindowLabel,
    },
    {
      key: "captureTrigger",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Capture trigger", ko: "수집 트리거" }),
      value: input.source.captureTriggerLabel,
    },
    {
      key: "previewReadiness",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Preview readiness", ko: "미리보기 준비도" }),
      value: input.source.previewReadinessLabel,
    },
    {
      key: "downloadReadiness",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Download readiness", ko: "다운로드 준비도" }),
      value: input.source.downloadReadinessLabel,
    },
    {
      key: "bundleHash",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Bundle hash", ko: "Bundle hash" }),
      value: input.source.bundleHashPresenceLabel,
    },
    {
      key: "manifestVersion",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Manifest", ko: "Manifest" }),
      value: input.source.manifestVersionLabel,
    },
    {
      key: "redactionVersion",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Redaction", ko: "Redaction" }),
      value: input.source.redactionVersionLabel,
    },
    {
      key: "deliveryPathState",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Delivery path", ko: "전달 경로" }),
      value: input.source.deliveryPathStateLabel,
    },
    {
      key: "bundleDeliverySource",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Bundle delivery", ko: "bundle 전달" }),
      value: input.source.bundleDeliverySourceLabel,
    },
    {
      key: "latestTrace",
      label: pickAdminDashboardLocaleText(input.locale, { en: "Latest trace", ko: "최근 추적" }),
      value: `${input.source.latestRequestIdLabel} / ${input.source.latestCorrelationIdLabel}`,
    },
  ];
}

function buildIncidentBrowserDirectEvidenceRows(input: {
  labels: {
    host: string;
    route: string;
    reopenRoute: string;
    locale: string;
    release: string;
    bundleFingerprint: string;
    account: string;
    accountCue: string;
    providerCue: string;
    linkedProviders: string;
    internalUserId: string;
    representativeArtifactId: string;
    selectedArtifactId: string;
    photoPreviewArtifactId: string;
    photoDownloadArtifactId: string;
    photoLogRelation: string;
    sameDeviceLogArtifactId: string;
    deliveryPathState: string;
    bundleDeliverySource: string;
  };
  source: {
    host: string;
    route: string;
    reopenRoute: string;
    locale: string;
    release: string;
    bundleFingerprint: string;
    accountEmail: string;
    accountIdentityLabel?: string;
    providerIdentityLabel?: string;
    linkedProviderCountLabel?: string;
    internalUserId?: string;
    representativeArtifactId?: string;
    selectedArtifactId?: string;
    photoPreviewArtifactId?: string;
    photoDownloadArtifactId?: string;
    photoLogRelation?: string;
    sameDeviceLogArtifactId?: string;
    deliveryPathState?: string;
    bundleDeliverySource?: string;
  };
}): IncidentBrowserDirectEvidenceRow[] {
  const rows: IncidentBrowserDirectEvidenceRow[] = [
    { key: "host", label: input.labels.host, value: input.source.host },
    { key: "route", label: input.labels.route, value: input.source.route },
    { key: "reopenRoute", label: input.labels.reopenRoute, value: input.source.reopenRoute },
    { key: "locale", label: input.labels.locale, value: input.source.locale },
    { key: "release", label: input.labels.release, value: input.source.release },
    { key: "bundleFingerprint", label: input.labels.bundleFingerprint, value: input.source.bundleFingerprint },
    { key: "account", label: input.labels.account, value: input.source.accountEmail },
  ];

  if (input.source.accountIdentityLabel) {
    rows.push({ key: "accountCue", label: input.labels.accountCue, value: input.source.accountIdentityLabel });
  }
  if (input.source.providerIdentityLabel) {
    rows.push({ key: "providerCue", label: input.labels.providerCue, value: input.source.providerIdentityLabel });
  }
  if (input.source.linkedProviderCountLabel) {
    rows.push({
      key: "linkedProviders",
      label: input.labels.linkedProviders,
      value: input.source.linkedProviderCountLabel,
    });
  }

  if (input.source.internalUserId) {
    rows.push({ key: "internalUserId", label: input.labels.internalUserId, value: input.source.internalUserId });
  }
  if (input.source.representativeArtifactId) {
    rows.push({
      key: "representativeArtifactId",
      label: input.labels.representativeArtifactId,
      value: input.source.representativeArtifactId,
    });
  }
  if (input.source.selectedArtifactId) {
    rows.push({
      key: "selectedArtifactId",
      label: input.labels.selectedArtifactId,
      value: input.source.selectedArtifactId,
    });
  }
  if (input.source.photoPreviewArtifactId) {
    rows.push({
      key: "photoPreviewArtifactId",
      label: input.labels.photoPreviewArtifactId,
      value: input.source.photoPreviewArtifactId,
    });
  }
  if (input.source.photoDownloadArtifactId) {
    rows.push({
      key: "photoDownloadArtifactId",
      label: input.labels.photoDownloadArtifactId,
      value: input.source.photoDownloadArtifactId,
    });
  }
  if (input.source.photoLogRelation) {
    rows.push({
      key: "photoLogRelation",
      label: input.labels.photoLogRelation,
      value: input.source.photoLogRelation,
    });
  }
  if (input.source.sameDeviceLogArtifactId) {
    rows.push({
      key: "sameDeviceLogArtifactId",
      label: input.labels.sameDeviceLogArtifactId,
      value: input.source.sameDeviceLogArtifactId,
    });
  }
  if (input.source.deliveryPathState) {
    rows.push({
      key: "deliveryPathState",
      label: input.labels.deliveryPathState,
      value: input.source.deliveryPathState,
    });
  }
  if (input.source.bundleDeliverySource) {
    rows.push({
      key: "bundleDeliverySource",
      label: input.labels.bundleDeliverySource,
      value: input.source.bundleDeliverySource,
    });
  }

  return rows;
}

function buildIncidentBrowserDirectTraceLinkageRows(input: {
  labels: {
    requestId: string;
    correlationId: string;
    failedFlowLink: string;
  };
  source: {
    requestId?: string;
    correlationId?: string;
    failedFlowLink?: string;
  };
}): IncidentBrowserDirectTraceLinkageRow[] {
  const rows: IncidentBrowserDirectTraceLinkageRow[] = [];

  if (input.source.requestId) {
    rows.push({ key: "requestId", label: input.labels.requestId, value: input.source.requestId });
  }
  if (input.source.correlationId) {
    rows.push({ key: "correlationId", label: input.labels.correlationId, value: input.source.correlationId });
  }
  if (input.source.failedFlowLink) {
    rows.push({ key: "failedFlowLink", label: input.labels.failedFlowLink, value: input.source.failedFlowLink });
  }

  return rows;
}

function formatIncidentBrowserDirectHandoffParityResultLabel(input: {
  locale: "ko" | "en";
  status: UserDetailIncidentHandoffPackagePreview["status"];
  relationLabel: string;
  photoLinkageStateLabel: string;
}): string {
  if (input.status === "ready") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: `Preview / download / handoff aligned (${input.relationLabel} / ${input.photoLinkageStateLabel})`,
      ko: `preview / download / handoff 정렬 완료 (${input.relationLabel} / ${input.photoLinkageStateLabel})`,
    });
  }

  if (input.status === "needs_trace_join") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: `Parity drift: trace join required (${input.photoLinkageStateLabel})`,
      ko: `parity 드리프트: 추적 정렬 필요 (${input.photoLinkageStateLabel})`,
    });
  }

  if (input.status === "needs_photo" || input.status === "needs_log") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: `Parity incomplete: missing paired evidence (${input.photoLinkageStateLabel})`,
      ko: `parity 미완료: 짝이 되는 증거 누락 (${input.photoLinkageStateLabel})`,
    });
  }

  return pickAdminDashboardLocaleText(input.locale, {
    en: "Parity unavailable: no paired photo/log evidence is pinned yet",
    ko: "parity 불가: 아직 짝이 되는 사진/로그 증거가 고정되지 않았습니다",
  });
}

function buildIncidentBrowserDirectAcceptanceSummary(input: {
  locale: "ko" | "en";
  host: string;
  route: string;
  reopenRoute: string;
  release: string;
  bundleFingerprint: string;
  accountEmail: string;
  accountIdentityLabel?: string;
  providerIdentityLabel?: string;
  linkedProviderCountLabel?: string;
  internalUserId?: string;
  representativeArtifactId?: string;
  photoArtifact: UserDetailIncidentHandoffPackagePreview["photoArtifact"];
  requestTrace: UserDetailIncidentHandoffPackagePreview["requestTrace"];
  sameDeviceLog: UserDetailIncidentHandoffPackagePreview["sameDeviceLog"];
  relationLabel: string;
  photoLinkageStateLabel: string;
  photoLinkageHint: string;
  photoLinkageNextActionLabel: string;
  status: UserDetailIncidentHandoffPackagePreview["status"];
}): IncidentBrowserDirectAcceptanceSummary | undefined {
  if (!input.photoArtifact && !input.sameDeviceLog) {
    return undefined;
  }

  const photoPreviewArtifactId =
    input.photoArtifact?.previewAvailability === "available"
      ? input.photoArtifact.artifactId
      : undefined;
  const photoDownloadArtifactId =
    input.photoArtifact &&
    input.photoArtifact.downloadAvailability === "available"
      ? input.photoArtifact.artifactId
      : undefined;
  const photoPreviewReadinessLabel = input.photoArtifact
    ? input.photoArtifact.previewAvailabilityLabel
    : pickAdminDashboardLocaleText(input.locale, { en: "No photo preview", ko: "사진 미리보기 없음" });
  const logPreviewReadinessLabel =
    input.sameDeviceLog?.previewReadinessLabel ??
    pickAdminDashboardLocaleText(input.locale, { en: "No log preview", ko: "로그 미리보기 없음" });
  const photoDownloadReadinessLabel = input.photoArtifact
    ? formatIncidentHandoffDownloadIssueLabel(
        input.photoArtifact.downloadIssue,
        input.locale,
        input.photoArtifact.downloadAvailabilityLabel,
      )
    : pickAdminDashboardLocaleText(input.locale, { en: "No photo download", ko: "사진 다운로드 없음" });
  const logDownloadReadinessLabel = input.sameDeviceLog
    ? formatIncidentHandoffDownloadIssueLabel(
        input.sameDeviceLog.downloadIssue,
        input.locale,
        input.sameDeviceLog.downloadReadinessLabel,
      )
    : pickAdminDashboardLocaleText(input.locale, { en: "No log download", ko: "로그 다운로드 없음" });

  return {
    evidencePackRows: buildIncidentBrowserDirectEvidenceRows({
      labels: {
        host: pickAdminDashboardLocaleText(input.locale, { en: "Host", ko: "호스트" }),
        route: pickAdminDashboardLocaleText(input.locale, { en: "Route", ko: "경로" }),
        reopenRoute: pickAdminDashboardLocaleText(input.locale, { en: "Reopen route", ko: "재진입 경로" }),
        locale: pickAdminDashboardLocaleText(input.locale, { en: "Locale", ko: "Locale" }),
        release: pickAdminDashboardLocaleText(input.locale, { en: "Release", ko: "릴리스" }),
        bundleFingerprint: pickAdminDashboardLocaleText(input.locale, { en: "Bundle fingerprint", ko: "Bundle fingerprint" }),
        account: pickAdminDashboardLocaleText(input.locale, { en: "Account", ko: "계정" }),
        accountCue: pickAdminDashboardLocaleText(input.locale, { en: "Account cue", ko: "계정 단서" }),
        providerCue: pickAdminDashboardLocaleText(input.locale, { en: "Provider cue", ko: "provider 단서" }),
        linkedProviders: pickAdminDashboardLocaleText(input.locale, { en: "Linked providers", ko: "연결된 provider" }),
        internalUserId: pickAdminDashboardLocaleText(input.locale, { en: "Internal user", ko: "내부 사용자" }),
        representativeArtifactId: pickAdminDashboardLocaleText(input.locale, { en: "Representative artifact", ko: "대표 artifact" }),
        selectedArtifactId: pickAdminDashboardLocaleText(input.locale, { en: "Selected artifact", ko: "선택 artifact" }),
        photoPreviewArtifactId: pickAdminDashboardLocaleText(input.locale, { en: "Photo preview artifact", ko: "사진 미리보기 artifact" }),
        photoDownloadArtifactId: pickAdminDashboardLocaleText(input.locale, { en: "Photo download artifact", ko: "사진 다운로드 artifact" }),
        photoLogRelation: pickAdminDashboardLocaleText(input.locale, { en: "Photo/log relation", ko: "사진/로그 관계" }),
        sameDeviceLogArtifactId: pickAdminDashboardLocaleText(input.locale, { en: "Same-device log artifact", ko: "동일 기기 로그 artifact" }),
        deliveryPathState: pickAdminDashboardLocaleText(input.locale, { en: "Delivery path", ko: "전달 경로" }),
        bundleDeliverySource: pickAdminDashboardLocaleText(input.locale, { en: "Bundle delivery", ko: "bundle 전달" }),
      },
      source: {
        host: input.host,
        route: input.route,
        reopenRoute: input.reopenRoute,
        locale: input.locale,
        release: input.release,
        bundleFingerprint: input.bundleFingerprint,
        accountEmail: input.accountEmail,
        accountIdentityLabel: input.accountIdentityLabel,
        providerIdentityLabel: input.providerIdentityLabel,
        linkedProviderCountLabel: input.linkedProviderCountLabel,
        internalUserId: input.internalUserId,
        representativeArtifactId: input.representativeArtifactId,
        selectedArtifactId: input.photoArtifact?.artifactId,
        photoPreviewArtifactId,
        photoDownloadArtifactId,
        photoLogRelation: input.relationLabel,
        sameDeviceLogArtifactId: input.sameDeviceLog?.artifactId,
        deliveryPathState: input.sameDeviceLog?.deliveryPathStateLabel,
        bundleDeliverySource: input.sameDeviceLog?.bundleDeliverySourceLabel,
      },
    }),
    photoReviewRows: input.photoArtifact
      ? buildIncidentPhotoCompareRows({
          labels: {
            artifactId: pickAdminDashboardLocaleText(input.locale, { en: "Artifact ID", ko: "Artifact ID" }),
            sourceScenario: pickAdminDashboardLocaleText(input.locale, { en: "Source", ko: "Source" }),
            relationType: pickAdminDashboardLocaleText(input.locale, { en: "Relation", ko: "Relation" }),
            artifactRole: pickAdminDashboardLocaleText(input.locale, { en: "Role", ko: "역할" }),
            contentType: pickAdminDashboardLocaleText(input.locale, { en: "Content Type", ko: "콘텐츠 유형" }),
            sizeBucket: pickAdminDashboardLocaleText(input.locale, { en: "Size", ko: "크기" }),
            uploadedAt: pickAdminDashboardLocaleText(input.locale, { en: "Created", ko: "생성 시각" }),
            previewAvailability: pickAdminDashboardLocaleText(input.locale, { en: "Preview", ko: "미리보기" }),
            downloadAvailability: pickAdminDashboardLocaleText(input.locale, { en: "Download", ko: "다운로드" }),
            binaryReviewPath: pickAdminDashboardLocaleText(input.locale, {
              en: "Binary review path",
              ko: "binary review 경로",
            }),
            archiveState: pickAdminDashboardLocaleText(input.locale, {
              en: "Archive state",
              ko: "archive 상태",
            }),
            incidentAttachment: pickAdminDashboardLocaleText(input.locale, {
              en: "Incident attachment",
              ko: "incident 연결",
            }),
            latestTrace: pickAdminDashboardLocaleText(input.locale, { en: "Latest trace", ko: "최근 추적" }),
          },
          source: input.photoArtifact,
        })
      : [],
    photoAccessIssuanceRows: input.photoArtifact ? input.photoArtifact.accessIssuanceRows : [],
    sameDeviceLogRows: buildIncidentMinimumSameDeviceLogRows({
      locale: input.locale,
      source: input.sameDeviceLog,
    }),
    sameDeviceLogAccessIssuanceRows: input.sameDeviceLog ? input.sameDeviceLog.accessIssuanceRows : [],
    excerptReplayLines: input.sameDeviceLog?.excerptReplayLines ?? [],
    sameDeviceHeaderSummaryRows: input.sameDeviceLog
      ? buildIncidentSameDeviceHeaderRows({
          labels: {
            artifactId: pickAdminDashboardLocaleText(input.locale, { en: "Artifact ID", ko: "Artifact ID" }),
            deviceInstallId: pickAdminDashboardLocaleText(input.locale, { en: "Device install", ko: "기기 설치본" }),
            release: pickAdminDashboardLocaleText(input.locale, { en: "Release", ko: "릴리스" }),
            appVersion: pickAdminDashboardLocaleText(input.locale, { en: "App version", ko: "앱 버전" }),
            platform: pickAdminDashboardLocaleText(input.locale, { en: "Platform", ko: "플랫폼" }),
            runtimeSummary: pickAdminDashboardLocaleText(input.locale, { en: "Runtime summary", ko: "런타임 요약" }),
            capturedAt: pickAdminDashboardLocaleText(input.locale, { en: "Captured at", ko: "수집 시각" }),
            captureTrigger: pickAdminDashboardLocaleText(input.locale, { en: "Capture trigger", ko: "수집 트리거" }),
            captureWindow: pickAdminDashboardLocaleText(input.locale, { en: "Capture window", ko: "수집 구간" }),
            bundleHash: pickAdminDashboardLocaleText(input.locale, { en: "Bundle hash", ko: "Bundle hash" }),
            manifestVersion: pickAdminDashboardLocaleText(input.locale, { en: "Manifest", ko: "Manifest" }),
          },
          source: input.sameDeviceLog,
        }).filter((row) => row.key !== "artifactId" && row.key !== "manifestVersion")
      : [],
    requestTraceLinkageRows: buildIncidentBrowserDirectTraceLinkageRows({
      labels: {
        requestId: pickAdminDashboardLocaleText(input.locale, { en: "Request ID", ko: "Request ID" }),
        correlationId: pickAdminDashboardLocaleText(input.locale, { en: "Correlation ID", ko: "Correlation ID" }),
        failedFlowLink: pickAdminDashboardLocaleText(input.locale, { en: "Failed-flow link", ko: "오류 흐름 링크" }),
      },
      source: {
        requestId: input.requestTrace?.requestId,
        correlationId: input.requestTrace?.correlationId,
        failedFlowLink: input.requestTrace?.drilldownTo,
      },
    }),
    artifactId:
      input.sameDeviceLog?.artifactId ??
      pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
    deviceInstallId:
      input.sameDeviceLog?.deviceInstallId ??
      pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
    captureTriggerLabel:
      input.sameDeviceLog?.captureTriggerLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No capture trigger", ko: "수집 트리거 없음" }),
    captureWindowLabel:
      input.sameDeviceLog?.captureWindowLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No capture window", ko: "수집 구간 없음" }),
    capturedAtBucketLabel:
      input.sameDeviceLog?.capturedAtBucketLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No capture bucket", ko: "수집 시각 버킷 없음" }),
    capturedAtOperatorValueLabel:
      input.sameDeviceLog?.capturedAtOperatorValueLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
    manifestVersionLabel:
      input.sameDeviceLog?.manifestVersionLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
    redactionVersionLabel:
      input.sameDeviceLog?.redactionVersionLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
    bundleHashPresenceLabel:
      input.sameDeviceLog?.bundleHashPresenceLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "Unknown", ko: "알 수 없음" }),
    bundleDeliverySourceLabel:
      input.sameDeviceLog?.bundleDeliverySourceLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "Issue the bundle download to classify the delivery source",
        ko: "bundle 다운로드를 발급해 전달 source를 판정하세요",
      }),
    photoArtifactId:
      input.photoArtifact?.artifactId ??
      pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
    photoSourceScenarioLabel:
      input.photoArtifact?.sourceScenarioLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No linked photo", ko: "연결된 사진 없음" }),
    photoArtifactRoleLabel:
      input.photoArtifact?.artifactRoleLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No photo role", ko: "사진 역할 없음" }),
    photoArchiveStateLabel:
      input.photoArtifact?.archiveStateLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No archive state", ko: "archive 상태 없음" }),
    photoIncidentAttachmentStateLabel:
      input.photoArtifact?.incidentAttachmentStateLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No incident attachment", ko: "incident 연결 없음" }),
    photoLinkageStateLabel: input.photoLinkageStateLabel,
    photoLinkageHint: input.photoLinkageHint,
    photoCompareReadyLabel:
      input.photoArtifact?.compareReadyLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No photo compare state", ko: "사진 비교 상태 없음" }),
    photoDeliverySourceLabel:
      input.photoArtifact?.deliverySourceLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "Issue the original download to classify the delivery source",
        ko: "원본 다운로드를 발급해 전달 source를 판정하세요",
      }),
    photoOriginalDownloadIssueLabel:
      input.photoArtifact?.originalDownloadIssueLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No photo download", ko: "사진 다운로드 없음" }),
    fallbackReasonLabel:
      input.photoArtifact?.fallbackReasonLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No photo fallback", ko: "사진 fallback 없음" }),
    reviewNextActionLabel:
      input.photoArtifact?.reviewNextActionLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "No photo next action", ko: "사진 다음 액션 없음" }),
    supportabilityStateLabel:
      input.sameDeviceLog?.supportabilityStateLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "No same-device supportability state",
        ko: "동일 기기 지원 상태 없음",
      }),
    previewReadinessLabel: `${photoPreviewReadinessLabel} / ${logPreviewReadinessLabel}`,
    previewTopCategoryLabel:
      input.sameDeviceLog?.previewTopCategoryLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "No preview category",
        ko: "미리보기 카테고리 없음",
      }),
    excerptLineCountBucketLabel:
      input.sameDeviceLog?.excerptLineCountBucketLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "No excerpt lines",
        ko: "발췌 줄 수 없음",
      }),
    previewHeadlineLabel:
      input.sameDeviceLog?.previewHeadlineLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "No preview headline",
        ko: "미리보기 헤드라인 없음",
      }),
    primaryFailureSignalLabel:
      input.sameDeviceLog?.primaryFailureSignalLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "No primary signal",
        ko: "주 실패 신호 없음",
      }),
    diagnosisSummaryLabel:
      input.sameDeviceLog?.diagnosisSummaryLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "No diagnosis summary",
        ko: "진단 요약 없음",
      }),
    diagnosisConfidenceLabel:
      input.sameDeviceLog?.diagnosisConfidenceLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "No diagnosis confidence",
        ko: "진단 확신도 없음",
      }),
    latestErrorCodeLabel:
      input.sameDeviceLog?.latestErrorCodeLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "Unavailable",
        ko: "없음",
      }),
    replayStateLabel:
      input.sameDeviceLog?.replayStateLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "Replay state unavailable",
        ko: "replay 상태 없음",
      }),
    replayNextActionLabel:
      input.sameDeviceLog?.replayNextActionLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "Open the same-device log workspace to re-check replay readiness.",
        ko: "same-device 로그 작업면을 다시 열어 replay 준비 상태를 확인하세요.",
      }),
    downloadReadinessLabel: `${photoDownloadReadinessLabel} / ${logDownloadReadinessLabel}`,
    fullBundleDownloadIssueLabel:
      input.sameDeviceLog
        ? formatIncidentHandoffDownloadIssueLabel(
            input.sameDeviceLog.downloadIssue,
            input.locale,
            input.sameDeviceLog.downloadReadinessLabel,
          )
        : pickAdminDashboardLocaleText(input.locale, { en: "No log download", ko: "로그 다운로드 없음" }),
    bundleContractSummary:
      input.sameDeviceLog?.bundleContractSummary ?? {
        bundleBaseName: pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
        manifestFilename: pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
        payloadFilename: pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
        compareSummaryFilename: pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
        payloadFormatLabel: pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
        manifestFieldRows: [],
        lineSchemaLabels: [],
      },
    deliveryPathStateLabel:
      input.sameDeviceLog?.deliveryPathStateLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "Delivery path unavailable",
        ko: "전달 경로 상태 없음",
      }),
    producerPathNextActionLabel:
      input.sameDeviceLog?.producerPathNextActionLabel ??
      pickAdminDashboardLocaleText(input.locale, {
        en: "Re-open the incident workspace to inspect the same-device delivery path.",
        ko: "incident 작업면을 다시 열어 same-device 전달 경로를 확인하세요.",
      }),
    latestRequestIdLabel:
      input.sameDeviceLog?.latestRequestIdLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
    latestCorrelationIdLabel:
      input.sameDeviceLog?.latestCorrelationIdLabel ??
      pickAdminDashboardLocaleText(input.locale, { en: "Unavailable", ko: "없음" }),
    photoLinkageNextActionLabel: input.photoLinkageNextActionLabel,
    handoffParityResultLabel: formatIncidentBrowserDirectHandoffParityResultLabel({
      locale: input.locale,
      status: input.status,
      relationLabel: input.relationLabel,
      photoLinkageStateLabel: input.photoLinkageStateLabel,
    }),
  };
}

function formatIncidentReplayStateLabel(input: {
  downloadIssue: IncidentHandoffDownloadIssue | undefined;
  locale: "ko" | "en";
  previewReadinessLabel: string;
}): string {
  if (input.downloadIssue?.state === "issued" || input.downloadIssue?.state === "issuing") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: `Replay ready with issued bundle (${input.previewReadinessLabel})`,
      ko: `발급된 bundle로 replay 준비 완료 (${input.previewReadinessLabel})`,
    });
  }

  if (input.downloadIssue?.state === "ready_to_issue") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: `Replay pending bundle issue (${input.previewReadinessLabel})`,
      ko: `bundle 발급 대기 replay (${input.previewReadinessLabel})`,
    });
  }

  return pickAdminDashboardLocaleText(input.locale, {
    en: `Replay blocked until bundle parity is restored (${input.previewReadinessLabel})`,
    ko: `bundle parity가 복구될 때까지 replay 차단 (${input.previewReadinessLabel})`,
  });
}

function buildIncidentReplayNextActionLabel(input: {
  locale: "ko" | "en";
  producerPathNextActionLabel: string;
  downloadIssue: IncidentHandoffDownloadIssue | undefined;
}): string | undefined {
  if (input.downloadIssue?.state === "issued" || input.downloadIssue?.state === "issuing") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Read the inline excerpt first, then compare the downloaded manifest against the same same-device header before handing the bundle off.",
      ko: "먼저 inline excerpt를 읽고, 번들을 넘기기 전에 다운로드한 manifest를 동일 same-device header와 비교하세요.",
    });
  }

  if (input.downloadIssue?.state === "ready_to_issue") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Issue the full bundle download from this same artifact, then confirm the manifest keeps the same same-device header and excerpt context.",
      ko: "이 동일 artifact에서 full bundle download를 먼저 발급한 뒤, manifest가 같은 same-device header와 excerpt 문맥을 유지하는지 확인하세요.",
    });
  }

  return input.producerPathNextActionLabel;
}

function formatIncidentLogBundleDeliverySourceLabel(input: {
  locale: "ko" | "en";
  accessState:
    | Pick<IncidentLogAccessState, "artifactId" | "action" | "status" | "result" | "url">
    | undefined;
  artifactId: string;
  downloadIssue: IncidentHandoffDownloadIssue | undefined;
}): string {
  const accessState = input.accessState;

  if (
    accessState &&
    accessState.artifactId === input.artifactId &&
    accessState.action === "download" &&
    accessState.status === "ready" &&
    accessState.result === "allowed"
  ) {
    const url = accessState.url ?? "";

    if (url.startsWith("https://") || url.startsWith("http://")) {
      return pickAdminDashboardLocaleText(input.locale, {
        en: "Storage-backed signed URL",
        ko: "스토리지 기반 signed URL",
      });
    }

    if (url.startsWith("data:")) {
      return pickAdminDashboardLocaleText(input.locale, {
        en: "Assembled inline bundle fallback",
        ko: "인라인 조립 bundle fallback",
      });
    }

    if (url.startsWith("blob:")) {
      return pickAdminDashboardLocaleText(input.locale, {
        en: "Browser-local blob replay",
        ko: "브라우저 로컬 blob 재생",
      });
    }

    return pickAdminDashboardLocaleText(input.locale, {
      en: "Granted bundle URL",
      ko: "발급된 bundle URL",
    });
  }

  if (accessState?.status === "loading") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Classifying bundle delivery source...",
      ko: "bundle 전달 source를 판정하는 중...",
    });
  }

  if (accessState?.status === "error") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Bundle access issuance failed before provenance check",
      ko: "provenance 확인 전에 bundle 접근 발급이 실패했습니다",
    });
  }

  if (accessState?.status === "ready" && accessState.result === "missing") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Delivery source unavailable",
      ko: "전달 source를 확인할 수 없음",
    });
  }

  if (
    accessState?.status === "ready" &&
    (accessState.result === "denied_policy" || accessState.result === "denied_role")
  ) {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Policy-blocked delivery path",
      ko: "정책으로 차단된 전달 경로",
    });
  }

  if (accessState?.status === "ready" && accessState.result === "expired") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Expired bundle grant",
      ko: "만료된 bundle 권한",
    });
  }

  if (input.downloadIssue?.state === "issued") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Issued bundle grant without classified delivery source",
      ko: "전달 source 분류 없이 발급된 bundle 권한",
    });
  }

  return pickAdminDashboardLocaleText(input.locale, {
    en: "Issue bundle download to classify delivery source",
    ko: "bundle 다운로드를 발급해 전달 source를 판정하세요",
  });
}

function formatIncidentPhotoDeliverySourceLabel(input: {
  locale: "ko" | "en";
  accessState:
    | Pick<MediaAccessState, "artifactId" | "action" | "status" | "result" | "url">
    | undefined;
  artifactId: string;
  downloadIssue: IncidentHandoffDownloadIssue | undefined;
}): string {
  const accessState = input.accessState;

  if (
    accessState &&
    accessState.artifactId === input.artifactId &&
    accessState.action === "download" &&
    accessState.status === "ready" &&
    accessState.result === "allowed"
  ) {
    const url = accessState.url ?? "";

    if (url.startsWith("https://") || url.startsWith("http://")) {
      return pickAdminDashboardLocaleText(input.locale, {
        en: "Storage-backed signed URL",
        ko: "스토리지 기반 signed URL",
      });
    }

    if (url.startsWith("data:")) {
      return pickAdminDashboardLocaleText(input.locale, {
        en: "Inline binary fallback",
        ko: "인라인 binary fallback",
      });
    }

    if (url.startsWith("blob:")) {
      return pickAdminDashboardLocaleText(input.locale, {
        en: "Browser-local blob replay",
        ko: "브라우저 로컬 blob 재생",
      });
    }

    return pickAdminDashboardLocaleText(input.locale, {
      en: "Granted artifact URL",
      ko: "발급된 artifact URL",
    });
  }

  if (accessState?.status === "loading") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Classifying photo delivery source...",
      ko: "사진 전달 source를 판정하는 중...",
    });
  }

  if (accessState?.status === "error") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Photo access issuance failed before provenance check",
      ko: "provenance 확인 전에 사진 접근 발급이 실패했습니다",
    });
  }

  if (accessState?.status === "ready" && accessState.result === "missing") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Delivery source unavailable",
      ko: "전달 source를 확인할 수 없음",
    });
  }

  if (
    accessState?.status === "ready" &&
    (accessState.result === "denied_policy" || accessState.result === "denied_role")
  ) {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Policy-blocked delivery path",
      ko: "정책으로 차단된 전달 경로",
    });
  }

  if (accessState?.status === "ready" && accessState.result === "expired") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Expired original grant",
      ko: "만료된 원본 권한",
    });
  }

  if (input.downloadIssue?.state === "issued") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Issued original grant without classified delivery source",
      ko: "전달 source 분류 없이 발급된 원본 권한",
    });
  }

  return pickAdminDashboardLocaleText(input.locale, {
    en: "Issue original download to classify delivery source",
    ko: "원본 다운로드를 발급해 전달 source를 판정하세요",
  });
}

function formatIncidentPhotoCompareReadyLabel(input: {
  downloadIssue: IncidentHandoffDownloadIssue | undefined;
  locale: "ko" | "en";
  downloadAvailabilityLabel: string;
}): string {
  if (input.downloadIssue?.state === "issued" || input.downloadIssue?.state === "issuing") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: `Original-download parity ready (${input.downloadAvailabilityLabel})`,
      ko: `원본 다운로드 parity 준비 완료 (${input.downloadAvailabilityLabel})`,
    });
  }

  if (input.downloadIssue?.state === "ready_to_issue") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: `Original-download parity pending grant (${input.downloadAvailabilityLabel})`,
      ko: `원본 다운로드 parity 발급 대기 (${input.downloadAvailabilityLabel})`,
    });
  }

  return pickAdminDashboardLocaleText(input.locale, {
    en: `Original-download parity blocked (${input.downloadAvailabilityLabel})`,
    ko: `원본 다운로드 parity 차단 (${input.downloadAvailabilityLabel})`,
  });
}

function buildIncidentPhotoCompareNextActionLabel(input: {
  locale: "ko" | "en";
  downloadIssue: IncidentHandoffDownloadIssue | undefined;
  fallbackReasonLabel: string;
  binaryReviewHint: string;
}): string | undefined {
  if (input.downloadIssue?.state === "issued" || input.downloadIssue?.state === "issuing") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Open the granted original from this same artifact, then compare it against the representative entry, selected row, and latest trace before leaving this user route.",
      ko: "이 동일 artifact에서 발급된 원본을 열고, 이 사용자 경로를 떠나기 전에 대표 항목, 선택 row, 최근 추적과 함께 비교하세요.",
    });
  }

  if (input.downloadIssue?.state === "ready_to_issue") {
    return pickAdminDashboardLocaleText(input.locale, {
      en: "Issue the original download from this selected artifact first, then compare the opened binary against the representative entry and current trace linkage.",
      ko: "이 선택 artifact에서 원본 다운로드를 먼저 발급한 뒤, 열린 binary를 대표 항목과 현재 추적 연결에 맞춰 비교하세요.",
    });
  }

  return input.binaryReviewHint || input.fallbackReasonLabel;
}

export function buildIncidentPhotoCompareSummary(input: {
  locale: "ko" | "en";
  selectedArtifact:
    | Pick<
        IncidentEvidenceSelectedArtifact,
        | "artifactId"
        | "sourceScenarioLabel"
        | "relationTypeLabel"
        | "artifactRoleLabel"
        | "contentTypeBucketLabel"
        | "sizeBucketLabel"
        | "uploadedAtLabel"
        | "previewAvailability"
        | "downloadAvailability"
        | "previewAvailabilityLabel"
        | "downloadAvailabilityLabel"
        | "binaryReviewPathLabel"
        | "binaryReviewHint"
        | "archiveStateLabel"
        | "incidentAttachmentStateLabel"
        | "fallbackReasonLabel"
        | "latestRequestIdLabel"
        | "latestCorrelationIdLabel"
      >
    | undefined;
  accessState: Pick<
    MediaAccessState,
    "artifactId" | "action" | "status" | "requestId" | "issuedAt" | "expiresAt" | "result" | "url"
  >;
}): IncidentPhotoCompareSummary | undefined {
  if (!input.selectedArtifact) {
    return undefined;
  }

  const downloadIssue = resolveIncidentHandoffDownloadIssue({
    accessState: input.accessState,
    artifactId: input.selectedArtifact.artifactId,
    action: "download",
    canIssue: input.selectedArtifact.downloadAvailability === "available",
  });
  const deliverySourceLabel = formatIncidentPhotoDeliverySourceLabel({
    locale: input.locale,
    accessState: input.accessState,
    artifactId: input.selectedArtifact.artifactId,
    downloadIssue,
  });

  return {
    parityRows: buildIncidentPhotoCompareRows({
      labels: {
        artifactId: pickAdminDashboardLocaleText(input.locale, { en: "Artifact ID", ko: "Artifact ID" }),
        sourceScenario: pickAdminDashboardLocaleText(input.locale, { en: "Source", ko: "Source" }),
        relationType: pickAdminDashboardLocaleText(input.locale, { en: "Relation", ko: "Relation" }),
        artifactRole: pickAdminDashboardLocaleText(input.locale, { en: "Role", ko: "역할" }),
        contentType: pickAdminDashboardLocaleText(input.locale, { en: "Content Type", ko: "콘텐츠 유형" }),
        sizeBucket: pickAdminDashboardLocaleText(input.locale, { en: "Size", ko: "크기" }),
        uploadedAt: pickAdminDashboardLocaleText(input.locale, { en: "Created", ko: "생성 시각" }),
        previewAvailability: pickAdminDashboardLocaleText(input.locale, { en: "Preview", ko: "미리보기" }),
        downloadAvailability: pickAdminDashboardLocaleText(input.locale, { en: "Download", ko: "다운로드" }),
        binaryReviewPath: pickAdminDashboardLocaleText(input.locale, {
          en: "Binary review path",
          ko: "binary review 경로",
        }),
        archiveState: pickAdminDashboardLocaleText(input.locale, {
          en: "Archive state",
          ko: "archive 상태",
        }),
        incidentAttachment: pickAdminDashboardLocaleText(input.locale, {
          en: "Incident attachment",
          ko: "incident 연결",
        }),
        latestTrace: pickAdminDashboardLocaleText(input.locale, { en: "Latest trace", ko: "최근 추적" }),
      },
      source: {
        artifactId: input.selectedArtifact.artifactId,
        sourceScenarioLabel: input.selectedArtifact.sourceScenarioLabel,
        relationTypeLabel: input.selectedArtifact.relationTypeLabel,
        artifactRoleLabel: input.selectedArtifact.artifactRoleLabel,
        contentTypeBucketLabel: input.selectedArtifact.contentTypeBucketLabel,
        sizeBucketLabel: input.selectedArtifact.sizeBucketLabel,
        uploadedAtLabel: input.selectedArtifact.uploadedAtLabel,
        previewAvailabilityLabel: input.selectedArtifact.previewAvailabilityLabel,
        downloadAvailabilityLabel: input.selectedArtifact.downloadAvailabilityLabel,
        binaryReviewPathLabel: input.selectedArtifact.binaryReviewPathLabel,
        archiveStateLabel: input.selectedArtifact.archiveStateLabel,
        incidentAttachmentStateLabel: input.selectedArtifact.incidentAttachmentStateLabel,
        latestTraceLabel: `${input.selectedArtifact.latestRequestIdLabel} / ${input.selectedArtifact.latestCorrelationIdLabel}`,
      },
    }),
    accessIssuanceRows: buildIncidentPhotoAccessIssuanceRows({
      labels: {
        accessResult: pickAdminDashboardLocaleText(input.locale, {
          en: "Download issuance",
          ko: "다운로드 발급 상태",
        }),
        requestId: pickAdminDashboardLocaleText(input.locale, { en: "Access request", ko: "접근 요청" }),
        issuedAt: pickAdminDashboardLocaleText(input.locale, { en: "Issued at", ko: "발급 시각" }),
        expiresAt: pickAdminDashboardLocaleText(input.locale, { en: "Expires at", ko: "만료 시각" }),
      },
      source: input.accessState,
      locale: input.locale,
    }),
    compareReadyLabel: formatIncidentPhotoCompareReadyLabel({
      locale: input.locale,
      downloadIssue,
      downloadAvailabilityLabel: input.selectedArtifact.downloadAvailabilityLabel,
    }),
    compareNextActionLabel: buildIncidentPhotoCompareNextActionLabel({
      locale: input.locale,
      downloadIssue,
      fallbackReasonLabel: input.selectedArtifact.fallbackReasonLabel,
      binaryReviewHint: input.selectedArtifact.binaryReviewHint,
    }),
    deliverySourceLabel,
    originalDownloadIssueLabel: formatIncidentHandoffDownloadIssueLabel(
      downloadIssue,
      input.locale,
      input.selectedArtifact.downloadAvailabilityLabel,
    ),
  };
}

export function buildIncidentManifestCompareSummary(input: {
  locale: "ko" | "en";
  selectedIncidentArtifact: IncidentEvidenceIncidentLog | undefined;
  accessState: Pick<
    IncidentLogAccessState,
    "artifactId" | "action" | "status" | "requestId" | "issuedAt" | "expiresAt" | "result" | "url"
  >;
}): IncidentManifestCompareSummary | undefined {
  if (!input.selectedIncidentArtifact) {
    return undefined;
  }

  const downloadIssue = resolveIncidentHandoffDownloadIssue({
    accessState: input.accessState,
    artifactId: input.selectedIncidentArtifact.artifactId,
    action: "download",
    canIssue: input.selectedIncidentArtifact.downloadReadiness === "ready",
  });
  const deliverySourceLabel = formatIncidentLogBundleDeliverySourceLabel({
    locale: input.locale,
    accessState: input.accessState,
    artifactId: input.selectedIncidentArtifact.artifactId,
    downloadIssue,
  });

  return {
    parityRows: [
      ...buildIncidentSameDeviceHeaderRows({
        labels: {
          artifactId: pickAdminDashboardLocaleText(input.locale, { en: "Artifact ID", ko: "Artifact ID" }),
          deviceInstallId: pickAdminDashboardLocaleText(input.locale, { en: "Device install", ko: "기기 설치본" }),
          release: pickAdminDashboardLocaleText(input.locale, { en: "Release", ko: "릴리스" }),
          appVersion: pickAdminDashboardLocaleText(input.locale, { en: "App version", ko: "앱 버전" }),
          platform: pickAdminDashboardLocaleText(input.locale, { en: "Platform", ko: "플랫폼" }),
          runtimeSummary: pickAdminDashboardLocaleText(input.locale, { en: "Runtime summary", ko: "런타임 요약" }),
          capturedAt: pickAdminDashboardLocaleText(input.locale, { en: "Captured at", ko: "수집 시각" }),
          captureTrigger: pickAdminDashboardLocaleText(input.locale, { en: "Capture trigger", ko: "수집 트리거" }),
          captureWindow: pickAdminDashboardLocaleText(input.locale, { en: "Capture window", ko: "수집 구간" }),
          bundleHash: pickAdminDashboardLocaleText(input.locale, { en: "Bundle hash", ko: "Bundle hash" }),
          manifestVersion: pickAdminDashboardLocaleText(input.locale, { en: "Manifest", ko: "Manifest" }),
        },
        source: input.selectedIncidentArtifact,
      }),
      {
        key: "redactionVersion",
        label: pickAdminDashboardLocaleText(input.locale, { en: "Redaction", ko: "Redaction" }),
        value: input.selectedIncidentArtifact.redactionVersionLabel,
      },
      {
        key: "deliverySource",
        label: pickAdminDashboardLocaleText(input.locale, { en: "Delivery source", ko: "전달 source" }),
        value: deliverySourceLabel,
      },
    ],
    compareReadyLabel: formatIncidentReplayStateLabel({
      downloadIssue,
      locale: input.locale,
      previewReadinessLabel: input.selectedIncidentArtifact.previewReadinessLabel,
    }),
    compareNextActionLabel: buildIncidentReplayNextActionLabel({
      locale: input.locale,
      producerPathNextActionLabel: input.selectedIncidentArtifact.producerPathNextActionLabel,
      downloadIssue,
    }),
    deliverySourceLabel,
  };
}

export function resolvePrimaryPhotoWorkspaceArtifact(input: {
  representativePhoto?: PrimaryPhotoWorkspaceArtifact;
  selectedArtifact?: PrimaryPhotoWorkspaceArtifact;
  requestedFocusActive: boolean;
}): PrimaryPhotoWorkspaceArtifact | undefined {
  if (input.requestedFocusActive && input.selectedArtifact) {
    return input.selectedArtifact;
  }

  return input.representativePhoto ?? input.selectedArtifact;
}

function buildIncidentHandoffOperatorNote(
  sameDeviceLog: UserDetailIncidentHandoffPackagePreview["sameDeviceLog"],
  locale: "ko" | "en",
): string | undefined {
  if (!sameDeviceLog) {
    return undefined;
  }

  return pickAdminDashboardLocaleText(locale, {
    en: `${sameDeviceLog.previewHeadlineLabel}. Primary signal: ${sameDeviceLog.primaryFailureSignalLabel}. ${sameDeviceLog.recommendedActionLabel}`,
    ko: `${sameDeviceLog.previewHeadlineLabel}. 주요 신호: ${sameDeviceLog.primaryFailureSignalLabel}. ${sameDeviceLog.recommendedActionLabel}`,
  });
}

function formatIncidentHandoffStatusLabel(
  status: UserDetailIncidentHandoffPackagePreview["status"],
  locale: "ko" | "en",
): string {
  switch (status) {
    case "ready":
      return pickAdminDashboardLocaleText(locale, {
        en: "Photo and log evidence stay in one incident handoff package.",
        ko: "사진과 로그 증거가 하나의 incident handoff 패키지에 함께 유지됩니다.",
      });
    case "needs_trace_join":
      return pickAdminDashboardLocaleText(locale, {
        en: "Trace-join the selected photo and same-device log before handing this incident to engineering.",
        ko: "이 incident를 개발자에게 넘기기 전에 선택된 사진과 same-device 로그를 같은 추적으로 먼저 정렬하세요.",
      });
    case "needs_photo":
      return pickAdminDashboardLocaleText(locale, {
        en: "Pin a photo artifact so the same-device log can be handed off with actual user-uploaded evidence.",
        ko: "same-device 로그를 실제 사용자 업로드 증거와 함께 넘길 수 있도록 사진 artifact를 먼저 고정하세요.",
      });
    case "needs_log":
      return pickAdminDashboardLocaleText(locale, {
        en: "Pin a same-device log artifact so photo evidence does not leave this route without device context.",
        ko: "사진 증거가 기기 문맥 없이 이 화면을 벗어나지 않도록 same-device 로그 artifact를 먼저 고정하세요.",
      });
    case "empty":
      return pickAdminDashboardLocaleText(locale, {
        en: "No photo or same-device log artifact is pinned yet.",
        ko: "아직 고정된 사진 또는 same-device 로그 artifact가 없습니다.",
      });
  }
}

function formatIncidentHandoffNextActionLabel(input: {
  locale: "ko" | "en";
  preview: UserDetailIncidentHandoffPackagePreview;
}): string {
  if (input.preview.photoLinkageState === "contextual_link_only") {
    return input.preview.photoLinkageNextActionLabel;
  }

  if (input.preview.status !== "ready" && input.preview.photoLinkageNextActionLabel) {
    return input.preview.photoLinkageNextActionLabel;
  }

  if (input.preview.status === "ready") {
    return (
      input.preview.sameDeviceLog?.recommendedActionLabel ??
      input.preview.photoLinkageNextActionLabel ??
      formatIncidentHandoffStatusLabel("ready", input.locale)
    );
  }

  return formatIncidentHandoffStatusLabel(input.preview.status, input.locale);
}

function formatPhotoLinkedIncidentStateLabel(
  state: UserDetailIncidentEvidenceWorkspace["photoLinkageState"],
  locale: "ko" | "en",
): string {
  switch (state) {
    case "same_device_aligned":
      return pickAdminDashboardLocaleText(locale, {
        en: "Same-device aligned",
        ko: "동일 기기 직접 정렬",
      });
    case "contextual_link_only":
      return pickAdminDashboardLocaleText(locale, {
        en: "Contextual link only",
        ko: "문맥 연결만 존재",
      });
    case "photo_only_pending_log":
      return pickAdminDashboardLocaleText(locale, {
        en: "Photo pinned, log missing",
        ko: "사진 고정됨, 로그 없음",
      });
    case "log_only_missing_photo":
      return pickAdminDashboardLocaleText(locale, {
        en: "Log pinned, photo missing",
        ko: "로그 고정됨, 사진 없음",
      });
    case "empty":
      return pickAdminDashboardLocaleText(locale, {
        en: "No linked incident evidence",
        ko: "연결된 incident 증거 없음",
      });
  }
}

function formatPhotoLinkedIncidentNextAction(
  workspace: UserDetailIncidentEvidenceWorkspace,
  selectedArtifact: IncidentEvidenceSelectedArtifact | undefined,
  locale: "ko" | "en",
): string {
  switch (workspace.photoLinkageState) {
    case "same_device_aligned":
      return pickAdminDashboardLocaleText(locale, {
        en: "Keep the photo artifact and same-device log pinned together, then compare excerpt, failed flow, and download parity in one pass.",
        ko: "사진 artifact와 same-device 로그를 함께 고정한 채 excerpt, 오류 흐름, 다운로드 parity를 한 번에 비교하세요.",
      });
    case "contextual_link_only":
      return pickAdminDashboardLocaleText(locale, {
        en: "Keep the contextual relation label visible and reopen the failed flow before treating this pair as a direct same-device replay.",
        ko: "이 조합을 직접 same-device replay로 보지 말고 관계 라벨을 유지한 채 오류 흐름을 먼저 다시 여세요.",
      });
    case "photo_only_pending_log":
      return pickAdminDashboardLocaleText(locale, {
        en: "Keep the photo pinned and locate the same-device log artifact before promising a replay-ready incident package.",
        ko: "replay-ready incident 패키지를 약속하기 전에 사진을 고정한 채 same-device 로그 artifact를 먼저 찾으세요.",
      });
    case "log_only_missing_photo":
      return pickAdminDashboardLocaleText(locale, {
        en: "Pin the uploaded photo artifact that belongs to this failed flow so the same-device log does not leave this route without user evidence.",
        ko: "same-device 로그가 사용자 증거 없이 이 화면을 벗어나지 않도록 이 오류 흐름에 속한 업로드 사진 artifact를 먼저 고정하세요.",
      });
    case "empty":
      return selectedArtifact?.incidentAttachmentHint ??
        pickAdminDashboardLocaleText(locale, {
          en: "Start from the failed flow or source trace before building a photo-linked incident package.",
          ko: "사진 연결 incident 패키지를 만들기 전에 오류 흐름이나 source trace부터 시작하세요.",
        });
  }
}

function renderMediaAccessFeedbackBlock(input: {
  artifactId: string | undefined;
  copy: {
    artifactId: string;
    request: string;
    expiresAt: string;
    issuedAt: string;
    accessAction: string;
    accessResult: string;
    preview: string;
    download: string;
    selectedArtifactBody: string;
    accessUnsupported: string;
  };
  mediaAccessFeedback: string | undefined;
  mediaAccessState: MediaAccessState;
}): JSX.Element {
  const { artifactId, copy, mediaAccessFeedback, mediaAccessState } = input;
  const isActiveArtifact = artifactId !== undefined && mediaAccessState.artifactId === artifactId;

  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: "16px",
        background: "rgba(248, 250, 252, 0.92)",
        border: "1px solid rgba(148, 163, 184, 0.2)",
        display: "grid",
        gap: "6px",
      }}
    >
      <strong>{copy.accessResult}</strong>
      <span style={{ color: "#475569" }}>
        {isActiveArtifact ? mediaAccessFeedback ?? copy.accessUnsupported : copy.selectedArtifactBody}
      </span>
      {isActiveArtifact ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.artifactId}: {mediaAccessState.artifactId}
        </span>
      ) : null}
      {isActiveArtifact && mediaAccessState.action ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.accessAction}: {mediaAccessState.action === "preview" ? copy.preview : copy.download}
        </span>
      ) : null}
      {isActiveArtifact && mediaAccessState.requestId ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.request}: {mediaAccessState.requestId}
        </span>
      ) : null}
      {isActiveArtifact && mediaAccessState.issuedAt ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.issuedAt}: {mediaAccessState.issuedAt}
        </span>
      ) : null}
      {isActiveArtifact && mediaAccessState.expiresAt ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.expiresAt}: {mediaAccessState.expiresAt}
        </span>
      ) : null}
    </div>
  );
}

function renderIncidentLogAccessFeedbackBlock(input: {
  artifactId: string | undefined;
  copy: {
    artifactId: string;
    request: string;
    expiresAt: string;
    issuedAt: string;
    accessAction: string;
    accessResult: string;
    deliverySource: string;
    download: string;
    selectedArtifactBody: string;
    accessUnsupported: string;
  };
  incidentAccessFeedback: string | undefined;
  incidentAccessState: IncidentLogAccessState;
  bundleDeliverySourceLabel?: string;
}): JSX.Element {
  const { artifactId, bundleDeliverySourceLabel, copy, incidentAccessFeedback, incidentAccessState } = input;
  const isActiveArtifact = artifactId !== undefined && incidentAccessState.artifactId === artifactId;

  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: "16px",
        background: "rgba(248, 250, 252, 0.92)",
        border: "1px solid rgba(148, 163, 184, 0.2)",
        display: "grid",
        gap: "6px",
      }}
    >
      <strong>{copy.accessResult}</strong>
      <span style={{ color: "#475569" }}>
        {isActiveArtifact
          ? incidentAccessFeedback ?? copy.accessUnsupported
          : copy.selectedArtifactBody}
      </span>
      {isActiveArtifact ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.artifactId}: {incidentAccessState.artifactId}
        </span>
      ) : null}
      {isActiveArtifact && incidentAccessState.action ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.accessAction}: {copy.download}
        </span>
      ) : null}
      {isActiveArtifact && incidentAccessState.requestId ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.request}: {incidentAccessState.requestId}
        </span>
      ) : null}
      {isActiveArtifact && bundleDeliverySourceLabel ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.deliverySource}: {bundleDeliverySourceLabel}
        </span>
      ) : null}
      {isActiveArtifact && incidentAccessState.issuedAt ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.issuedAt}: {incidentAccessState.issuedAt}
        </span>
      ) : null}
      {isActiveArtifact && incidentAccessState.expiresAt ? (
        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
          {copy.expiresAt}: {incidentAccessState.expiresAt}
        </span>
      ) : null}
    </div>
  );
}

export function UserDataDetailScreen(props: UserDataDetailScreenProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const params = useParams<{ internalUserId: string }>();
  const [searchParams] = useSearchParams();
  const lookup = useMemo(
    () => ({
      internalUserId: params.internalUserId,
    }),
    [params.internalUserId],
  );
  const screenModelDependencies = useMemo(
    () => ({
      ...props.dependencies,
      locale,
      lookup,
    }),
    [props.dependencies, locale, lookup],
  );
  const state = useUserDataDetailScreenModel(screenModelDependencies);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | undefined>(undefined);
  const [selectedIncidentArtifactId, setSelectedIncidentArtifactId] = useState<string | undefined>(undefined);
  const incidentWorkspaceSectionRef = useRef<HTMLElement | null>(null);
  const [selectedScenarioKey, setSelectedScenarioKey] = useState<
    | "all"
    | "avatar"
    | "pet_profile"
    | "daily_record"
    | "receipt"
    | "health_record"
    | "community_post"
    | "unknown"
  >("all");
  const [mediaAccessState, setMediaAccessState] = useState<MediaAccessState>({ status: "idle" });
  const [incidentAccessState, setIncidentAccessState] = useState<IncidentLogAccessState>({
    status: "idle",
  });
  const requestedMediaFocus = useMemo(() => readUserMediaWorkspaceFocus(searchParams), [searchParams]);
  const requestedIncidentFocus = useMemo(
    () => readUserIncidentWorkspaceFocus(searchParams),
    [searchParams],
  );
  const requestedSameDeviceContext = useMemo(
    () => readUserSameDeviceWorkspaceContext(searchParams),
    [searchParams],
  );
  const requestedRequestId = searchParams.get("requestId") ?? undefined;
  const requestedCorrelationId = searchParams.get("correlationId") ?? undefined;
  const requestedPhotoWorkspaceFocusActive = Boolean(
    requestedMediaFocus?.artifactId ||
      requestedMediaFocus?.fileId ||
      requestedMediaFocus?.scenarioKey ||
      requestedRequestId ||
      requestedCorrelationId ||
      requestedIncidentFocus?.artifactId,
  );
  const appliedRequestedSelectionKeyRef = useRef<string>("");
  const appliedRequestedIncidentArtifactIdRef = useRef<string>("");
  const filteredMediaArtifacts = useMemo(() => {
    if (!(state.status === "ready" || state.status === "partial" || state.status === "stale")) {
      return [];
    }

    return selectedScenarioKey === "all"
      ? state.viewModel.mediaArtifacts
      : state.viewModel.mediaArtifacts.filter((artifact) => artifact.sourceScenarioKey === selectedScenarioKey);
  }, [selectedScenarioKey, state]);
  const filteredMediaTimelineRows = useMemo(() => {
    if (!(state.status === "ready" || state.status === "partial" || state.status === "stale")) {
      return [];
    }

    return selectedScenarioKey === "all"
      ? state.viewModel.mediaTimelineRows
      : state.viewModel.mediaTimelineRows.filter((row) => row.sourceScenarioKey === selectedScenarioKey);
  }, [selectedScenarioKey, state]);
  const selectedArtifact = useMemo(() => {
    if (!(state.status === "ready" || state.status === "partial" || state.status === "stale")) {
      return undefined;
    }

    const fallbackArtifactId =
      filteredMediaArtifacts.find((artifact) => artifact.artifactId === state.viewModel.representativePhoto?.artifactId)?.artifactId
      ?? filteredMediaArtifacts[0]?.artifactId;
    const resolvedArtifactId = selectedArtifactId ?? fallbackArtifactId;

    if (!resolvedArtifactId) {
      return undefined;
    }

    return filteredMediaArtifacts.find((artifact) => artifact.artifactId === resolvedArtifactId);
  }, [filteredMediaArtifacts, selectedArtifactId, state]);
  const selectedScenarioPivot = useMemo(() => {
    if (
      !(state.status === "ready" || state.status === "partial" || state.status === "stale") ||
      !selectedArtifact
    ) {
      return undefined;
    }

    return state.viewModel.mediaScenarioPivots.find((pivot) => pivot.scenarioKey === selectedArtifact.sourceScenarioKey);
  }, [selectedArtifact, state]);
  const selectedIncidentArtifact = useMemo(() => {
    if (!(state.status === "ready" || state.status === "partial" || state.status === "stale")) {
      return undefined;
    }

    const fallbackArtifactId = pickRecommendedIncidentLogArtifactId(
      selectedArtifact,
      state.viewModel.incidentLogArtifacts,
      requestedSameDeviceContext,
    );
    const resolvedArtifactId = selectedIncidentArtifactId ?? fallbackArtifactId;

    if (!resolvedArtifactId) {
      return undefined;
    }

    return state.viewModel.incidentLogArtifacts.find(
      (artifact) => artifact.artifactId === resolvedArtifactId,
    );
  }, [selectedIncidentArtifactId, state]);
  const recommendedIncidentArtifactId = useMemo(() => {
    if (!(state.status === "ready" || state.status === "partial" || state.status === "stale")) {
      return undefined;
    }

    return pickRecommendedIncidentLogArtifactId(
      selectedArtifact,
      state.viewModel.incidentLogArtifacts,
      requestedSameDeviceContext,
    );
  }, [requestedSameDeviceContext, selectedArtifact, state]);
  const incidentWorkspacePhotoArtifact =
    (state.status === "ready" || state.status === "partial" || state.status === "stale")
      ? selectedArtifact ?? state.viewModel.representativePhoto
      : undefined;
  const incidentEvidenceWorkspace = useMemo(
    () =>
      resolveUserDetailIncidentEvidenceWorkspace({
        selectedArtifact: incidentWorkspacePhotoArtifact,
        selectedIncidentLog: selectedIncidentArtifact,
      }),
    [incidentWorkspacePhotoArtifact, selectedIncidentArtifact],
  );
  const incidentHandoffPackagePreview = useMemo(
    () =>
      buildUserDetailIncidentHandoffPackagePreview({
        selectedArtifact: incidentWorkspacePhotoArtifact,
        incidentEvidenceWorkspace,
        incidentAccessState,
        locale,
        mediaAccessState,
      }),
    [incidentAccessState, incidentEvidenceWorkspace, incidentWorkspacePhotoArtifact, locale, mediaAccessState],
  );
  const selectedArtifactPhotoCompareSummary = useMemo(
    () =>
      buildIncidentPhotoCompareSummary({
        locale,
        selectedArtifact,
        accessState: mediaAccessState,
      }),
    [locale, mediaAccessState, selectedArtifact],
  );
  const representativePhotoCompareSummary = useMemo(
    () =>
      buildIncidentPhotoCompareSummary({
        locale,
        selectedArtifact:
          state.status === "ready" || state.status === "partial" || state.status === "stale"
            ? state.viewModel.representativePhoto
            : undefined,
        accessState: mediaAccessState,
      }),
    [locale, mediaAccessState, state],
  );
  const primaryPhotoWorkspaceArtifact = useMemo(
    () =>
      resolvePrimaryPhotoWorkspaceArtifact({
        representativePhoto:
          state.status === "ready" || state.status === "partial" || state.status === "stale"
            ? state.viewModel.representativePhoto
            : undefined,
        selectedArtifact,
        requestedFocusActive: requestedPhotoWorkspaceFocusActive,
      }),
    [requestedPhotoWorkspaceFocusActive, selectedArtifact, state],
  );
  const primaryPhotoCompareSummary =
    requestedPhotoWorkspaceFocusActive && selectedArtifact
      ? selectedArtifactPhotoCompareSummary
      : representativePhotoCompareSummary;
  const primaryPhotoPreviewAvailability = primaryPhotoWorkspaceArtifact?.previewAvailability ?? "blocked";
  const primaryPhotoDownloadAvailability = primaryPhotoWorkspaceArtifact?.downloadAvailability ?? "blocked";
  const incidentManifestCompareSummary = useMemo(
    () =>
      buildIncidentManifestCompareSummary({
        locale,
        selectedIncidentArtifact,
        accessState: incidentAccessState,
      }),
    [incidentAccessState, locale, selectedIncidentArtifact],
  );
  const incidentHandoffPackageExport = useMemo(
    () => {
      const reopenRoute = buildUserDetailReopenRoute({
        internalUserId: params.internalUserId,
        locale,
        params: searchParams,
      });
      const route = reopenRoute;

      return buildUserDetailIncidentHandoffPackageExport({
        exportedAt: new Date().toISOString(),
        handoffPackagePreview: incidentHandoffPackagePreview,
        internalUserId: params.internalUserId,
        route,
        reopenRoute,
        locale,
        photoSourceSurfaceMatrix:
          state.status === "ready" || state.status === "partial" || state.status === "stale"
            ? state.viewModel.photoSourceSurfaceMatrix
            : [],
        missingPhotoSourceDisclosures:
          state.status === "ready" || state.status === "partial" || state.status === "stale"
            ? state.viewModel.missingPhotoSourceDisclosures
            : [],
        browserDirectContext: {
          host:
            typeof window !== "undefined" && typeof window.location?.origin === "string"
              ? window.location.origin
              : props.dependencies.runtimeConfig.apiBaseUrl,
          route,
          reopenRoute,
          release: props.dependencies.runtimeConfig.release ?? "dev-local",
          bundleFingerprint: props.dependencies.runtimeConfig.bundleFingerprint,
          accountEmail:
            state.status === "ready" || state.status === "partial" || state.status === "stale"
              ? state.viewModel.account.find((item) => item.key === "accountEmail")?.value ?? "-"
              : "-",
          accountIdentityLabel:
            state.status === "ready" || state.status === "partial" || state.status === "stale"
              ? state.viewModel.accountIdentityLabel
              : "-",
          providerIdentityLabel:
            state.status === "ready" || state.status === "partial" || state.status === "stale"
              ? state.viewModel.providerIdentityLabel
              : "-",
          linkedProviderCountLabel:
            state.status === "ready" || state.status === "partial" || state.status === "stale"
              ? state.viewModel.linkedProviderCountLabel
              : "-",
          representativeArtifactId:
            state.status === "ready" || state.status === "partial" || state.status === "stale"
              ? state.viewModel.representativePhoto?.artifactId
              : undefined,
        },
      });
    },
    [incidentHandoffPackagePreview, locale, params.internalUserId, props.dependencies.runtimeConfig, searchParams, state],
  );
  const incidentHandoffExportFilename = useMemo(() => {
    const primaryIdentity =
      incidentHandoffPackagePreview.requestTrace?.requestId ??
      incidentHandoffPackagePreview.sameDeviceLog?.artifactId ??
      incidentHandoffPackagePreview.photoArtifact?.artifactId ??
      "workspace";
    return `petlog-incident-handoff-${primaryIdentity}.json`;
  }, [incidentHandoffPackagePreview]);
  const uploadedPhotoReviewExportFilename = useMemo(() => {
    const primaryIdentity =
      selectedArtifact?.artifactId ??
      ((state.status === "ready" || state.status === "partial" || state.status === "stale")
        ? state.viewModel.representativePhoto?.artifactId
        : undefined) ??
      "photo-review";
    return `petlog-uploaded-photo-review-${primaryIdentity}.json`;
  }, [selectedArtifact, state]);
  const representativeWorkspaceTo = useMemo(() => {
    if (
      !(state.status === "ready" || state.status === "partial" || state.status === "stale") ||
      !primaryPhotoWorkspaceArtifact ||
      !lookup.internalUserId
    ) {
      return undefined;
    }

    const params = appendUserMediaWorkspaceFocus(searchParams, {
      artifactId: primaryPhotoWorkspaceArtifact.artifactId,
      scenarioKey: primaryPhotoWorkspaceArtifact.sourceScenarioKey as UserMediaWorkspaceScenarioKey,
    });

    return `/admin/users/${lookup.internalUserId}?${params.toString()}`;
  }, [lookup.internalUserId, primaryPhotoWorkspaceArtifact, searchParams, state]);
  const selectedArtifactMatchesRepresentative =
    (state.status === "ready" || state.status === "partial" || state.status === "stale") &&
    !!selectedArtifact &&
    selectedArtifact.artifactId === state.viewModel.representativePhoto?.artifactId;
  const representativeSelectionParityLabel =
    !(state.status === "ready" || state.status === "partial" || state.status === "stale") ||
    !state.viewModel.representativePhoto
      ? undefined
      : !selectedArtifact || selectedArtifact.artifactId === state.viewModel.representativePhoto.artifactId
        ? pickAdminDashboardLocaleText(locale, {
            en: "Representative is the active workspace artifact",
            ko: "대표 사진이 현재 작업면 artifact입니다",
          })
        : pickAdminDashboardLocaleText(locale, {
            en: `Workspace is pinned to ${selectedArtifact.artifactId}; compare it against this representative entry before leaving the user route.`,
            ko: `작업면이 ${selectedArtifact.artifactId}에 고정되어 있습니다. 사용자 경로를 떠나기 전에 이 대표 항목과 비교하세요.`,
          });
  const representativeCompareNextActionLabel =
    requestedPhotoWorkspaceFocusActive && selectedArtifact
      ? selectedArtifactPhotoCompareSummary?.compareNextActionLabel
      : !(state.status === "ready" || state.status === "partial" || state.status === "stale") ||
        !state.viewModel.representativePhoto
      ? undefined
      : selectedArtifact && selectedArtifact.artifactId !== state.viewModel.representativePhoto.artifactId
        ? pickAdminDashboardLocaleText(locale, {
            en: "Keep this representative entry as the baseline, then reopen the pinned corpus row and compare original-download support and latest trace on the same user route.",
            ko: "이 대표 항목을 기준선으로 유지한 뒤, 고정된 코퍼스 row를 다시 열어 같은 사용자 경로에서 원본 다운로드 지원과 최신 추적을 함께 비교하세요.",
          })
        : representativePhotoCompareSummary?.compareNextActionLabel;
  const mediaReviewPackageRoute = useMemo(
    () =>
      buildUserDetailReopenRoute({
        internalUserId: params.internalUserId,
        locale,
        params: searchParams,
      }),
    [locale, params.internalUserId, searchParams],
  );
  const uploadedPhotoReviewPackageExport = useMemo(
    () =>
      buildUploadedPhotoReviewPackageExport({
        exportedAt: new Date().toISOString(),
        route: mediaReviewPackageRoute,
        reopenRoute: buildUserDetailReopenRoute({
          internalUserId: params.internalUserId,
          locale,
          params: searchParams,
          incidentArtifactIdOverride: selectedIncidentArtifact?.artifactId ?? recommendedIncidentArtifactId,
        }),
        internalUserId: params.internalUserId,
        representativeArtifactId:
          state.status === "ready" || state.status === "partial" || state.status === "stale"
            ? state.viewModel.representativePhoto?.artifactId
            : undefined,
        selectedArtifact,
        selectedArtifactPhotoCompareSummary,
        selectedScenarioSummary: selectedScenarioPivot
          ? {
              scenarioKey: selectedScenarioPivot.scenarioKey,
              label: selectedScenarioPivot.label,
              artifactCountLabel: selectedScenarioPivot.artifactCountLabel,
              representativeArtifactIdLabel: selectedScenarioPivot.representativeArtifactIdLabel,
              latestUploadAtLabel: selectedScenarioPivot.latestUploadAtLabel,
              sourceConnectionLabel: selectedScenarioPivot.sourceConnectionLabel,
              coverageStateLabel: selectedScenarioPivot.coverageStateLabel,
              fallbackSummaryLabel: selectedScenarioPivot.fallbackSummaryLabel,
              latestTraceLabel: selectedScenarioPivot.latestTraceLabel,
            }
          : undefined,
        representativeSelectionParityLabel,
        timelineRows: filteredMediaTimelineRows,
        incidentCompareArtifactId: selectedIncidentArtifact?.artifactId ?? recommendedIncidentArtifactId,
        nextActionLabel:
          selectedArtifactPhotoCompareSummary?.compareNextActionLabel ??
          representativeCompareNextActionLabel ??
          selectedArtifact?.binaryReviewHint,
      }),
    [
      filteredMediaTimelineRows,
      mediaReviewPackageRoute,
      params.internalUserId,
      recommendedIncidentArtifactId,
      representativeCompareNextActionLabel,
      representativeSelectionParityLabel,
      selectedArtifact,
      selectedArtifactPhotoCompareSummary,
      selectedIncidentArtifact,
      selectedScenarioPivot,
      searchParams,
      state,
    ],
  );
  const requestedMediaSelection = useMemo(() => {
    if (!(state.status === "ready" || state.status === "partial" || state.status === "stale")) {
      return undefined;
    }

    return resolveUserMediaWorkspaceSelection(
      state.viewModel.mediaArtifacts.map((artifact) => ({
        artifactId: artifact.artifactId,
        sourceScenarioKey: artifact.sourceScenarioKey,
      })),
      state.viewModel.files,
      requestedMediaFocus,
    );
  }, [requestedMediaFocus, state]);
  const contextualRequestedMediaSelection = useMemo(() => {
    if (!(state.status === "ready" || state.status === "partial" || state.status === "stale")) {
      return undefined;
    }

    return resolveContextualUserMediaWorkspaceSelection(
      state.viewModel.mediaArtifacts.map((artifact) => ({
        artifactId: artifact.artifactId,
        sourceScenarioKey: artifact.sourceScenarioKey,
        latestRequestId: artifact.latestRequestId,
        latestCorrelationId: artifact.latestCorrelationId,
      })),
      requestedMediaFocus,
      {
        requestId: requestedRequestId,
        correlationId: requestedCorrelationId,
      },
    );
  }, [requestedCorrelationId, requestedMediaFocus, requestedRequestId, state]);

  function focusMediaWorkspace(
    artifactId: string | undefined,
    scenarioKey: UserMediaWorkspaceScenarioKey | undefined,
  ): void {
    if (scenarioKey) {
      setSelectedScenarioKey(scenarioKey);
    }

    if (artifactId) {
      setSelectedArtifactId(artifactId);
    }
  }

  function focusIncidentWorkspace(artifactId: string | undefined): void {
    if (artifactId) {
      setSelectedIncidentArtifactId(artifactId);
    }
  }

  function openIncidentCompareWorkspace(artifactId: string | undefined): void {
    focusIncidentWorkspace(artifactId);
    incidentWorkspaceSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  useEffect(() => {
    const requestedSelectionKey = requestedMediaFocus
      ? [requestedMediaFocus.artifactId ?? "", requestedMediaFocus.fileId ?? "", requestedMediaFocus.scenarioKey ?? ""].join(":")
      : "";

    const resolvedRequestedSelection = requestedMediaSelection ?? contextualRequestedMediaSelection;

    if (!requestedSelectionKey || !resolvedRequestedSelection) {
      if (!requestedSelectionKey) {
        appliedRequestedSelectionKeyRef.current = "";
      }
      return;
    }

    if (appliedRequestedSelectionKeyRef.current === requestedSelectionKey) {
      return;
    }

    appliedRequestedSelectionKeyRef.current = requestedSelectionKey;
    if (resolvedRequestedSelection.scenarioKey) {
      setSelectedScenarioKey(resolvedRequestedSelection.scenarioKey);
    }
    if (resolvedRequestedSelection.artifactId) {
      setSelectedArtifactId(resolvedRequestedSelection.artifactId);
    }
  }, [contextualRequestedMediaSelection, requestedMediaFocus, requestedMediaSelection]);

  useEffect(() => {
    const requestedIncidentArtifactId = requestedIncidentFocus?.artifactId ?? "";

    if (!requestedIncidentArtifactId) {
      appliedRequestedIncidentArtifactIdRef.current = "";
      return;
    }

    if (!(state.status === "ready" || state.status === "partial" || state.status === "stale")) {
      return;
    }

    if (appliedRequestedIncidentArtifactIdRef.current === requestedIncidentArtifactId) {
      return;
    }

    const matchedArtifact = state.viewModel.incidentLogArtifacts.find(
      (artifact) => artifact.artifactId === requestedIncidentArtifactId,
    );

    if (!matchedArtifact) {
      return;
    }

    appliedRequestedIncidentArtifactIdRef.current = requestedIncidentArtifactId;
    setSelectedIncidentArtifactId(requestedIncidentArtifactId);
  }, [requestedIncidentFocus, state]);

  useEffect(() => {
    if (!(state.status === "ready" || state.status === "partial" || state.status === "stale")) {
      return;
    }

    if (filteredMediaArtifacts.length === 0) {
      if (selectedArtifactId !== undefined) {
        setSelectedArtifactId(undefined);
      }
      return;
    }

    const fallbackArtifactId =
      filteredMediaArtifacts.find((artifact) => artifact.artifactId === state.viewModel.representativePhoto?.artifactId)?.artifactId
      ?? filteredMediaArtifacts[0]?.artifactId;
    const hasSelectedArtifact = selectedArtifactId
      ? filteredMediaArtifacts.some((artifact) => artifact.artifactId === selectedArtifactId)
      : false;

    if (!hasSelectedArtifact && fallbackArtifactId && fallbackArtifactId !== selectedArtifactId) {
      setSelectedArtifactId(fallbackArtifactId);
    }
  }, [filteredMediaArtifacts, selectedArtifactId, state]);

  useEffect(() => {
    if (!(state.status === "ready" || state.status === "partial" || state.status === "stale")) {
      return;
    }

    if (state.viewModel.incidentLogArtifacts.length === 0) {
      if (selectedIncidentArtifactId !== undefined) {
        setSelectedIncidentArtifactId(undefined);
      }
      return;
    }

    const fallbackArtifactId = pickRecommendedIncidentLogArtifactId(
      incidentWorkspacePhotoArtifact,
      state.viewModel.incidentLogArtifacts,
      requestedSameDeviceContext,
    );
    const hasSelectedArtifact = selectedIncidentArtifactId
      ? state.viewModel.incidentLogArtifacts.some(
          (artifact) => artifact.artifactId === selectedIncidentArtifactId,
        )
      : false;
    const selectedArtifactRequestId = incidentWorkspacePhotoArtifact?.latestRequestId;
    const selectedIncidentMatchesPhoto = selectedIncidentArtifactId
      ? state.viewModel.incidentLogArtifacts.some(
          (artifact) =>
            artifact.artifactId === selectedIncidentArtifactId &&
            artifact.latestRequestId === selectedArtifactRequestId,
        )
      : false;
    const requestedIncidentArtifactPinned =
      requestedIncidentFocus?.artifactId !== undefined &&
      requestedIncidentFocus.artifactId === selectedIncidentArtifactId &&
      hasSelectedArtifact;

    if (
      ((!hasSelectedArtifact) ||
        (Boolean(selectedArtifactRequestId) &&
          !selectedIncidentMatchesPhoto &&
          !requestedIncidentArtifactPinned)) &&
      fallbackArtifactId &&
      fallbackArtifactId !== selectedIncidentArtifactId
    ) {
      setSelectedIncidentArtifactId(fallbackArtifactId);
    }
  }, [incidentWorkspacePhotoArtifact, requestedIncidentFocus, requestedSameDeviceContext, selectedIncidentArtifactId, state]);

  const copy = {
    section: pickAdminDashboardLocaleText(locale, { en: "User Detail", ko: "사용자 상세" }),
    title: pickAdminDashboardLocaleText(locale, { en: "Stored User Data Detail", ko: "사용자 저장 데이터 상세" }),
    back: pickAdminDashboardLocaleText(locale, { en: "Back to summary", ko: "요약으로 돌아가기" }),
    loading: pickAdminDashboardLocaleText(locale, { en: "Loading user data detail...", ko: "사용자 상세 데이터를 불러오는 중입니다..." }),
    deniedTitle: pickAdminDashboardLocaleText(locale, { en: "User detail denied", ko: "사용자 상세 조회 권한 없음" }),
    deniedBody: pickAdminDashboardLocaleText(locale, { en: "User data detail read permission is missing.", ko: "사용자 데이터 상세 조회 권한이 없습니다." }),
    failedTitle: pickAdminDashboardLocaleText(locale, { en: "User detail failed", ko: "사용자 상세 조회 실패" }),
    emptyTitle: pickAdminDashboardLocaleText(locale, { en: "No user detail", ko: "사용자 상세 없음" }),
    emptyBody: pickAdminDashboardLocaleText(locale, { en: "No result. internalUserId=", ko: "조회 결과가 없습니다. 내부 사용자 ID=" }),
    emptyNoParam: pickAdminDashboardLocaleText(locale, { en: "The internalUserId route param is missing, so detail lookup cannot start.", ko: "내부 사용자 ID 경로 파라미터가 없어 상세 조회를 시작할 수 없습니다." }),
    eyebrow: pickAdminDashboardLocaleText(locale, { en: "User Data Detail", ko: "사용자 데이터 상세" }),
    description: pickAdminDashboardLocaleText(locale, { en: "A detailed operator surface that checks missing stored data, broken links, deletion or cleanup status, and recent failures in one place.", ko: "저장 데이터 누락, 연결 깨짐, 삭제 또는 정리 상태, 최근 실패 흐름을 같은 화면에서 확인하는 운영자용 상세 화면입니다." }),
    internal: pickAdminDashboardLocaleText(locale, { en: "internal", ko: "내부 ID" }),
    accountCue: pickAdminDashboardLocaleText(locale, { en: "Account cue", ko: "계정 단서" }),
    providerCue: pickAdminDashboardLocaleText(locale, { en: "Provider cue", ko: "provider 단서" }),
    linkedProviders: pickAdminDashboardLocaleText(locale, { en: "Linked providers", ko: "연결된 provider" }),
    detailSnapshot: pickAdminDashboardLocaleText(locale, { en: "Detail snapshot", ko: "상세 스냅샷" }),
    metadata: pickAdminDashboardLocaleText(locale, { en: "Metadata", ko: "조회 메타데이터" }),
    freshness: pickAdminDashboardLocaleText(locale, { en: "Freshness", ko: "신선도" }),
    release: pickAdminDashboardLocaleText(locale, { en: "Release", ko: "릴리스" }),
    app: pickAdminDashboardLocaleText(locale, { en: "App", ko: "앱" }),
    appVersion: pickAdminDashboardLocaleText(locale, { en: "App version", ko: "앱 버전" }),
    platform: pickAdminDashboardLocaleText(locale, { en: "Platform", ko: "플랫폼" }),
    generated: pickAdminDashboardLocaleText(locale, { en: "Generated", ko: "생성 시각" }),
    request: pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" }),
    timeBucket: pickAdminDashboardLocaleText(locale, { en: "Time bucket", ko: "시간 구간" }),
    currentEvidenceTitle: pickAdminDashboardLocaleText(locale, { en: "Current evidence", ko: "현재 증거" }),
    currentEvidenceDesc: pickAdminDashboardLocaleText(locale, { en: "Read account deletion severity, current risk, and the latest linked failure together before moving into tables.", ko: "표 영역으로 내려가기 전에 계정 삭제 심각도, 현재 위험, 가장 최근 연결 실패를 함께 읽습니다." }),
    accountDeletion: pickAdminDashboardLocaleText(locale, { en: "Account deletion", ko: "계정 삭제" }),
    topSummary: pickAdminDashboardLocaleText(locale, { en: "Top summary", ko: "상단 요약" }),
    severity: pickAdminDashboardLocaleText(locale, { en: "Severity", ko: "심각도" }),
    status: pickAdminDashboardLocaleText(locale, { en: "Status", ko: "상태" }),
    requested: pickAdminDashboardLocaleText(locale, { en: "Requested", ko: "요청 시각" }),
    completed: pickAdminDashboardLocaleText(locale, { en: "Completed", ko: "완료 시각" }),
    primary: pickAdminDashboardLocaleText(locale, { en: "Primary", ko: "기본 동작" }),
    jobs: pickAdminDashboardLocaleText(locale, { en: "Jobs", ko: "작업" }),
    openDeletionJobs: pickAdminDashboardLocaleText(locale, { en: "Open deletion jobs", ko: "삭제 작업 열기" }),
    reqShort: pickAdminDashboardLocaleText(locale, { en: "req", ko: "요청" }),
    corrShort: pickAdminDashboardLocaleText(locale, { en: "corr", ko: "상관관계" }),
    openDeletionFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Open deletion failed flow", ko: "삭제 오류 흐름 열기" }),
    riskSummary: pickAdminDashboardLocaleText(locale, { en: "Risk summary", ko: "위험 요약" }),
    overview: pickAdminDashboardLocaleText(locale, { en: "Overview", ko: "개요" }),
    risk: pickAdminDashboardLocaleText(locale, { en: "Risk", ko: "위험" }),
    signals: pickAdminDashboardLocaleText(locale, { en: "Signals", ko: "신호" }),
    evidence: pickAdminDashboardLocaleText(locale, { en: "evidence", ko: "근거" }),
    noRiskSignals: pickAdminDashboardLocaleText(locale, { en: "No risk signals to summarize.", ko: "요약할 위험 신호가 없습니다." }),
    dailyEvidenceTitle: pickAdminDashboardLocaleText(locale, { en: "Daily triage evidence", ko: "일상 조사 증거" }),
    dailyEvidenceDesc: pickAdminDashboardLocaleText(locale, { en: "Compare the latest daily failure, latest successful save, and current risk before checking raw record rows.", ko: "원시 기록 행을 보기 전에 최근 일상 실패, 최근 저장 성공, 현재 위험을 먼저 비교합니다." }),
    dailyRecordTriage: pickAdminDashboardLocaleText(locale, { en: "Daily record triage", ko: "일상 기록 조사" }),
    dailyScenario: pickAdminDashboardLocaleText(locale, { en: "Daily scenario", ko: "일상 시나리오" }),
    dailyRecords: pickAdminDashboardLocaleText(locale, { en: "Daily records", ko: "일상 기록" }),
    dailyFailures: pickAdminDashboardLocaleText(locale, { en: "Daily failures", ko: "일상 실패" }),
    dailySaves: pickAdminDashboardLocaleText(locale, { en: "Daily saves", ko: "일상 저장" }),
    latestDailyFailure: pickAdminDashboardLocaleText(locale, { en: "Latest daily failure", ko: "최근 일상 실패" }),
    latestDailySave: pickAdminDashboardLocaleText(locale, { en: "Latest daily save", ko: "최근 일상 저장" }),
    openDailyFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Open daily failed flow", ko: "일상 오류 흐름 열기" }),
    type: pickAdminDashboardLocaleText(locale, { en: "Type", ko: "유형" }),
    record: pickAdminDashboardLocaleText(locale, { en: "Record", ko: "기록" }),
    recentFailures: pickAdminDashboardLocaleText(locale, { en: "Recent failures", ko: "최근 실패" }),
    latestOverall: pickAdminDashboardLocaleText(locale, { en: "Latest overall", ko: "전체 최근 항목" }),
    feature: pickAdminDashboardLocaleText(locale, { en: "Feature", ko: "기능" }),
    useCase: pickAdminDashboardLocaleText(locale, { en: "Use case", ko: "유스케이스" }),
    error: pickAdminDashboardLocaleText(locale, { en: "Error", ko: "오류" }),
    occurred: pickAdminDashboardLocaleText(locale, { en: "Occurred", ko: "발생 시각" }),
    drilldown: pickAdminDashboardLocaleText(locale, { en: "Drilldown", ko: "연결 조사" }),
    openFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Open failed flow", ko: "오류 흐름 열기" }),
    open: pickAdminDashboardLocaleText(locale, { en: "Open", ko: "열기" }),
    noRecentFailures: pickAdminDashboardLocaleText(locale, { en: "No recent failed flows.", ko: "최근 실패 흐름이 없습니다." }),
    latestPetFailure: pickAdminDashboardLocaleText(locale, { en: "Latest pet failure", ko: "최근 반려동물 실패" }),
    petFlow: pickAdminDashboardLocaleText(locale, { en: "Pet flow", ko: "반려동물 흐름" }),
    openPetFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Open pet failed flow", ko: "반려동물 오류 흐름 열기" }),
    noRecentPetFailure: pickAdminDashboardLocaleText(locale, { en: "No recent pet failure.", ko: "최근 반려동물 실패가 없습니다." }),
    recentSaves: pickAdminDashboardLocaleText(locale, { en: "Recent saves", ko: "최근 저장" }),
    event: pickAdminDashboardLocaleText(locale, { en: "Event", ko: "이벤트" }),
    receipt: pickAdminDashboardLocaleText(locale, { en: "Receipt", ko: "영수증" }),
    noRecentSaves: pickAdminDashboardLocaleText(locale, { en: "No recent successful save events.", ko: "최근 저장 성공 이벤트가 없습니다." }),
    account: pickAdminDashboardLocaleText(locale, { en: "Account", ko: "계정" }),
    accountMetadata: pickAdminDashboardLocaleText(locale, { en: "Account metadata", ko: "계정 정보" }),
    pets: pickAdminDashboardLocaleText(locale, { en: "Pets", ko: "펫" }),
    petsBody: pickAdminDashboardLocaleText(locale, { en: "Check missing profiles, broken photo links, and deletion state first.", ko: "프로필 누락, 사진 링크 깨짐, 삭제 여부를 먼저 확인합니다." }),
    petId: pickAdminDashboardLocaleText(locale, { en: "Pet ID", ko: "펫 ID" }),
    petHash: pickAdminDashboardLocaleText(locale, { en: "Pet Hash", ko: "펫 해시" }),
    created: pickAdminDashboardLocaleText(locale, { en: "Created", ko: "생성 시각" }),
    species: pickAdminDashboardLocaleText(locale, { en: "Species", ko: "종류" }),
    profile: pickAdminDashboardLocaleText(locale, { en: "Profile", ko: "프로필" }),
    photo: pickAdminDashboardLocaleText(locale, { en: "Photo", ko: "사진" }),
    records: pickAdminDashboardLocaleText(locale, { en: "Records", ko: "기록" }),
    deleted: pickAdminDashboardLocaleText(locale, { en: "Deleted", ko: "삭제 시각" }),
    noPets: pickAdminDashboardLocaleText(locale, { en: "No pets", ko: "펫 없음" }),
    noPetsBody: pickAdminDashboardLocaleText(locale, { en: "No linked pet data.", ko: "연결된 반려동물 데이터가 없습니다." }),
    recordsTitle: pickAdminDashboardLocaleText(locale, { en: "Records", ko: "기록" }),
    recordsBody: pickAdminDashboardLocaleText(locale, { en: "Review record storage state, file linkage, and AI processing together.", ko: "기록 저장 상태, 파일 연결, AI 처리 상태를 함께 확인합니다." }),
    recordId: pickAdminDashboardLocaleText(locale, { en: "Record ID", ko: "기록 ID" }),
    titleLabel: pickAdminDashboardLocaleText(locale, { en: "Title", ko: "제목" }),
    recorded: pickAdminDashboardLocaleText(locale, { en: "Recorded", ko: "기록 시각" }),
    updated: pickAdminDashboardLocaleText(locale, { en: "Updated", ko: "수정 시각" }),
    files: pickAdminDashboardLocaleText(locale, { en: "Files", ko: "파일" }),
    aiJob: pickAdminDashboardLocaleText(locale, { en: "AI Job", ko: "AI 작업" }),
    completeness: pickAdminDashboardLocaleText(locale, { en: "Completeness", ko: "완전성" }),
    noRecords: pickAdminDashboardLocaleText(locale, { en: "No records", ko: "기록 없음" }),
    noRecordsBody: pickAdminDashboardLocaleText(locale, { en: "No linked record data.", ko: "연결된 기록 데이터가 없습니다." }),
    receipts: pickAdminDashboardLocaleText(locale, { en: "Receipts", ko: "영수증" }),
    receiptsBody: pickAdminDashboardLocaleText(locale, { en: "Check receipt storage, draft ingress, and soft-delete traces.", ko: "영수증 저장, 초안 유입, 소프트 삭제 흔적을 확인합니다." }),
    receiptId: pickAdminDashboardLocaleText(locale, { en: "Receipt ID", ko: "영수증 ID" }),
    purchased: pickAdminDashboardLocaleText(locale, { en: "Purchased", ko: "구매 시각" }),
    amount: pickAdminDashboardLocaleText(locale, { en: "Amount", ko: "금액" }),
    category: pickAdminDashboardLocaleText(locale, { en: "Category", ko: "카테고리" }),
    extraction: pickAdminDashboardLocaleText(locale, { en: "Extraction", ko: "추출" }),
    noReceipts: pickAdminDashboardLocaleText(locale, { en: "No receipts", ko: "영수증 없음" }),
    noReceiptsBody: pickAdminDashboardLocaleText(locale, { en: "No linked receipt data.", ko: "연결된 영수증 데이터가 없습니다." }),
    filesTitle: pickAdminDashboardLocaleText(locale, { en: "Files", ko: "파일" }),
    filesBody: pickAdminDashboardLocaleText(locale, { en: "Review confirmed/temp/cleanup state alongside lifecycle and upstream traces.", ko: "확정/임시/정리 상태와 수명주기 조사, 상위 요청 추적을 함께 봅니다." }),
    uploadDiagnosisTitle: pickAdminDashboardLocaleText(locale, { en: "Upload diagnosis workspace", ko: "업로드 진단 작업면" }),
    uploadDiagnosisBody: pickAdminDashboardLocaleText(locale, {
      en: "Read how each upload-linked flow moved from source evidence to extract or analysis and final save, without opening raw OCR or Gemini payloads.",
      ko: "raw OCR 또는 Gemini payload를 열지 않고도, 각 업로드 연결 흐름이 원본 증거에서 추출 또는 분석을 거쳐 최종 저장까지 어떻게 이동했는지 읽습니다.",
    }),
    sourceAnchor: pickAdminDashboardLocaleText(locale, { en: "Source anchor", ko: "원본 anchor" }),
    recognitionSignal: pickAdminDashboardLocaleText(locale, { en: "Recognition signal", ko: "인식 신호" }),
    failedModule: pickAdminDashboardLocaleText(locale, { en: "Failed module", ko: "실패 모듈" }),
    latestSaveEvidence: pickAdminDashboardLocaleText(locale, { en: "Latest save evidence", ko: "최근 저장 근거" }),
    uploadSteps: pickAdminDashboardLocaleText(locale, { en: "Steps", ko: "단계" }),
    stepDetail: pickAdminDashboardLocaleText(locale, { en: "Step detail", ko: "단계 상세" }),
    openStepFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Open failed flow", ko: "오류 흐름 열기" }),
    openStepJobs: pickAdminDashboardLocaleText(locale, { en: "Open jobs", ko: "작업 열기" }),
    representativePhotoTitle: pickAdminDashboardLocaleText(locale, { en: "Representative photo", ko: "대표 사진" }),
    representativePhotoBody: pickAdminDashboardLocaleText(locale, { en: "Use the representative artifact as the first photo evidence entry before opening the full corpus list.", ko: "전체 코퍼스를 열기 전에 대표 artifact를 첫 사진 증거 진입점으로 사용합니다." }),
    mediaArtifactsTitle: pickAdminDashboardLocaleText(locale, { en: "Uploaded media artifacts", ko: "업로드 미디어 artifact" }),
    mediaArtifactsBody: pickAdminDashboardLocaleText(locale, { en: "Review uploaded photo or file artifacts by scenario, role, preview or download availability, and latest request trace.", ko: "시나리오, 역할, 미리보기 또는 다운로드 가능 여부, 최근 요청 추적 기준으로 업로드 artifact를 확인합니다." }),
    incidentLogsTitle: pickAdminDashboardLocaleText(locale, { en: "Same-device incident log artifacts", ko: "동일 기기 incident log artifact" }),
    incidentLogsBody: pickAdminDashboardLocaleText(locale, { en: "Review which same-device log bundles are supportable, preview-ready, or download-ready before falling back to request trace only.", ko: "request trace만 보조로 남기기 전에 어떤 same-device log bundle이 지원되는지, preview 가능한지, download 가능한지 먼저 확인합니다." }),
    incidentWorkspaceTitle: pickAdminDashboardLocaleText(locale, { en: "Incident evidence workspace", ko: "incident 증거 작업면" }),
    incidentWorkspaceBody: pickAdminDashboardLocaleText(locale, { en: "Pin one same-device log artifact, read the bounded excerpt, then compare it with the selected photo evidence and linked request trace.", ko: "same-device log artifact 하나를 고정하고 bounded excerpt를 읽은 뒤, 선택된 사진 증거와 연결된 요청 추적을 함께 비교합니다." }),
    mediaScenarioPivotTitle: pickAdminDashboardLocaleText(locale, { en: "Scenario pivots", ko: "시나리오 pivot" }),
    mediaScenarioPivotBody: pickAdminDashboardLocaleText(locale, { en: "Narrow the full uploaded corpus by user scenario before comparing one selected artifact.", ko: "선택 artifact를 비교하기 전에 전체 업로드 코퍼스를 사용자 시나리오 기준으로 먼저 좁혀 봅니다." }),
    mediaTimelineTitle: pickAdminDashboardLocaleText(locale, { en: "Upload timeline", ko: "업로드 타임라인" }),
    mediaTimelineBody: pickAdminDashboardLocaleText(locale, {
      en: "Review the same uploaded corpus in reverse upload order so the latest artifact, its scenario, and its binary review path stay visible before you pin one row.",
      ko: "같은 업로드 코퍼스를 최근 업로드 순서로 다시 읽어, row 하나를 고정하기 전에 최신 artifact, 시나리오, binary review 경로를 함께 확인합니다.",
    }),
    mediaCoverageTitle: pickAdminDashboardLocaleText(locale, { en: "Coverage and fallback states", ko: "coverage 및 fallback 상태" }),
    mediaCoverageBody: pickAdminDashboardLocaleText(locale, { en: "Read which photo scenarios are previewable, download-only, metadata-only, or still missing before you assume a data or product failure.", ko: "데이터 또는 제품 실패로 단정하기 전에 어떤 사진 시나리오가 미리보기 가능, 다운로드 전용, 메타데이터만 가능, 또는 아직 누락 상태인지 먼저 읽습니다." }),
    sourceSurfaceTitle: pickAdminDashboardLocaleText(locale, { en: "Source surface baseline", ko: "source surface 기준선" }),
    sourceSurfaceBody: pickAdminDashboardLocaleText(locale, { en: "Read which uploaded photo surfaces are currently connected, projection-dependent, or still a rollout gap before interpreting missing inventory.", ko: "누락 inventory를 해석하기 전에 어떤 업로드 사진 surface가 현재 연결됐는지, projection 보강이 필요한지, 아직 rollout gap인지 먼저 읽습니다." }),
    missingSourceTitle: pickAdminDashboardLocaleText(locale, { en: "Missing source disclosures", ko: "미연결 source 고지" }),
    missingSourceBody: pickAdminDashboardLocaleText(locale, { en: "Separate rollout gaps from real missing uploads so operators do not mistake unconnected inventory for user data loss.", ko: "운영자가 미연결 inventory를 사용자 데이터 손실로 오인하지 않도록 rollout gap과 실제 누락 업로드를 분리해 읽습니다." }),
    sourceConnection: pickAdminDashboardLocaleText(locale, { en: "Source connection", ko: "source 연결 상태" }),
    sourceStatus: pickAdminDashboardLocaleText(locale, { en: "Source status", ko: "source 상태" }),
    targetEntity: pickAdminDashboardLocaleText(locale, { en: "Target entity", ko: "대상 엔터티" }),
    linkedEntityType: pickAdminDashboardLocaleText(locale, { en: "Linked entity type", ko: "연결 엔터티 유형" }),
    baselineSummary: pickAdminDashboardLocaleText(locale, { en: "Baseline summary", ko: "기준선 요약" }),
    currentCoverage: pickAdminDashboardLocaleText(locale, { en: "Current coverage", ko: "현재 coverage" }),
    binaryReviewPath: pickAdminDashboardLocaleText(locale, { en: "Binary review path", ko: "binary review 경로" }),
    archiveState: pickAdminDashboardLocaleText(locale, { en: "Archive state", ko: "archive 상태" }),
    incidentAttachment: pickAdminDashboardLocaleText(locale, { en: "Incident attachment", ko: "incident 연결" }),
    representativeIncluded: pickAdminDashboardLocaleText(locale, { en: "Representative included", ko: "대표 포함" }),
    artifactCount: pickAdminDashboardLocaleText(locale, { en: "Artifact count", ko: "artifact 수" }),
    representativeArtifact: pickAdminDashboardLocaleText(locale, { en: "Representative artifact", ko: "대표 artifact" }),
    latestUpload: pickAdminDashboardLocaleText(locale, { en: "Latest upload", ko: "최근 업로드" }),
    archiveReadyCount: pickAdminDashboardLocaleText(locale, { en: "Archive-ready", ko: "archive 준비" }),
    incidentAttachedCount: pickAdminDashboardLocaleText(locale, { en: "Incident-attached", ko: "incident 연결 수" }),
    selectedMatchesRepresentative: pickAdminDashboardLocaleText(locale, { en: "Matches scenario representative", ko: "시나리오 대표 일치" }),
    selectedMatchesLatestUpload: pickAdminDashboardLocaleText(locale, { en: "Matches latest upload", ko: "최근 업로드 일치" }),
    coverageState: pickAdminDashboardLocaleText(locale, { en: "Coverage state", ko: "coverage 상태" }),
    previewReady: pickAdminDashboardLocaleText(locale, { en: "Preview ready", ko: "미리보기 가능 수" }),
    downloadReady: pickAdminDashboardLocaleText(locale, { en: "Download ready", ko: "다운로드 가능 수" }),
    downloadOnly: pickAdminDashboardLocaleText(locale, { en: "Download only", ko: "다운로드 전용 수" }),
    blockedArtifacts: pickAdminDashboardLocaleText(locale, { en: "Blocked artifacts", ko: "차단된 artifact 수" }),
    fallbackSummary: pickAdminDashboardLocaleText(locale, { en: "Fallback summary", ko: "fallback 요약" }),
    mediaCoverageHint: pickAdminDashboardLocaleText(locale, { en: "Operator hint", ko: "운영자 힌트" }),
    missingSourceReason: pickAdminDashboardLocaleText(locale, { en: "Reason", ko: "사유" }),
    nextAction: pickAdminDashboardLocaleText(locale, { en: "Next action", ko: "다음 액션" }),
    workspaceScenarioState: pickAdminDashboardLocaleText(locale, { en: "Scenario state", ko: "시나리오 상태" }),
    workspaceScenarioStateBody: pickAdminDashboardLocaleText(locale, {
      en: "Read the selected scenario coverage and fallback here before deciding whether to stay on preview, move to original download, or jump back to trace and lifecycle context.",
      ko: "선택한 시나리오의 coverage와 fallback을 여기서 먼저 읽고, 미리보기를 유지할지 원본 다운로드로 갈지, 추적/라이프사이클 문맥으로 돌아갈지 결정합니다.",
    }),
    yes: pickAdminDashboardLocaleText(locale, { en: "Yes", ko: "예" }),
    no: pickAdminDashboardLocaleText(locale, { en: "No", ko: "아니오" }),
    mediaWorkspaceTitle: pickAdminDashboardLocaleText(locale, { en: "Artifact investigation workspace", ko: "artifact 조사 작업면" }),
    mediaWorkspaceBody: pickAdminDashboardLocaleText(locale, { en: "Pin one uploaded artifact, compare its request trace, then issue preview or original download from the same user context.", ko: "업로드 artifact 하나를 고정하고 요청 추적을 비교한 뒤, 같은 사용자 문맥에서 미리보기 또는 원본 다운로드를 발급합니다." }),
    artifactId: pickAdminDashboardLocaleText(locale, { en: "Artifact ID", ko: "Artifact ID" }),
    source: pickAdminDashboardLocaleText(locale, { en: "Source", ko: "Source" }),
    relation: pickAdminDashboardLocaleText(locale, { en: "Relation", ko: "Relation" }),
    role: pickAdminDashboardLocaleText(locale, { en: "Role", ko: "Role" }),
    mediaKind: pickAdminDashboardLocaleText(locale, { en: "Media kind", ko: "미디어 종류" }),
    auditScope: pickAdminDashboardLocaleText(locale, { en: "Audit scope", ko: "감사 범위" }),
    sameDeviceContext: pickAdminDashboardLocaleText(locale, { en: "Same-device context", ko: "동일 기기 문맥" }),
    deviceInstallId: pickAdminDashboardLocaleText(locale, { en: "Device install", ko: "기기 설치본" }),
    captureTrigger: pickAdminDashboardLocaleText(locale, { en: "Capture trigger", ko: "수집 트리거" }),
    captureWindow: pickAdminDashboardLocaleText(locale, { en: "Capture window", ko: "수집 구간" }),
    runtimeSummary: pickAdminDashboardLocaleText(locale, { en: "Runtime summary", ko: "런타임 요약" }),
    capturedAt: pickAdminDashboardLocaleText(locale, { en: "Captured at", ko: "수집 시각" }),
    supportability: pickAdminDashboardLocaleText(locale, { en: "Supportability", ko: "지원 상태" }),
    excerptLines: pickAdminDashboardLocaleText(locale, { en: "Excerpt lines", ko: "발췌 줄 수" }),
    bundleHash: pickAdminDashboardLocaleText(locale, { en: "Bundle hash", ko: "Bundle hash" }),
    manifestVersion: pickAdminDashboardLocaleText(locale, { en: "Manifest", ko: "Manifest" }),
    redactionVersion: pickAdminDashboardLocaleText(locale, { en: "Redaction", ko: "Redaction" }),
    previewReadiness: pickAdminDashboardLocaleText(locale, { en: "Preview readiness", ko: "미리보기 준비도" }),
    downloadReadiness: pickAdminDashboardLocaleText(locale, { en: "Download readiness", ko: "다운로드 준비도" }),
    handoffReadiness: pickAdminDashboardLocaleText(locale, { en: "Handoff readiness", ko: "handoff 준비도" }),
    previewHeadline: pickAdminDashboardLocaleText(locale, { en: "Preview headline", ko: "미리보기 헤드라인" }),
    topCategory: pickAdminDashboardLocaleText(locale, { en: "Top category", ko: "상위 카테고리" }),
    diagnosisTitle: pickAdminDashboardLocaleText(locale, { en: "Diagnosis summary", ko: "진단 요약" }),
    diagnosisBody: pickAdminDashboardLocaleText(locale, {
      en: "Read the excerpt-derived failure signal here before deciding whether the issue is already clear enough to download and hand off.",
      ko: "다운로드와 handoff로 넘어가기 전에 excerpt 기반 실패 신호가 충분히 분명한지 여기서 먼저 읽습니다.",
    }),
    failureSignal: pickAdminDashboardLocaleText(locale, { en: "Failure signal", ko: "실패 신호" }),
    diagnosisConfidence: pickAdminDashboardLocaleText(locale, { en: "Diagnosis confidence", ko: "진단 확신도" }),
    level: pickAdminDashboardLocaleText(locale, { en: "Level", ko: "수준" }),
    messageKey: pickAdminDashboardLocaleText(locale, { en: "Message key", ko: "메시지 키" }),
    redactedContext: pickAdminDashboardLocaleText(locale, { en: "Redacted context", ko: "redacted 문맥" }),
    linkedPhotoEvidence: pickAdminDashboardLocaleText(locale, { en: "Linked photo evidence", ko: "연결된 사진 증거" }),
    previewPhotoEvidence: pickAdminDashboardLocaleText(locale, { en: "Preview photo evidence", ko: "사진 증거 미리보기" }),
    downloadPhotoOriginal: pickAdminDashboardLocaleText(locale, { en: "Download photo original", ko: "사진 원본 다운로드" }),
    downloadLogBundle: pickAdminDashboardLocaleText(locale, { en: "Download log bundle", ko: "로그 번들 다운로드" }),
    openPhotoWorkspace: pickAdminDashboardLocaleText(locale, { en: "Open photo workspace", ko: "사진 작업면 열기" }),
    traceAlignment: pickAdminDashboardLocaleText(locale, { en: "Trace alignment", ko: "추적 정렬" }),
    alignedTrace: pickAdminDashboardLocaleText(locale, { en: "Aligned", ko: "정렬됨" }),
    contextualTrace: pickAdminDashboardLocaleText(locale, { en: "Contextual", ko: "문맥 연결" }),
    noLinkedPhotoEvidence: pickAdminDashboardLocaleText(locale, { en: "No photo evidence is pinned yet", ko: "아직 고정된 사진 증거가 없습니다" }),
    handoffPackageTitle: pickAdminDashboardLocaleText(locale, { en: "Unified handoff package", ko: "통합 handoff 패키지" }),
    handoffPackageBody: pickAdminDashboardLocaleText(locale, {
      en: "Keep photo evidence, same-device log readiness, and request trace linkage in one operator handoff block before you leave this route.",
      ko: "이 화면을 떠나기 전에 사진 증거, same-device 로그 readiness, 요청 추적 연결을 하나의 운영 handoff 블록에 함께 고정합니다.",
    }),
    handoffPackageSchemaTitle: pickAdminDashboardLocaleText(locale, { en: "Package preview", ko: "패키지 미리보기" }),
    handoffPackageSchemaBody: pickAdminDashboardLocaleText(locale, {
      en: "The workspace keeps a structured preview of the incident handoff payload so engineering can reopen the same photo, trace, and same-device log context without reinterpreting screenshots.",
      ko: "이 작업면은 스크린샷을 다시 해석하지 않아도 엔지니어링이 같은 사진, 추적, same-device 로그 문맥을 다시 열 수 있도록 incident handoff payload의 구조화된 미리보기를 유지합니다.",
    }),
    photoLinkedIncidentTitle: pickAdminDashboardLocaleText(locale, { en: "Photo-linked incident state", ko: "사진 연결 incident 상태" }),
    photoLinkedIncidentBody: pickAdminDashboardLocaleText(locale, {
      en: "Read whether the selected photo and same-device log are directly aligned, context-only, or still missing one side before you hand this incident off.",
      ko: "이 incident를 넘기기 전에 선택된 사진과 same-device 로그가 직접 정렬인지, 문맥 연결만 남았는지, 한쪽이 비었는지 먼저 읽습니다.",
    }),
    photoLinkedIncidentState: pickAdminDashboardLocaleText(locale, { en: "Photo-log relation", ko: "사진-로그 관계" }),
    photoLinkedIncidentHint: pickAdminDashboardLocaleText(locale, { en: "Relation hint", ko: "관계 힌트" }),
    photoLinkedIncidentNextAction: pickAdminDashboardLocaleText(locale, { en: "Photo-log next action", ko: "사진-로그 다음 액션" }),
    deliveryPathState: pickAdminDashboardLocaleText(locale, { en: "Delivery path state", ko: "전달 경로 상태" }),
    manifestCompareTitle: pickAdminDashboardLocaleText(locale, { en: "Manifest compare", ko: "manifest 비교" }),
    manifestCompareBody: pickAdminDashboardLocaleText(locale, {
      en: "Compare the issued bundle manifest against the same same-device header and artifact identity before handing this incident off.",
      ko: "이 incident를 넘기기 전에 발급된 bundle manifest가 동일 same-device header와 artifact identity를 유지하는지 비교합니다.",
    }),
    manifestCompareReady: pickAdminDashboardLocaleText(locale, { en: "Compare readiness", ko: "비교 준비도" }),
    photoCompareTitle: pickAdminDashboardLocaleText(locale, { en: "Photo compare", ko: "사진 비교" }),
    photoCompareBody: pickAdminDashboardLocaleText(locale, {
      en: "Keep the selected artifact pinned while you compare photo identity, original-download issuance, and latest trace before leaving this user route.",
      ko: "이 사용자 경로를 떠나기 전에 선택 artifact를 고정한 채 사진 identity, 원본 다운로드 발급, 최근 추적을 함께 비교합니다.",
    }),
    representativePhotoCompareBody: pickAdminDashboardLocaleText(locale, {
      en: "Use the representative photo as the primary baseline for photo identity, original-download issuance, and latest trace before switching to another corpus row.",
      ko: "다른 코퍼스 row로 이동하기 전에 대표 사진을 기준선으로 삼아 사진 identity, 원본 다운로드 발급, 최근 추적을 먼저 비교합니다.",
    }),
    selectedArtifactCompareBody: pickAdminDashboardLocaleText(locale, {
      en: "Use this incident-focused photo artifact as the primary baseline for preview or original-download parity before switching back to a broader corpus row.",
      ko: "더 넓은 코퍼스 row로 돌아가기 전에 이 incident-focused 사진 artifact를 미리보기 또는 원본 다운로드 parity의 1차 기준선으로 사용합니다.",
    }),
    photoReviewPackageTitle: pickAdminDashboardLocaleText(locale, {
      en: "Uploaded photo review package",
      ko: "업로드 사진 검토 패키지",
    }),
    photoReviewPackageBody: pickAdminDashboardLocaleText(locale, {
      en: "Keep the representative baseline, selected corpus row, preview or original-download parity, and latest trace in one structured package before you leave this photo workspace.",
      ko: "이 사진 작업면을 떠나기 전에 대표 기준선, 선택 코퍼스 row, 미리보기 또는 원본 다운로드 parity, 최근 추적을 하나의 구조화된 패키지에 함께 고정합니다.",
    }),
    photoReviewPackageSchemaTitle: pickAdminDashboardLocaleText(locale, {
      en: "Review package preview",
      ko: "검토 패키지 미리보기",
    }),
    photoReviewPackageSchemaBody: pickAdminDashboardLocaleText(locale, {
      en: "Engineering can reopen the same selected photo row, timeline context, and trace linkage from this JSON without rebuilding the operator's photo review order.",
      ko: "이 JSON만으로도 엔지니어링이 운영자의 사진 검토 순서를 다시 해석하지 않고 같은 선택 사진 row, 타임라인 문맥, 추적 연결을 다시 열 수 있어야 합니다.",
    }),
    photoCompareReady: pickAdminDashboardLocaleText(locale, { en: "Original parity", ko: "원본 parity" }),
    workspaceParity: pickAdminDashboardLocaleText(locale, { en: "Workspace parity", ko: "작업면 parity" }),
    producerPathNextAction: pickAdminDashboardLocaleText(locale, { en: "Producer path next action", ko: "producer 경로 다음 액션" }),
    exportHandoffPackage: pickAdminDashboardLocaleText(locale, { en: "Export handoff package", ko: "handoff 패키지 내보내기" }),
    exportPhotoReviewPackage: pickAdminDashboardLocaleText(locale, {
      en: "Export photo review package",
      ko: "사진 검토 패키지 내보내기",
    }),
    reviewTimeline: pickAdminDashboardLocaleText(locale, { en: "Timeline context", ko: "타임라인 문맥" }),
    reviewTimelineRows: pickAdminDashboardLocaleText(locale, { en: "Timeline rows", ko: "타임라인 row 수" }),
    reviewRepresentativeArtifact: pickAdminDashboardLocaleText(locale, {
      en: "Representative baseline",
      ko: "대표 기준선",
    }),
    reviewScenarioSummary: pickAdminDashboardLocaleText(locale, { en: "Scenario summary", ko: "시나리오 요약" }),
    reviewPackageNextAction: pickAdminDashboardLocaleText(locale, { en: "Review next action", ko: "검토 다음 액션" }),
    handoffStatus: pickAdminDashboardLocaleText(locale, { en: "Handoff status", ko: "handoff 상태" }),
    handoffRoute: pickAdminDashboardLocaleText(locale, { en: "Canonical route", ko: "기준 경로" }),
    handoffReopenRoute: pickAdminDashboardLocaleText(locale, { en: "Reopen route", ko: "재진입 경로" }),
    handoffPhotoArtifact: pickAdminDashboardLocaleText(locale, { en: "Photo artifact", ko: "사진 artifact" }),
    handoffPhotoDownload: pickAdminDashboardLocaleText(locale, { en: "Photo download", ko: "사진 다운로드" }),
    handoffLogArtifact: pickAdminDashboardLocaleText(locale, { en: "Log artifact", ko: "로그 artifact" }),
    handoffLogDownload: pickAdminDashboardLocaleText(locale, { en: "Log download", ko: "로그 다운로드" }),
    handoffTraceLink: pickAdminDashboardLocaleText(locale, { en: "Trace link", ko: "추적 링크" }),
    handoffFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Failed flow route", ko: "오류 흐름 경로" }),
    handoffRelation: pickAdminDashboardLocaleText(locale, { en: "Relation", ko: "관계" }),
    handoffPhotoContext: pickAdminDashboardLocaleText(locale, { en: "Photo context", ko: "사진 문맥" }),
    handoffPhotoCompare: pickAdminDashboardLocaleText(locale, { en: "Photo compare identity", ko: "사진 비교 식별자" }),
    handoffLogManifest: pickAdminDashboardLocaleText(locale, { en: "Log manifest summary", ko: "로그 manifest 요약" }),
    handoffSameDeviceHeader: pickAdminDashboardLocaleText(locale, { en: "Same-device header parity", ko: "동일 기기 header parity" }),
    handoffReplayState: pickAdminDashboardLocaleText(locale, { en: "Replay state", ko: "replay 상태" }),
    handoffReplayExcerpt: pickAdminDashboardLocaleText(locale, { en: "Excerpt replay contract", ko: "excerpt replay 계약" }),
    handoffBundleContract: pickAdminDashboardLocaleText(locale, { en: "Bundle contract", ko: "bundle 계약" }),
    handoffLineSchema: pickAdminDashboardLocaleText(locale, { en: "Payload line schema", ko: "payload line schema" }),
    handoffRootCauseWorkspace: pickAdminDashboardLocaleText(locale, { en: "Root-cause compare workspace", ko: "root-cause 비교 작업면" }),
    handoffBrowserDirectBaseline: pickAdminDashboardLocaleText(locale, { en: "Browser-direct baseline pack", ko: "browser-direct 기준선 팩" }),
    handoffRequestTraceLinkage: pickAdminDashboardLocaleText(locale, { en: "Request trace linkage", ko: "요청 추적 연결" }),
    handoffBrowserDirectState: pickAdminDashboardLocaleText(locale, { en: "Acceptance state", ko: "acceptance 상태" }),
    handoffChecklist: pickAdminDashboardLocaleText(locale, { en: "Verification checklist", ko: "검증 체크리스트" }),
    handoffOperatorNote: pickAdminDashboardLocaleText(locale, { en: "Operator note", ko: "운영자 메모" }),
    handoffNextAction: pickAdminDashboardLocaleText(locale, { en: "Next action", ko: "다음 액션" }),
    diagnosisNextAction: pickAdminDashboardLocaleText(locale, { en: "Diagnosis next action", ko: "진단 다음 액션" }),
    handoffReady: pickAdminDashboardLocaleText(locale, { en: "Photo and log evidence stay in one incident handoff package.", ko: "사진과 로그 증거가 하나의 incident handoff 패키지에 함께 유지됩니다." }),
    handoffNeedsJoin: pickAdminDashboardLocaleText(locale, { en: "Trace-join the selected photo and same-device log before handing this incident to engineering.", ko: "이 incident를 개발자에게 넘기기 전에 선택된 사진과 same-device 로그를 같은 추적으로 먼저 정렬하세요." }),
    handoffNeedsPhoto: pickAdminDashboardLocaleText(locale, { en: "Pin a photo artifact so the same-device log can be handed off with actual user-uploaded evidence.", ko: "same-device 로그를 실제 사용자 업로드 증거와 함께 넘길 수 있도록 사진 artifact를 먼저 고정하세요." }),
    handoffNeedsLog: pickAdminDashboardLocaleText(locale, { en: "Pin a same-device log artifact so photo evidence does not leave this route without device context.", ko: "사진 증거가 기기 문맥 없이 이 화면을 벗어나지 않도록 same-device 로그 artifact를 먼저 고정하세요." }),
    handoffUnavailable: pickAdminDashboardLocaleText(locale, { en: "No photo or same-device log artifact is pinned yet.", ko: "아직 고정된 사진 또는 same-device 로그 artifact가 없습니다." }),
    incidentPreviewBody: pickAdminDashboardLocaleText(locale, { en: "The bounded excerpt keeps the source unchanged and the excerpt redacted. Use it to understand the failure before falling back to raw request trace only.", ko: "bounded excerpt는 원문 변경 없이 redaction 경계를 유지합니다. raw request trace만 보기 전에 먼저 실패 맥락을 이해하는 데 사용합니다." }),
    noIncidentPreview: pickAdminDashboardLocaleText(locale, { en: "No bounded inline excerpt is available for this same-device artifact yet.", ko: "이 same-device artifact에는 아직 bounded inline excerpt가 없습니다." }),
    noIncidentLogs: pickAdminDashboardLocaleText(locale, { en: "No incident log artifacts", ko: "incident log artifact 없음" }),
    noIncidentLogsBody: pickAdminDashboardLocaleText(locale, { en: "No same-device incident log artifact inventory is available in this user detail snapshot.", ko: "현재 사용자 상세 스냅샷에 same-device incident log artifact inventory가 없습니다." }),
    linkedFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Linked failed flow", ko: "연결된 오류 흐름" }),
    linkedLifecycle: pickAdminDashboardLocaleText(locale, { en: "Linked lifecycle", ko: "연결된 라이프사이클" }),
    trace: pickAdminDashboardLocaleText(locale, { en: "Trace", ko: "추적" }),
    openTrace: pickAdminDashboardLocaleText(locale, { en: "Open trace", ko: "추적 열기" }),
    openLinkedFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Open linked failed flow", ko: "연결된 오류 흐름 열기" }),
    openLinkedLifecycle: pickAdminDashboardLocaleText(locale, { en: "Open linked lifecycle", ko: "연결된 라이프사이클 열기" }),
    openIncidentCompare: pickAdminDashboardLocaleText(locale, { en: "Open incident compare", ko: "incident 비교 열기" }),
    traceUnavailable: pickAdminDashboardLocaleText(locale, { en: "No deterministic request trace", ko: "확정된 요청 추적 없음" }),
    lifecycleUnavailable: pickAdminDashboardLocaleText(locale, { en: "No deterministic lifecycle trace", ko: "확정된 라이프사이클 추적 없음" }),
    inspect: pickAdminDashboardLocaleText(locale, { en: "Inspect", ko: "조사" }),
    selected: pickAdminDashboardLocaleText(locale, { en: "Selected", ko: "선택됨" }),
    selectedArtifactTitle: pickAdminDashboardLocaleText(locale, { en: "Selected artifact", ko: "선택된 artifact" }),
    selectedArtifactBody: pickAdminDashboardLocaleText(locale, { en: "Keep the selected corpus row fixed while you compare preview support, original download, trace linkage, and incident context.", ko: "선택한 코퍼스 row를 고정한 상태로 미리보기 지원, 원본 다운로드, 추적 연결, incident 문맥을 함께 비교합니다." }),
    noScenarioArtifacts: pickAdminDashboardLocaleText(locale, { en: "No artifacts match the selected scenario.", ko: "선택한 시나리오에 맞는 artifact가 없습니다." }),
    preview: pickAdminDashboardLocaleText(locale, { en: "Preview", ko: "미리보기" }),
    download: pickAdminDashboardLocaleText(locale, { en: "Download", ko: "다운로드" }),
    openArtifact: pickAdminDashboardLocaleText(locale, { en: "Open artifact", ko: "artifact 열기" }),
    accessUnsupported: pickAdminDashboardLocaleText(locale, { en: "This environment does not yet provide media access issuance.", ko: "이 환경은 아직 미디어 접근 발급을 제공하지 않습니다." }),
    accessLoading: pickAdminDashboardLocaleText(locale, { en: "Issuing secure artifact access...", ko: "보안 artifact 접근을 발급하는 중입니다..." }),
    accessAllowed: pickAdminDashboardLocaleText(locale, { en: "Secure access issued. Open the artifact in a new tab and compare it with the linked trace.", ko: "보안 접근이 발급되었습니다. 새 탭에서 artifact를 열고 연결된 추적과 비교하세요." }),
    accessMissing: pickAdminDashboardLocaleText(locale, { en: "The artifact could not be issued from the current delivery path. Follow the fallback and trace before retrying.", ko: "현재 전달 경로에서 artifact를 발급할 수 없습니다. 재시도 전에 fallback과 추적을 먼저 확인하세요." }),
    accessDenied: pickAdminDashboardLocaleText(locale, { en: "Access was denied by policy or role. Confirm the operator role boundary before retrying.", ko: "정책 또는 역할 경계로 접근이 거부됐습니다. 재시도 전에 운영자 역할 경계를 확인하세요." }),
    accessExpired: pickAdminDashboardLocaleText(locale, { en: "The short-lived grant expired before use. Issue a new grant from the same selected artifact.", ko: "단기 접근 권한이 사용 전에 만료됐습니다. 같은 선택 artifact에서 새 권한을 다시 발급하세요." }),
    accessUnknown: pickAdminDashboardLocaleText(locale, { en: "The artifact access state is unknown. Start from the linked request trace and supportability fallback.", ko: "artifact 접근 상태를 확정할 수 없습니다. 연결된 요청 추적과 지원 fallback부터 확인하세요." }),
    accessFailed: pickAdminDashboardLocaleText(locale, { en: "Artifact access issuance failed.", ko: "artifact 접근 발급에 실패했습니다." }),
    issuedAt: pickAdminDashboardLocaleText(locale, { en: "Issued at", ko: "발급 시각" }),
    accessAction: pickAdminDashboardLocaleText(locale, { en: "Access action", ko: "접근 동작" }),
    accessResult: pickAdminDashboardLocaleText(locale, { en: "Access result", ko: "접근 결과" }),
    deliverySource: pickAdminDashboardLocaleText(locale, { en: "Delivery source", ko: "전달 source" }),
    expiresAt: pickAdminDashboardLocaleText(locale, { en: "Expires at", ko: "만료 시각" }),
    latestTrace: pickAdminDashboardLocaleText(locale, { en: "Latest trace", ko: "최근 추적" }),
    traceLink: pickAdminDashboardLocaleText(locale, { en: "Trace link", ko: "추적 링크" }),
    fallback: pickAdminDashboardLocaleText(locale, { en: "Fallback", ko: "Fallback" }),
    openRepresentativeInWorkspace: pickAdminDashboardLocaleText(locale, { en: "Open in workspace", ko: "작업면에서 열기" }),
    representativeEntry: pickAdminDashboardLocaleText(locale, { en: "Representative entry", ko: "대표 항목" }),
    noRepresentativePhoto: pickAdminDashboardLocaleText(locale, { en: "No representative photo", ko: "대표 사진 없음" }),
    noRepresentativePhotoBody: pickAdminDashboardLocaleText(locale, { en: "No representative uploaded artifact is available in the current user detail snapshot.", ko: "현재 사용자 상세 스냅샷에 대표 업로드 artifact가 없습니다." }),
    noMediaArtifacts: pickAdminDashboardLocaleText(locale, { en: "No media artifacts", ko: "미디어 artifact 없음" }),
    noMediaArtifactsBody: pickAdminDashboardLocaleText(locale, { en: "No uploaded media artifact inventory is available for this user detail.", ko: "이 사용자 상세에 사용할 업로드 미디어 artifact inventory가 없습니다." }),
    fileId: pickAdminDashboardLocaleText(locale, { en: "File ID", ko: "파일 ID" }),
    purpose: pickAdminDashboardLocaleText(locale, { en: "Purpose", ko: "용도" }),
    contentType: pickAdminDashboardLocaleText(locale, { en: "Content Type", ko: "콘텐츠 유형" }),
    size: pickAdminDashboardLocaleText(locale, { en: "Size", ko: "크기" }),
    linked: pickAdminDashboardLocaleText(locale, { en: "Linked", ko: "연결 대상" }),
    lifecycleTrace: pickAdminDashboardLocaleText(locale, { en: "Lifecycle trace", ko: "수명주기 조사" }),
    lifecycleContext: pickAdminDashboardLocaleText(locale, { en: "Lifecycle context", ko: "수명주기 문맥" }),
    mediaReEntry: pickAdminDashboardLocaleText(locale, { en: "Media re-entry", ko: "미디어 재진입" }),
    openLifecycleFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Open lifecycle failed flow", ko: "수명주기 오류 흐름 열기" }),
    openLinkedArtifact: pickAdminDashboardLocaleText(locale, { en: "Open linked artifact", ko: "연결 artifact 열기" }),
    noLinkedArtifact: pickAdminDashboardLocaleText(locale, { en: "No linked artifact", ko: "연결 artifact 없음" }),
    noDeterministicLifecycleTrace: pickAdminDashboardLocaleText(locale, { en: "No deterministic lifecycle trace", ko: "확정된 수명주기 조사 없음" }),
    upstreamTrace: pickAdminDashboardLocaleText(locale, { en: "Upstream trace", ko: "상위 요청 추적" }),
    openUpstreamFailedFlow: pickAdminDashboardLocaleText(locale, { en: "Open upstream failed flow", ko: "상위 오류 흐름 열기" }),
    openJobs: pickAdminDashboardLocaleText(locale, { en: "Open jobs", ko: "작업 열기" }),
    confirmed: pickAdminDashboardLocaleText(locale, { en: "Confirmed", ko: "확정 시각" }),
    expires: pickAdminDashboardLocaleText(locale, { en: "Expires", ko: "만료 시각" }),
    storageDeleted: pickAdminDashboardLocaleText(locale, { en: "Storage deleted", ko: "스토리지 삭제 시각" }),
    operatorHint: pickAdminDashboardLocaleText(locale, { en: "Operator hint", ko: "운영자 힌트" }),
    noFiles: pickAdminDashboardLocaleText(locale, { en: "No files", ko: "파일 없음" }),
    noFilesBody: pickAdminDashboardLocaleText(locale, { en: "No linked file data.", ko: "연결된 파일 데이터가 없습니다." }),
    aiJobs: pickAdminDashboardLocaleText(locale, { en: "AI Jobs", ko: "AI 작업" }),
    aiJobsBody: pickAdminDashboardLocaleText(locale, { en: "Review failed jobs, providers, and linked file or record context.", ko: "실패 작업, 제공자, 연결된 파일 또는 기록 문맥을 운영 관점에서 확인합니다." }),
    jobId: pickAdminDashboardLocaleText(locale, { en: "Job ID", ko: "작업 ID" }),
    attempts: pickAdminDashboardLocaleText(locale, { en: "Attempts", ko: "시도 횟수" }),
    lastError: pickAdminDashboardLocaleText(locale, { en: "Last Error", ko: "마지막 오류" }),
    provider: pickAdminDashboardLocaleText(locale, { en: "Provider", ko: "제공자" }),
    queued: pickAdminDashboardLocaleText(locale, { en: "Queued", ko: "대기 시각" }),
    started: pickAdminDashboardLocaleText(locale, { en: "Started", ko: "시작 시각" }),
    finished: pickAdminDashboardLocaleText(locale, { en: "Finished", ko: "완료 시각" }),
    duration: pickAdminDashboardLocaleText(locale, { en: "Duration", ko: "소요 시간" }),
    file: pickAdminDashboardLocaleText(locale, { en: "File", ko: "파일" }),
    noAiJobs: pickAdminDashboardLocaleText(locale, { en: "No AI jobs", ko: "AI 작업 없음" }),
    noAiJobsBody: pickAdminDashboardLocaleText(locale, { en: "No linked AI job data.", ko: "연결된 AI 작업 데이터가 없습니다." }),
  };

  const renderPhotoCompareBlock = (input: {
    body: string;
    nextActionLabel: string | undefined;
    summary: IncidentPhotoCompareSummary | undefined;
    workspaceParityLabel?: string;
  }) => (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: "16px",
        background: "rgba(255, 255, 255, 0.92)",
        border: "1px solid rgba(148, 163, 184, 0.2)",
        display: "grid",
        gap: "10px",
      }}
    >
      <strong>{copy.photoCompareTitle}</strong>
      <span style={{ color: "#475569", lineHeight: 1.6 }}>{input.body}</span>
      <div
        style={{
          display: "grid",
          gap: "10px",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        }}
      >
        <div>
          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoCompareReady}</div>
          <div>{input.summary?.compareReadyLabel ?? copy.handoffUnavailable}</div>
        </div>
        <div>
          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.download}</div>
          <div>{input.summary?.originalDownloadIssueLabel ?? copy.handoffUnavailable}</div>
        </div>
        <div>
          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.deliverySource}</div>
          <div>{input.summary?.deliverySourceLabel ?? copy.handoffUnavailable}</div>
        </div>
        {input.workspaceParityLabel ? (
          <div>
            <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.workspaceParity}</div>
            <div>{input.workspaceParityLabel}</div>
          </div>
        ) : null}
      </div>
      <div style={{ display: "grid", gap: "4px" }}>
        {input.summary
          ? input.summary.parityRows.map((row) => (
              <span key={`photo_compare_${row.key}_${input.body}`}>{`${row.label}: ${row.value}`}</span>
            ))
          : <span>{copy.handoffUnavailable}</span>}
      </div>
      {input.summary ? (
        <div style={{ display: "grid", gap: "4px" }}>
          {input.summary.accessIssuanceRows.map((row) => (
            <span key={`photo_compare_access_${row.key}_${input.body}`}>{`${row.label}: ${row.value}`}</span>
          ))}
        </div>
      ) : null}
      <div>
        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.nextAction}</div>
        <div>{input.nextActionLabel ?? copy.handoffUnavailable}</div>
      </div>
    </div>
  );

  const mediaAccessFeedback =
    mediaAccessState.status === "idle"
      ? undefined
      : mediaAccessState.status === "loading"
        ? copy.accessLoading
        : mediaAccessState.status === "error"
          ? `${copy.accessFailed} ${mediaAccessState.requestId ? `(${mediaAccessState.requestId})` : ""}`.trim()
          : mediaAccessState.result === "allowed"
            ? copy.accessAllowed
            : mediaAccessState.result === "missing"
              ? copy.accessMissing
              : mediaAccessState.result === "denied_policy" || mediaAccessState.result === "denied_role"
                ? copy.accessDenied
                : mediaAccessState.result === "expired"
                  ? copy.accessExpired
                  : copy.accessUnknown;
  const incidentAccessFeedback =
    incidentAccessState.status === "idle"
      ? undefined
      : incidentAccessState.status === "loading"
        ? copy.accessLoading
        : incidentAccessState.status === "error"
          ? `${copy.accessFailed} ${
              incidentAccessState.requestId ? `(${incidentAccessState.requestId})` : ""
            }`.trim()
          : incidentAccessState.result === "allowed"
            ? copy.accessAllowed
            : incidentAccessState.result === "missing"
              ? copy.accessMissing
              : incidentAccessState.result === "denied_policy" ||
                  incidentAccessState.result === "denied_role"
                ? copy.accessDenied
                : incidentAccessState.result === "expired"
                  ? copy.accessExpired
                  : copy.accessUnknown;

  async function issueMediaAccess(
    artifactId: string,
    action: AdminMediaArtifactAccessAction,
  ): Promise<void> {
    const issueAccess = props.dependencies.queryPort.issueUserMediaArtifactAccess;

    if (!params.internalUserId) {
      return;
    }

    if (typeof issueAccess !== "function") {
      setMediaAccessState({
        artifactId,
        action,
        status: "ready",
        result: "unknown",
      });
      return;
    }

    setMediaAccessState({
      artifactId,
      action,
      status: "loading",
    });

    try {
      const response = await issueAccess({
        internalUserId: params.internalUserId,
        artifactId,
        action,
      });
      const nextState: MediaAccessState = {
        artifactId,
        action,
        status: "ready",
        result: response.data.accessResult,
        expiresAt: response.data.expiresAt,
        issuedAt: response.data.issuedAt,
        requestId: response.meta.requestId,
        url: response.data.url,
      };
      setMediaAccessState(nextState);

      if (response.data.accessResult === "allowed") {
        openGrantedArtifact(response.data.url);
      }
    } catch (error) {
      setMediaAccessState({
        artifactId,
        action,
        status: "error",
        requestId:
          typeof error === "object" && error !== null && "requestId" in error
            ? String(Reflect.get(error, "requestId"))
            : undefined,
      });
    }
  }

  async function issueIncidentLogAccess(
    artifactId: string,
    action: AdminIncidentLogArtifactAccessAction,
  ): Promise<void> {
    const issueAccess = props.dependencies.queryPort.issueUserIncidentLogArtifactAccess;

    if (!params.internalUserId) {
      return;
    }

    if (typeof issueAccess !== "function") {
      setIncidentAccessState({
        artifactId,
        action,
        status: "ready",
        result: "unknown",
      });
      return;
    }

    setIncidentAccessState({
      artifactId,
      action,
      status: "loading",
    });

    try {
      const response = await issueAccess({
        internalUserId: params.internalUserId,
        artifactId,
        action,
      });
      const nextState: IncidentLogAccessState = {
        artifactId,
        action,
        status: "ready",
        result: response.data.accessResult,
        expiresAt: response.data.expiresAt,
        issuedAt: response.data.issuedAt,
        requestId: response.meta.requestId,
        url: response.data.url,
      };
      setIncidentAccessState(nextState);

      if (response.data.accessResult === "allowed") {
        openGrantedArtifact(response.data.url);
      }
    } catch (error) {
      setIncidentAccessState({
        artifactId,
        action,
        status: "error",
        requestId:
          typeof error === "object" && error !== null && "requestId" in error
            ? String(Reflect.get(error, "requestId"))
            : undefined,
      });
    }
  }

  return (
    <section style={{ display: "grid", gap: "18px" }}>
      <header style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
            <h3 style={{ margin: "8px 0 0", fontSize: "1.5rem", letterSpacing: "-0.03em" }}>
              {copy.title}
            </h3>
          </div>
          <AdminLocalizedLink style={{ color: "#0f766e", fontWeight: 700 }} to="/admin/users">
            {copy.back}
          </AdminLocalizedLink>
        </div>
      </header>

      {state.status === "loading" || state.status === "idle" ? (
        <section style={cardStyle}>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
          <p style={{ margin: "10px 0 0", color: "#475569" }}>{copy.loading}</p>
        </section>
      ) : null}

      {state.status === "permission_denied" ? (
        <AdminErrorState
          title={copy.deniedTitle}
          body={copy.deniedBody}
          guidance={{
            screen: "users_detail",
            slot: "shared_error_state_guidance",
          }}
          meta={buildUserDataDetailStateMeta(locale, params.internalUserId)}
          requestId={state.error.requestId}
          tone="permission_denied"
        />
      ) : null}

      {state.status === "error" ? (
        <AdminErrorState
          title={copy.failedTitle}
          body={state.error.message}
          guidance={{
            screen: "users_detail",
            slot: "shared_error_state_guidance",
          }}
          meta={buildUserDataDetailStateMeta(locale, params.internalUserId)}
          requestId={state.error.requestId}
          tone="error"
        />
      ) : null}

      {state.status === "empty" ? (
        <AdminEmptyState
          title={copy.emptyTitle}
          body={
            params.internalUserId
              ? `${copy.emptyBody}${params.internalUserId}`
              : copy.emptyNoParam
          }
          guidance={{
            screen: "users_detail",
            slot: "shared_empty_state_guidance",
          }}
          meta={buildUserDataDetailStateMeta(locale, params.internalUserId)}
        />
      ) : null}

      {state.status === "ready" || state.status === "partial" || state.status === "stale" ? (
        <>
          <section
            style={{
              ...cardStyle,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.35fr) minmax(280px, 0.85fr)",
              gap: "18px",
            }}
          >
            <div>
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.82rem", fontWeight: 700 }}>
                {copy.eyebrow}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "1.3rem", fontWeight: 700 }}>
                {state.viewModel.userIdHash}
              </p>
              <div style={{ display: "grid", gap: "4px", marginTop: "10px" }}>
                <span style={{ color: "#475569", fontSize: "0.92rem" }}>
                  {copy.accountCue}: {state.viewModel.accountIdentityLabel}
                </span>
                <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                  {copy.providerCue}: {state.viewModel.providerIdentityLabel}
                </span>
                <span style={{ color: "#94a3b8", fontSize: "0.84rem" }}>
                  {copy.linkedProviders}: {state.viewModel.linkedProviderCountLabel}
                </span>
              </div>
              <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6 }}>
                {copy.description}
              </p>
              <AdminOperatorExplanationBlock
                screen="users_detail"
                slot="users_detail_top_summary"
                style={{ marginTop: "14px" }}
              />
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "14px" }}>
                <AdminStatusBadge value={state.status} />
                <AdminStatusBadge
                  value={toAdminFreshnessBadgeValue(state.viewModel.freshness)}
                />
                <AdminStatusBadge
                  value={toAdminRiskBadgeValue(state.viewModel.riskSummary.highestLevel)}
                />
                <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                  {copy.internal}: {state.viewModel.internalUserId}
                </span>
              </div>
            </div>

            <AdminDetailPanel
              title={copy.detailSnapshot}
              sections={[
                {
                  title: copy.metadata,
                  items: [
                    {
                      key: "freshness",
                      label: copy.freshness,
                      value: <AdminStatusBadge value={toAdminFreshnessBadgeValue(state.viewModel.freshness)} />,
                    },
                    { key: "accountCue", label: copy.accountCue, value: state.viewModel.accountIdentityLabel },
                    { key: "providerCue", label: copy.providerCue, value: state.viewModel.providerIdentityLabel },
                    { key: "linkedProviders", label: copy.linkedProviders, value: state.viewModel.linkedProviderCountLabel },
                    { key: "release", label: copy.release, value: state.viewModel.latestReleaseLabel },
                    { key: "appVersion", label: copy.app, value: state.viewModel.latestAppVersionLabel },
                    { key: "generatedAt", label: copy.generated, value: state.viewModel.generatedAtLabel },
                    { key: "requestId", label: copy.request, value: state.viewModel.requestId },
                  ],
                },
              ]}
            />
          </section>

          <div
            style={{
              display: "grid",
              gap: "12px",
            }}
          >
            <div style={{ display: "grid", gap: "8px" }}>
              <div style={{ display: "grid", gap: "6px" }}>
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {copy.currentEvidenceTitle}
                </p>
                <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>
                  {copy.currentEvidenceDesc}
                </p>
              </div>
              <AdminOperatorExplanationBlock
                screen="users_detail"
                slot="users_detail_risk_summary"
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.9fr)",
                gap: "16px",
                alignItems: "start",
              }}
            >
              <AdminDetailPanel
                title={copy.accountDeletion}
                sections={[
                  {
                    title: copy.topSummary,
                    items: [
                      {
                        key: "severity",
                        label: copy.severity,
                        value: (
                          <AdminStatusBadge
                            value={toAdminRiskBadgeValue(state.viewModel.accountDeletionSummary.severity)}
                          />
                        ),
                      },
                      { key: "status", label: copy.status, value: state.viewModel.accountDeletionSummary.statusLabel },
                      {
                        key: "requestedAt",
                        label: copy.requested,
                        value: state.viewModel.accountDeletionSummary.requestedAtLabel,
                      },
                      {
                        key: "completedAt",
                        label: copy.completed,
                        value: state.viewModel.accountDeletionSummary.completedAtLabel,
                      },
                      {
                        key: "primaryAction",
                        label: copy.primary,
                        value:
                          state.viewModel.accountDeletionSummary.primaryActionLabel &&
                          state.viewModel.accountDeletionSummary.primaryActionTo ? (
                            <AdminLocalizedLink to={state.viewModel.accountDeletionSummary.primaryActionTo}>
                              {state.viewModel.accountDeletionSummary.primaryActionLabel}
                            </AdminLocalizedLink>
                          ) : (
                            "-"
                          ),
                      },
                      {
                        key: "jobs",
                        label: copy.jobs,
                        value: (
                          <AdminLocalizedLink to={state.viewModel.accountDeletionSummary.jobsDrilldownTo}>
                            {copy.openDeletionJobs}
                          </AdminLocalizedLink>
                        ),
                      },
                    ],
                  },
                ]}
                footer={
                  <div style={{ display: "grid", gap: "12px" }}>
                    <div
                      style={{
                        padding: "12px 14px",
                        borderRadius: "16px",
                        background: "rgba(255, 247, 237, 0.72)",
                        border: "1px solid rgba(251, 146, 60, 0.18)",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                        <strong>{state.viewModel.accountDeletionSummary.headline}</strong>
                        <AdminStatusBadge
                          value={toAdminRiskBadgeValue(state.viewModel.accountDeletionSummary.severity)}
                          label={state.viewModel.accountDeletionSummary.statusLabel}
                        />
                      </div>
                      <p style={{ margin: "8px 0 0", color: "#475569", lineHeight: 1.6 }}>
                        {state.viewModel.accountDeletionSummary.summary}
                      </p>
                      <p style={{ margin: "10px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
                        {state.viewModel.accountDeletionSummary.investigationHint}
                      </p>
                    </div>
                    {state.viewModel.accountDeletionSummary.latestFailure ? (
                      <div
                        style={{
                          padding: "12px 14px",
                          borderRadius: "16px",
                          background: "rgba(248, 250, 252, 0.95)",
                          border: "1px solid rgba(148, 163, 184, 0.2)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                          <strong>{state.viewModel.accountDeletionSummary.latestFailure.errorCode}</strong>
                          <span style={{ color: "#64748b", fontSize: "0.86rem" }}>
                            {state.viewModel.accountDeletionSummary.latestFailure.occurredAtLabel}
                          </span>
                        </div>
                        <p style={{ margin: "8px 0 0", fontWeight: 600 }}>
                          {state.viewModel.accountDeletionSummary.latestFailure.feature} /{" "}
                          {state.viewModel.accountDeletionSummary.latestFailure.useCase}
                        </p>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "8px" }}>
                          {state.viewModel.accountDeletionSummary.latestFailure.requestId ? (
                            <span style={{ color: "#64748b", fontSize: "0.86rem" }}>
                              {copy.reqShort}: {state.viewModel.accountDeletionSummary.latestFailure.requestId}
                            </span>
                          ) : null}
                          {state.viewModel.accountDeletionSummary.latestFailure.correlationId ? (
                            <span style={{ color: "#64748b", fontSize: "0.86rem" }}>
                              {copy.corrShort}: {state.viewModel.accountDeletionSummary.latestFailure.correlationId}
                            </span>
                          ) : null}
                          {state.viewModel.accountDeletionSummary.latestFailure.drilldownTo ? (
                            <AdminLocalizedLink to={state.viewModel.accountDeletionSummary.latestFailure.drilldownTo}>
                              {copy.openDeletionFailedFlow}
                            </AdminLocalizedLink>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                }
              />

              <AdminDetailPanel
                title={copy.riskSummary}
                sections={[
                  {
                    title: copy.overview,
                    items: [
                      {
                        key: "freshness",
                        label: copy.freshness,
                        value: (
                          <AdminStatusBadge value={toAdminFreshnessBadgeValue(state.viewModel.freshness)} />
                        ),
                      },
                      {
                        key: "highestLevel",
                        label: copy.risk,
                        value: (
                          <AdminStatusBadge
                            value={toAdminRiskBadgeValue(state.viewModel.riskSummary.highestLevel)}
                          />
                        ),
                      },
                      { key: "signalCount", label: copy.signals, value: String(state.viewModel.riskSummary.items.length) },
                    ],
                  },
                ]}
                footer={
                  state.viewModel.riskSummary.items.length > 0 ? (
                    <div style={{ display: "grid", gap: "10px" }}>
                      {state.viewModel.riskSummary.items.map((item) => (
                        <div
                          key={`${item.code}:${item.evidence}`}
                          style={{
                            padding: "12px 14px",
                            borderRadius: "16px",
                            background: "rgba(248, 250, 252, 0.95)",
                            border: "1px solid rgba(148, 163, 184, 0.2)",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                            <strong>{item.label}</strong>
                            <AdminStatusBadge value={toAdminRiskBadgeValue(item.level)} />
                          </div>
                          <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
                            {copy.evidence}: {item.evidence}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: 0, color: "#64748b" }}>{copy.noRiskSignals}</p>
                  )
                }
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gap: "16px",
            }}
          >
            <div style={{ display: "grid", gap: "8px" }}>
              <div style={{ display: "grid", gap: "6px" }}>
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {copy.dailyEvidenceTitle}
                </p>
                <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>
                  {copy.dailyEvidenceDesc}
                </p>
              </div>
              <AdminOperatorExplanationBlock
                screen="users_detail"
                slot="users_detail_daily_triage"
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "14px",
              }}
            >
              {state.viewModel.counts.map((item) => (
                <article key={item.key} style={cardStyle}>
                  <p
                    style={{
                      margin: 0,
                      color: "#64748b",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    {item.label}
                  </p>
                  <p
                    style={{
                      margin: "10px 0 0",
                      fontSize: "1.45rem",
                      fontWeight: 700,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    {item.valueLabel}
                  </p>
                </article>
              ))}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.8fr)",
              gap: "16px",
              alignItems: "start",
            }}
          >
            <AdminDetailPanel
              title={copy.dailyRecordTriage}
              sections={[
                {
                  title: copy.dailyScenario,
                  items: [
                    {
                      key: "riskLevel",
                      label: copy.risk,
                      value: (
                        <AdminStatusBadge
                          value={toAdminRiskBadgeValue(state.viewModel.dailyRecordTriage.riskLevel)}
                        />
                      ),
                    },
                    { key: "dailyRecordCount", label: copy.dailyRecords, value: state.viewModel.dailyRecordTriage.dailyRecordCountLabel },
                    { key: "recentFailureCount", label: copy.dailyFailures, value: state.viewModel.dailyRecordTriage.recentFailureCountLabel },
                    { key: "recentSaveCount", label: copy.dailySaves, value: state.viewModel.dailyRecordTriage.recentSuccessfulSaveCountLabel },
                    {
                      key: "latestFailure",
                      label: copy.latestDailyFailure,
                      value: state.viewModel.dailyRecordTriage.latestFailure?.useCase ?? "-",
                    },
                    {
                      key: "latestSave",
                      label: copy.latestDailySave,
                      value: state.viewModel.dailyRecordTriage.latestSuccessfulSave?.event ?? "-",
                    },
                  ],
                },
              ]}
              footer={
                <div style={{ display: "grid", gap: "12px" }}>
                  <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>
                    {state.viewModel.dailyRecordTriage.summary}
                  </p>
                  {state.viewModel.dailyRecordTriage.latestFailure ? (
                    <div
                      style={{
                        padding: "12px 14px",
                        borderRadius: "16px",
                        background: "rgba(255, 247, 237, 0.72)",
                        border: "1px solid rgba(251, 146, 60, 0.18)",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                        <strong>{state.viewModel.dailyRecordTriage.latestFailure.errorCode}</strong>
                        <span style={{ color: "#64748b", fontSize: "0.86rem" }}>
                          {state.viewModel.dailyRecordTriage.latestFailure.occurredAtLabel}
                        </span>
                      </div>
                      <p style={{ margin: "8px 0 0", fontWeight: 600 }}>
                        {state.viewModel.dailyRecordTriage.latestFailure.feature} / {state.viewModel.dailyRecordTriage.latestFailure.useCase}
                      </p>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "8px" }}>
                        {state.viewModel.dailyRecordTriage.latestFailure.requestId ? (
                          <span style={{ color: "#64748b", fontSize: "0.86rem" }}>
                            {copy.reqShort}: {state.viewModel.dailyRecordTriage.latestFailure.requestId}
                          </span>
                        ) : null}
                        {state.viewModel.dailyRecordTriage.latestFailure.correlationId ? (
                          <span style={{ color: "#64748b", fontSize: "0.86rem" }}>
                            {copy.corrShort}: {state.viewModel.dailyRecordTriage.latestFailure.correlationId}
                          </span>
                        ) : null}
                        {state.viewModel.dailyRecordTriage.latestFailure.drilldownTo ? (
                          <AdminLocalizedLink to={state.viewModel.dailyRecordTriage.latestFailure.drilldownTo}>
                            {copy.openDailyFailedFlow}
                          </AdminLocalizedLink>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  {state.viewModel.dailyRecordTriage.latestSuccessfulSave ? (
                    <div
                      style={{
                        padding: "12px 14px",
                        borderRadius: "16px",
                        background: "rgba(240, 253, 250, 0.8)",
                        border: "1px solid rgba(20, 184, 166, 0.16)",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                        <strong>{state.viewModel.dailyRecordTriage.latestSuccessfulSave.event}</strong>
                        <span style={{ color: "#64748b", fontSize: "0.86rem" }}>
                          {state.viewModel.dailyRecordTriage.latestSuccessfulSave.occurredAtLabel}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "8px" }}>
                        {state.viewModel.dailyRecordTriage.latestSuccessfulSave.recordType ? (
                          <span style={{ color: "#64748b", fontSize: "0.86rem" }}>
                            {copy.type}: {state.viewModel.dailyRecordTriage.latestSuccessfulSave.recordType}
                          </span>
                        ) : null}
                        {state.viewModel.dailyRecordTriage.latestSuccessfulSave.recordId ? (
                          <span style={{ color: "#64748b", fontSize: "0.86rem" }}>
                            {copy.record}: {state.viewModel.dailyRecordTriage.latestSuccessfulSave.recordId}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              }
            />

            <AdminDetailPanel
              title={copy.recentFailures}
              sections={[
                {
                  title: copy.latestOverall,
                  items: [
                    { key: "feature", label: copy.feature, value: state.viewModel.latestFailure?.feature ?? "-" },
                    { key: "useCase", label: copy.useCase, value: state.viewModel.latestFailure?.useCase ?? "-" },
                    { key: "errorCode", label: copy.error, value: state.viewModel.latestFailure?.errorCode ?? "-" },
                    { key: "occurredAt", label: copy.occurred, value: state.viewModel.latestFailure?.occurredAtLabel ?? "-" },
                    {
                      key: "drilldown",
                      label: copy.drilldown,
                      value:
                        state.viewModel.latestFailure?.drilldownTo ? (
                          <AdminLocalizedLink to={state.viewModel.latestFailure.drilldownTo}>
                            {copy.openFailedFlow}
                          </AdminLocalizedLink>
                        ) : (
                          "-"
                        ),
                    },
                  ],
                },
              ]}
              footer={
                state.viewModel.recentFailures.length > 0 ? (
                  <div style={{ display: "grid", gap: "10px" }}>
                    {state.viewModel.recentFailures.map((failure) => (
                      <div
                        key={failure.id}
                        style={{
                          padding: "12px 14px",
                          borderRadius: "16px",
                          background: "rgba(255, 247, 237, 0.72)",
                          border: "1px solid rgba(251, 146, 60, 0.18)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                          <strong>{failure.errorCode}</strong>
                          <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{failure.occurredAtLabel}</span>
                        </div>
                        <p style={{ margin: "8px 0 0", fontWeight: 600 }}>
                          {failure.feature} / {failure.useCase}
                        </p>
                        {failure.contextLabels.length > 0 ? (
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>
                            {failure.contextLabels.map((label) => (
                              <span
                                key={label}
                                style={{
                                  borderRadius: "999px",
                                  background: "rgba(255, 255, 255, 0.88)",
                                  border: "1px solid rgba(251, 146, 60, 0.28)",
                                  color: "#9a3412",
                                  padding: "4px 10px",
                                  fontSize: "0.82rem",
                                  fontWeight: 600,
                                }}
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "8px" }}>
                          {failure.requestId ? <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.reqShort}: {failure.requestId}</span> : null}
                          {failure.correlationId ? <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.corrShort}: {failure.correlationId}</span> : null}
                          {failure.drilldownTo ? <AdminLocalizedLink to={failure.drilldownTo}>{copy.open}</AdminLocalizedLink> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: "#64748b" }}>{copy.noRecentFailures}</p>
                )
              }
            />

            <div style={{ display: "grid", gap: "16px" }}>
              <AdminDetailPanel
                title={copy.latestPetFailure}
                sections={[
                  {
                    title: copy.petFlow,
                    items: [
                      { key: "feature", label: copy.feature, value: state.viewModel.latestPetFailure?.feature ?? "-" },
                      { key: "useCase", label: copy.useCase, value: state.viewModel.latestPetFailure?.useCase ?? "-" },
                      { key: "errorCode", label: copy.error, value: state.viewModel.latestPetFailure?.errorCode ?? "-" },
                      { key: "occurredAt", label: copy.occurred, value: state.viewModel.latestPetFailure?.occurredAtLabel ?? "-" },
                      {
                        key: "drilldown",
                        label: copy.drilldown,
                        value:
                          state.viewModel.latestPetFailure?.drilldownTo ? (
                            <AdminLocalizedLink to={state.viewModel.latestPetFailure.drilldownTo}>
                              {copy.openPetFailedFlow}
                            </AdminLocalizedLink>
                          ) : (
                            "-"
                          ),
                      },
                    ],
                  },
                ]}
                footer={
                  state.viewModel.latestPetFailure?.requestId || state.viewModel.latestPetFailure?.correlationId ? (
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      {state.viewModel.latestPetFailure?.requestId ? (
                        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                          {copy.reqShort}: {state.viewModel.latestPetFailure.requestId}
                        </span>
                      ) : null}
                      {state.viewModel.latestPetFailure?.correlationId ? (
                        <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                          {copy.corrShort}: {state.viewModel.latestPetFailure.correlationId}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <p style={{ margin: 0, color: "#64748b" }}>{copy.noRecentPetFailure}</p>
                  )
                }
              />

              <AdminDetailPanel
                title={copy.recentSaves}
                sections={[
                  {
                    title: copy.latestOverall,
                    items: [
                      { key: "event", label: copy.event, value: state.viewModel.latestSuccessfulSave?.event ?? "-" },
                      { key: "occurredAt", label: copy.occurred, value: state.viewModel.latestSuccessfulSave?.occurredAtLabel ?? "-" },
                      { key: "recordType", label: copy.type, value: state.viewModel.latestSuccessfulSave?.recordType ?? "-" },
                      { key: "recordId", label: copy.record, value: state.viewModel.latestSuccessfulSave?.recordId ?? "-" },
                      { key: "receiptId", label: copy.receipt, value: state.viewModel.latestSuccessfulSave?.receiptId ?? "-" },
                    ],
                  },
                ]}
                footer={
                  state.viewModel.recentSuccessfulSaves.length > 0 ? (
                    <div style={{ display: "grid", gap: "10px" }}>
                      {state.viewModel.recentSuccessfulSaves.map((save) => (
                        <div
                          key={save.id}
                          style={{
                            padding: "12px 14px",
                            borderRadius: "16px",
                            background: "rgba(240, 253, 250, 0.8)",
                            border: "1px solid rgba(20, 184, 166, 0.16)",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                            <strong>{save.event}</strong>
                            <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{save.occurredAtLabel}</span>
                        </div>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "8px" }}>
                          {save.recordType ? <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.type}: {save.recordType}</span> : null}
                          {save.recordId ? <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.record}: {save.recordId}</span> : null}
                          {save.receiptId ? <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.receipt}: {save.receiptId}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: "#64748b" }}>{copy.noRecentSaves}</p>
                )
              }
            />

              <AdminDetailPanel
                title={copy.account}
                sections={[
                  {
                    title: copy.accountMetadata,
                    items: state.viewModel.account.map((item) => ({
                      key: item.key,
                      label: item.label,
                      value: item.value,
                    })),
                  },
                ]}
              />
            </div>
          </div>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.pets, copy.petsBody)}
            <AdminDataTable
              columns={[
                { id: "petId", label: copy.petId, renderCell: (row) => row.petId },
                { id: "petIdHash", label: copy.petHash, renderCell: (row) => row.petIdHash },
                { id: "createdAt", label: copy.created, renderCell: (row) => row.createdAtLabel },
                { id: "species", label: copy.species, renderCell: (row) => row.speciesLabel },
                { id: "profileStatus", label: copy.profile, renderCell: (row) => row.profileStatusLabel },
                { id: "photoStatus", label: copy.photo, renderCell: (row) => row.photoFileLinkStatusLabel },
                { id: "recordCount", label: copy.records, renderCell: (row) => row.recordCountLabel, align: "right" },
                { id: "deletedAt", label: copy.deleted, renderCell: (row) => row.deletedAtLabel },
              ]}
              emptyState={<AdminEmptyState title={copy.noPets} body={copy.noPetsBody} />}
              getRowId={(row) => row.petId}
              rows={state.viewModel.pets}
            />
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.recordsTitle, copy.recordsBody)}
            <AdminDataTable
              columns={[
                { id: "recordId", label: copy.recordId, renderCell: (row) => row.recordId },
                { id: "type", label: copy.type, renderCell: (row) => row.typeLabel },
                { id: "title", label: copy.titleLabel, renderCell: (row) => row.titleLabel },
                { id: "recordedAt", label: copy.recorded, renderCell: (row) => row.recordedAtLabel },
                { id: "createdAt", label: copy.created, renderCell: (row) => row.createdAtLabel },
                { id: "updatedAt", label: copy.updated, renderCell: (row) => row.updatedAtLabel },
                { id: "deletedAt", label: copy.deleted, renderCell: (row) => row.deletedAtLabel },
                { id: "linkedFileCount", label: copy.files, renderCell: (row) => row.linkedFileCountLabel, align: "right" },
                { id: "latestAiJobStatus", label: copy.aiJob, renderCell: (row) => row.latestAiJobStatusLabel },
                { id: "dataCompleteness", label: copy.completeness, renderCell: (row) => row.dataCompletenessLabel },
              ]}
              emptyState={<AdminEmptyState title={copy.noRecords} body={copy.noRecordsBody} />}
              getRowId={(row) => row.recordId}
              rows={state.viewModel.records}
            />
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.receipts, copy.receiptsBody)}
            <AdminDataTable
              columns={[
                { id: "receiptId", label: copy.receiptId, renderCell: (row) => row.receiptId },
                { id: "purchasedAt", label: copy.purchased, renderCell: (row) => row.purchasedAtLabel },
                { id: "amountBucket", label: copy.amount, renderCell: (row) => row.amountBucketLabel },
                { id: "category", label: copy.category, renderCell: (row) => row.categoryLabel },
                { id: "createdAt", label: copy.created, renderCell: (row) => row.createdAtLabel },
                { id: "updatedAt", label: copy.updated, renderCell: (row) => row.updatedAtLabel },
                { id: "deletedAt", label: copy.deleted, renderCell: (row) => row.deletedAtLabel },
                { id: "extractionStatus", label: copy.extraction, renderCell: (row) => row.extractionStatusLabel },
              ]}
              emptyState={<AdminEmptyState title={copy.noReceipts} body={copy.noReceiptsBody} />}
              getRowId={(row) => row.receiptId}
              rows={state.viewModel.receipts}
            />
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.filesTitle, copy.filesBody)}
            <AdminDataTable
              columns={[
                { id: "fileId", label: copy.fileId, renderCell: (row) => row.fileId },
                { id: "purpose", label: copy.purpose, renderCell: (row) => row.purposeLabel },
                { id: "status", label: copy.status, renderCell: (row) => row.statusLabel },
                { id: "contentType", label: copy.contentType, renderCell: (row) => row.contentTypeLabel },
                { id: "sizeBucket", label: copy.size, renderCell: (row) => row.sizeBucketLabel },
                { id: "linkedEntity", label: copy.linked, renderCell: (row) => row.linkedEntityLabel },
                {
                  id: "lifecycleTrace",
                  label: copy.lifecycleTrace,
                  renderCell: (row) => (
                    <div style={{ display: "grid", gap: "6px" }}>
                      <span>{row.latestLifecycleRequestIdLabel}</span>
                      {row.lifecycleFailedFlowDrilldownTo ? (
                        <AdminLocalizedLink to={row.lifecycleFailedFlowDrilldownTo}>{copy.openLifecycleFailedFlow}</AdminLocalizedLink>
                      ) : (
                        <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.noDeterministicLifecycleTrace}</span>
                      )}
                    </div>
                  ),
                },
                {
                  id: "lifecycleContext",
                  label: copy.lifecycleContext,
                  renderCell: (row) => (
                    <div style={{ display: "grid", gap: "6px" }}>
                      <span>{row.lifecycleContextSummaryLabel}</span>
                      <span style={{ color: "#64748b", fontSize: "0.86rem" }}>
                        {copy.latestTrace}: {row.latestLifecycleRequestIdLabel}
                      </span>
                    </div>
                  ),
                  width: "320px",
                },
                {
                  id: "mediaReEntry",
                  label: copy.mediaReEntry,
                  renderCell: (row) =>
                    row.relatedMediaArtifactId ? (
                      <button
                        onClick={() => focusMediaWorkspace(row.relatedMediaArtifactId, row.relatedMediaScenarioKey)}
                        style={secondaryActionButtonStyle}
                        type="button"
                      >
                        {copy.openLinkedArtifact}
                      </button>
                    ) : (
                      <span style={{ color: "#64748b", fontSize: "0.86rem" }}>{copy.noLinkedArtifact}</span>
                    ),
                },
                {
                  id: "upstreamTrace",
                  label: copy.upstreamTrace,
                  renderCell: (row) => (
                    <div style={{ display: "grid", gap: "6px" }}>
                      <span>{row.upstreamRequestIdLabel}</span>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {row.upstreamFailedFlowDrilldownTo ? (
                          <AdminLocalizedLink to={row.upstreamFailedFlowDrilldownTo}>{copy.openUpstreamFailedFlow}</AdminLocalizedLink>
                        ) : null}
                        {row.jobsDrilldownTo ? <AdminLocalizedLink to={row.jobsDrilldownTo}>{copy.openJobs}</AdminLocalizedLink> : null}
                      </div>
                    </div>
                  ),
                },
                { id: "createdAt", label: copy.created, renderCell: (row) => row.createdAtLabel },
                { id: "confirmedAt", label: copy.confirmed, renderCell: (row) => row.confirmedAtLabel },
                { id: "expiresAt", label: copy.expires, renderCell: (row) => row.expiresAtLabel },
                { id: "deletedAt", label: copy.deleted, renderCell: (row) => row.deletedAtLabel },
                { id: "storageDeletedAt", label: copy.storageDeleted, renderCell: (row) => row.storageDeletedAtLabel },
                { id: "investigationHint", label: copy.operatorHint, renderCell: (row) => row.investigationHint, width: "280px" },
              ]}
              emptyState={<AdminEmptyState title={copy.noFiles} body={copy.noFilesBody} />}
              getRowId={(row) => row.fileId}
              rows={state.viewModel.files}
            />
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.uploadDiagnosisTitle, copy.uploadDiagnosisBody)}
            <div
              style={{
                display: "grid",
                gap: "16px",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              }}
            >
              {state.viewModel.uploadDiagnoses.map((diagnosis) => (
                <div key={diagnosis.scenarioKey} style={cardStyle}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "12px",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ display: "grid", gap: "6px" }}>
                      <strong style={{ fontSize: "1rem" }}>{diagnosis.title}</strong>
                      <span style={{ color: "#475569", lineHeight: 1.6 }}>{diagnosis.summary}</span>
                    </div>
                    <AdminStatusBadge value={diagnosis.overallStatus} />
                  </div>

                  <div
                    style={{
                      marginTop: "14px",
                      display: "grid",
                      gap: "12px",
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    }}
                  >
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.sourceAnchor}</div>
                      <div>{diagnosis.sourceAnchorLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.recognitionSignal}</div>
                      <div>{diagnosis.recognitionSignalLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.failedModule}</div>
                      <div>{diagnosis.failedModuleLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.latestTrace}</div>
                      <div>{diagnosis.latestTraceLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.latestSaveEvidence}</div>
                      <div>{diagnosis.latestSaveLabel}</div>
                    </div>
                  </div>

                  <div style={{ marginTop: "16px", display: "grid", gap: "10px" }}>
                    <strong style={{ fontSize: "0.92rem" }}>{copy.uploadSteps}</strong>
                    {diagnosis.steps.map((step) => (
                      <div
                        key={`${diagnosis.scenarioKey}:${step.key}`}
                        style={{
                          padding: "12px 14px",
                          borderRadius: "16px",
                          border: "1px solid rgba(148, 163, 184, 0.2)",
                          background: "rgba(248, 250, 252, 0.8)",
                          display: "grid",
                          gap: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "10px",
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ display: "grid", gap: "4px" }}>
                            <strong>{step.title}</strong>
                            <span style={{ color: "#475569" }}>{step.summary}</span>
                          </div>
                          <AdminStatusBadge value={step.status} />
                        </div>

                        <div>
                          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.stepDetail}</div>
                          <div>{step.detail}</div>
                        </div>

                        {step.metaRows.length > 0 ? (
                          <div
                            style={{
                              display: "grid",
                              gap: "8px",
                              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                            }}
                          >
                            {step.metaRows.map((row) => (
                              <div key={`${diagnosis.scenarioKey}:${step.key}:${row.key}`}>
                                <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{row.label}</div>
                                <div>{row.value}</div>
                              </div>
                            ))}
                          </div>
                        ) : null}

                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", color: "#64748b", fontSize: "0.86rem" }}>
                          {step.fileIdLabel !== "-" ? <span>{copy.fileId}: {step.fileIdLabel}</span> : null}
                          {step.recordIdLabel !== "-" ? <span>{copy.recordId}: {step.recordIdLabel}</span> : null}
                          {step.receiptIdLabel !== "-" ? <span>{copy.receiptId}: {step.receiptIdLabel}</span> : null}
                          {step.jobIdLabel !== "-" ? <span>{copy.jobId}: {step.jobIdLabel}</span> : null}
                          {step.requestIdLabel !== "-" ? <span>{copy.reqShort}: {step.requestIdLabel}</span> : null}
                          {step.correlationIdLabel !== "-" ? <span>{copy.corrShort}: {step.correlationIdLabel}</span> : null}
                          {step.errorCodeLabel !== "-" ? <span>{copy.error}: {step.errorCodeLabel}</span> : null}
                        </div>

                        {step.failedFlowDrilldownTo || step.jobsDrilldownTo ? (
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {step.failedFlowDrilldownTo ? (
                              <AdminLocalizedLink to={step.failedFlowDrilldownTo}>
                                {copy.openStepFailedFlow}
                              </AdminLocalizedLink>
                            ) : null}
                            {step.jobsDrilldownTo ? (
                              <AdminLocalizedLink to={step.jobsDrilldownTo}>
                                {copy.openStepJobs}
                              </AdminLocalizedLink>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: "14px", color: "#475569", lineHeight: 1.6 }}>
                    {diagnosis.operatorHint}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(
              requestedPhotoWorkspaceFocusActive && primaryPhotoWorkspaceArtifact
                ? copy.selectedArtifactTitle
                : copy.representativePhotoTitle,
              requestedPhotoWorkspaceFocusActive && primaryPhotoWorkspaceArtifact
                ? copy.selectedArtifactBody
                : copy.representativePhotoBody,
            )}
            <AdminOperatorExplanationBlock
              screen="users_detail"
              slot="users_detail_media_workspace"
            />
            {primaryPhotoWorkspaceArtifact ? (
              <div
                style={{
                  display: "grid",
                  gap: "12px",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  padding: "16px",
                  borderRadius: "18px",
                  border: "1px solid rgba(148, 163, 184, 0.22)",
                  background: "rgba(248, 250, 252, 0.82)",
                }}
              >
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.artifactId}</div>
                  <div>{primaryPhotoWorkspaceArtifact.artifactId}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.source}</div>
                  <div>{primaryPhotoWorkspaceArtifact.sourceScenarioLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.linkedEntityType}</div>
                  <div>{primaryPhotoWorkspaceArtifact.linkedEntityTypeLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.relation}</div>
                  <div>{primaryPhotoWorkspaceArtifact.relationTypeLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.role}</div>
                  <div>{primaryPhotoWorkspaceArtifact.artifactRoleLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.contentType}</div>
                  <div>{primaryPhotoWorkspaceArtifact.contentTypeBucketLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.size}</div>
                  <div>{primaryPhotoWorkspaceArtifact.sizeBucketLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.created}</div>
                  <div>{primaryPhotoWorkspaceArtifact.uploadedAtLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.latestTrace}</div>
                  <div>{primaryPhotoWorkspaceArtifact.latestTraceLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.status}</div>
                  <div>{primaryPhotoWorkspaceArtifact.storageStatusLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.preview}</div>
                  <div>{primaryPhotoWorkspaceArtifact.previewAvailabilityLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.download}</div>
                  <div>{primaryPhotoWorkspaceArtifact.downloadAvailabilityLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.binaryReviewPath}</div>
                  <div>{primaryPhotoWorkspaceArtifact.binaryReviewPathLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.archiveState}</div>
                  <div>{primaryPhotoWorkspaceArtifact.archiveStateLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.incidentAttachment}</div>
                  <div>{primaryPhotoWorkspaceArtifact.incidentAttachmentStateLabel}</div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.fallback}</div>
                  <div>{primaryPhotoWorkspaceArtifact.fallbackReasonLabel}</div>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.operatorHint}</div>
                  <div>{primaryPhotoWorkspaceArtifact.binaryReviewHint}</div>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.archiveState}</div>
                  <div>{primaryPhotoWorkspaceArtifact.archiveStateHint}</div>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.incidentAttachment}</div>
                  <div>{primaryPhotoWorkspaceArtifact.incidentAttachmentHint}</div>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  {renderPhotoCompareBlock({
                    body:
                      requestedPhotoWorkspaceFocusActive && selectedArtifact
                        ? copy.selectedArtifactCompareBody
                        : copy.representativePhotoCompareBody,
                    nextActionLabel: representativeCompareNextActionLabel,
                    summary: primaryPhotoCompareSummary,
                    workspaceParityLabel: representativeSelectionParityLabel,
                  })}
                </div>
                <div style={{ gridColumn: "1 / -1", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    disabled={!canIssueMediaAccess(primaryPhotoPreviewAvailability) || mediaAccessState.status === "loading"}
                    onClick={() => void issueMediaAccess(primaryPhotoWorkspaceArtifact.artifactId, "preview")}
                    style={{
                      ...actionButtonStyle,
                      opacity: canIssueMediaAccess(primaryPhotoPreviewAvailability) ? 1 : 0.45,
                    }}
                    type="button"
                  >
                    {copy.preview}
                  </button>
                  <button
                    disabled={!canIssueMediaAccess(primaryPhotoDownloadAvailability) || mediaAccessState.status === "loading"}
                    onClick={() => void issueMediaAccess(primaryPhotoWorkspaceArtifact.artifactId, "download")}
                    style={{
                      ...secondaryActionButtonStyle,
                      opacity: canIssueMediaAccess(primaryPhotoDownloadAvailability) ? 1 : 0.45,
                    }}
                    type="button"
                  >
                    {copy.download}
                  </button>
                  {primaryPhotoWorkspaceArtifact.latestRequestId ? (
                    <AdminLocalizedLink
                      style={{ alignSelf: "center", fontWeight: 700 }}
                      to={`/admin/failed-flows?${new URLSearchParams({ requestId: primaryPhotoWorkspaceArtifact.latestRequestId }).toString()}`}
                    >
                      {copy.openTrace}
                    </AdminLocalizedLink>
                  ) : (
                    <span style={{ alignSelf: "center", color: "#64748b", fontSize: "0.88rem" }}>{copy.traceUnavailable}</span>
                  )}
                  {representativeWorkspaceTo ? (
                    <AdminLocalizedLink style={{ alignSelf: "center", fontWeight: 700 }} to={representativeWorkspaceTo}>
                      {copy.openRepresentativeInWorkspace}
                    </AdminLocalizedLink>
                  ) : null}
                  {mediaAccessState.status === "ready" &&
                  mediaAccessState.result === "allowed" &&
                  mediaAccessState.url &&
                  mediaAccessState.artifactId === primaryPhotoWorkspaceArtifact.artifactId ? (
                    <button onClick={() => openGrantedArtifact(mediaAccessState.url)} style={secondaryActionButtonStyle} type="button">
                      {copy.openArtifact}
                    </button>
                  ) : null}
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  {renderMediaAccessFeedbackBlock({
                    artifactId: primaryPhotoWorkspaceArtifact.artifactId,
                    copy,
                    mediaAccessFeedback,
                    mediaAccessState,
                  })}
                </div>
              </div>
            ) : (
              <AdminEmptyState title={copy.noRepresentativePhoto} body={copy.noRepresentativePhotoBody} />
            )}
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.mediaScenarioPivotTitle, copy.mediaScenarioPivotBody)}
            <AdminOperatorExplanationBlock
              screen="users_detail"
              slot="users_detail_media_reading_flow"
            />
            <div
              style={{
                ...cardStyle,
                display: "grid",
                gap: "12px",
              }}
            >
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                {state.viewModel.mediaScenarioPivots.map((pivot) => {
                  const isSelected = pivot.scenarioKey === selectedScenarioKey;
                  return (
                    <button
                      key={pivot.scenarioKey}
                      onClick={() => setSelectedScenarioKey(pivot.scenarioKey)}
                      style={{
                        ...secondaryActionButtonStyle,
                        background: isSelected ? "#0f766e" : "#fff",
                        border: isSelected ? "1px solid #0f766e" : secondaryActionButtonStyle.border,
                        color: isSelected ? "#fff" : "#18212b",
                      }}
                      type="button"
                    >
                      {pivot.label}
                    </button>
                  );
                })}
              </div>
              <div
                style={{
                  display: "grid",
                  gap: "12px",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                }}
              >
                {state.viewModel.mediaScenarioPivots.map((pivot) => (
                  <div
                    key={`${pivot.scenarioKey}_summary`}
                    style={{
                      padding: "12px 14px",
                      borderRadius: "16px",
                      border: pivot.scenarioKey === selectedScenarioKey
                        ? "1px solid rgba(15, 118, 110, 0.42)"
                        : "1px solid rgba(148, 163, 184, 0.2)",
                      background: pivot.scenarioKey === selectedScenarioKey
                        ? "rgba(240, 253, 250, 0.95)"
                        : "rgba(248, 250, 252, 0.92)",
                      display: "grid",
                      gap: "4px",
                    }}
                  >
                    <strong>{pivot.label}</strong>
                    <span style={{ color: "#475569" }}>{pivot.artifactCountLabel}</span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.representativeArtifact}: {pivot.representativeArtifactIdLabel}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.latestUpload}: {pivot.latestUploadAtLabel}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.sourceConnection}: {pivot.sourceConnectionLabel}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.coverageState}: {pivot.coverageStateLabel}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.downloadOnly}: {pivot.downloadOnlyCountLabel}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.archiveReadyCount}: {pivot.archiveReadyCountLabel}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.incidentAttachedCount}: {pivot.incidentAttachedCountLabel}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.blockedArtifacts}: {pivot.blockedCountLabel}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.fallbackSummary}: {pivot.fallbackSummaryLabel}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.latestTrace}: {pivot.latestTraceLabel}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {copy.representativeIncluded}: {pivot.representativeIncluded ? copy.yes : copy.no}
                    </span>
                    <span style={{ color: "#475569", fontSize: "0.88rem", lineHeight: 1.5 }}>
                      {pivot.investigationHint}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.mediaTimelineTitle, copy.mediaTimelineBody)}
            <div
              style={{
                display: "grid",
                gap: "12px",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              }}
            >
              {filteredMediaTimelineRows.map((row) => {
                const isSelected = selectedArtifact?.artifactId === row.artifactId;
                return (
                  <button
                    key={`${row.artifactId}_timeline`}
                    onClick={() => {
                      setSelectedScenarioKey(row.sourceScenarioKey);
                      setSelectedArtifactId(row.artifactId);
                    }}
                    style={{
                      ...cardStyle,
                      display: "grid",
                      gap: "8px",
                      textAlign: "left",
                      border: isSelected
                        ? "1px solid rgba(15, 118, 110, 0.42)"
                        : "1px solid rgba(148, 163, 184, 0.2)",
                      background: isSelected ? "rgba(240, 253, 250, 0.95)" : cardStyle.background,
                    }}
                    type="button"
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "baseline" }}>
                      <strong>{row.artifactId}</strong>
                      <span style={{ color: "#475569", fontSize: "0.88rem" }}>{row.uploadedAtLabel}</span>
                    </div>
                    <span style={{ color: "#475569", fontSize: "0.9rem" }}>
                      {`${row.sourceScenarioLabel} / ${row.relationTypeLabel} / ${row.artifactRoleLabel}`}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {`${copy.preview}: ${row.previewAvailabilityLabel}`}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {`${copy.download}: ${row.downloadAvailabilityLabel}`}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {`${copy.binaryReviewPath}: ${row.binaryReviewPathLabel}`}
                    </span>
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>
                      {`${copy.latestTrace}: ${row.latestTraceLabel}`}
                    </span>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                      {row.isRepresentativeEntry ? (
                        <span style={{ color: "#0f766e", fontWeight: 700, fontSize: "0.82rem" }}>{copy.representativeEntry}</span>
                      ) : null}
                      {row.isLatestForScenario ? (
                        <span style={{ color: "#0369a1", fontWeight: 700, fontSize: "0.82rem" }}>{copy.latestUpload}</span>
                      ) : null}
                      {row.requestTraceDrilldownTo ? (
                        <AdminLocalizedLink
                          onClick={(event) => event.stopPropagation()}
                          style={{ fontWeight: 700 }}
                          to={row.requestTraceDrilldownTo}
                        >
                          {copy.openTrace}
                        </AdminLocalizedLink>
                      ) : (
                        <span style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.traceUnavailable}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.mediaCoverageTitle, copy.mediaCoverageBody)}
            <AdminOperatorExplanationBlock
              screen="users_detail"
              slot="users_detail_media_coverage"
            />
            <div
              style={{
                display: "grid",
                gap: "12px",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              }}
            >
              {state.viewModel.mediaCoverageLedger.map((entry) => (
                <article
                  key={entry.scenarioKey}
                  style={{
                    ...cardStyle,
                    display: "grid",
                    gap: "10px",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "baseline" }}>
                    <strong>{entry.label}</strong>
                    <span style={{ color: "#475569", fontSize: "0.9rem" }}>{entry.artifactCountLabel}</span>
                  </div>
                  <div style={{ display: "grid", gap: "6px" }}>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.sourceConnection}</div>
                      <div>{entry.sourceConnectionLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.coverageState}</div>
                      <div>{entry.coverageStateLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.previewReady}</div>
                      <div>{entry.previewReadyCountLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.downloadReady}</div>
                      <div>{entry.downloadReadyCountLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.blockedArtifacts}</div>
                      <div>{entry.blockedCountLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.fallbackSummary}</div>
                      <div>{entry.fallbackSummaryLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.latestTrace}</div>
                      <div>{entry.latestTraceLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.representativeIncluded}</div>
                      <div>{entry.representativeIncluded ? copy.yes : copy.no}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.mediaCoverageHint}</div>
                      <div>{entry.investigationHint}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.sourceSurfaceTitle, copy.sourceSurfaceBody)}
            <AdminOperatorExplanationBlock
              screen="users_detail"
              slot="users_detail_source_surface"
            />
            <div
              style={{
                display: "grid",
                gap: "12px",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              }}
            >
              {state.viewModel.photoSourceSurfaceMatrix.map((entry) => (
                <article
                  key={entry.scenarioKey}
                  style={{
                    ...cardStyle,
                    display: "grid",
                    gap: "10px",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "baseline" }}>
                    <strong>{entry.label}</strong>
                    <span style={{ color: "#475569", fontSize: "0.9rem" }}>{entry.baselineStatusLabel}</span>
                  </div>
                  <div style={{ display: "grid", gap: "6px" }}>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.targetEntity}</div>
                      <div>{entry.targetLinkedEntityLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.sourceConnection}</div>
                      <div>{entry.sourceConnectionLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.sourceStatus}</div>
                      <div>{entry.baselineStatusLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.baselineSummary}</div>
                      <div>{entry.baselineSummaryLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.currentCoverage}</div>
                      <div>{entry.currentCoverageStateLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.previewReady}</div>
                      <div>{entry.previewReadyCountLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.downloadReady}</div>
                      <div>{entry.downloadReadyCountLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.blockedArtifacts}</div>
                      <div>{entry.blockedCountLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.binaryReviewPath}</div>
                      <div>{entry.binaryReviewPathLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.operatorHint}</div>
                      <div>{entry.operatorHint}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {state.viewModel.missingPhotoSourceDisclosures.length > 0 ? (
            <section style={{ display: "grid", gap: "12px" }}>
              {renderSectionTitle(copy.missingSourceTitle, copy.missingSourceBody)}
              <AdminOperatorExplanationBlock
                screen="users_detail"
                slot="users_detail_missing_sources"
              />
              <div
                style={{
                  display: "grid",
                  gap: "12px",
                  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                }}
              >
                {state.viewModel.missingPhotoSourceDisclosures.map((entry) => (
                  <article
                    key={entry.scenarioKey}
                    style={{
                      ...cardStyle,
                      display: "grid",
                      gap: "10px",
                      border: "1px solid rgba(245, 158, 11, 0.28)",
                      background: "rgba(255, 251, 235, 0.9)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "baseline" }}>
                      <strong>{entry.label}</strong>
                      <span style={{ color: "#92400e", fontSize: "0.9rem", fontWeight: 700 }}>{entry.statusLabel}</span>
                    </div>
                    <div style={{ display: "grid", gap: "6px" }}>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.sourceConnection}</div>
                        <div>{entry.sourceConnectionLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.sourceStatus}</div>
                        <div>{entry.statusLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.missingSourceReason}</div>
                        <div>{entry.reasonLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.nextAction}</div>
                        <div>{entry.nextActionHint}</div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.mediaWorkspaceTitle, copy.mediaWorkspaceBody)}
            {selectedArtifact ? (
              <div
                style={{
                  ...cardStyle,
                  display: "grid",
                  gap: "14px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ display: "grid", gap: "4px" }}>
                    <strong>{copy.selectedArtifactTitle}</strong>
                    <span style={{ color: "#475569" }}>{selectedArtifact.artifactId}</span>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                    {selectedArtifactMatchesRepresentative ? (
                      <span style={{ color: "#0f766e", fontWeight: 700 }}>{copy.representativeEntry}</span>
                    ) : null}
                    <span style={{ color: "#0f766e", fontWeight: 700 }}>{copy.selected}</span>
                  </div>
                </div>

                <AdminDetailPanel
                  title={copy.selectedArtifactTitle}
                  sections={[
                    {
                      title: copy.selectedArtifactBody,
                      items: [
                        { key: "source", label: copy.source, value: selectedArtifact.sourceScenarioLabel },
                        { key: "linkedEntityType", label: copy.linkedEntityType, value: selectedArtifact.linkedEntityTypeLabel },
                        { key: "relation", label: copy.relation, value: selectedArtifact.relationTypeLabel },
                        { key: "role", label: copy.role, value: selectedArtifact.artifactRoleLabel },
                        { key: "linked", label: copy.linked, value: selectedArtifact.linkedEntityLabel },
                        { key: "mediaKind", label: copy.mediaKind, value: selectedArtifact.mediaKindLabel },
                        { key: "contentType", label: copy.contentType, value: selectedArtifact.contentTypeBucketLabel },
                        { key: "size", label: copy.size, value: selectedArtifact.sizeBucketLabel },
                        { key: "uploadedAt", label: copy.created, value: selectedArtifact.uploadedAtLabel },
                        { key: "confirmedAt", label: copy.confirmed, value: selectedArtifact.confirmedAtLabel },
                        { key: "status", label: copy.status, value: selectedArtifact.storageStatusLabel },
                        { key: "preview", label: copy.preview, value: selectedArtifact.previewAvailabilityLabel },
                        { key: "download", label: copy.download, value: selectedArtifact.downloadAvailabilityLabel },
                        { key: "binaryReviewPath", label: copy.binaryReviewPath, value: selectedArtifact.binaryReviewPathLabel },
                        { key: "archiveState", label: copy.archiveState, value: selectedArtifact.archiveStateLabel },
                        {
                          key: "incidentAttachment",
                          label: copy.incidentAttachment,
                          value: selectedArtifact.incidentAttachmentStateLabel,
                        },
                        { key: "latestTrace", label: copy.latestTrace, value: selectedArtifact.latestTraceLabel },
                        {
                          key: "scenarioRepresentativeArtifact",
                          label: copy.representativeArtifact,
                          value: selectedArtifact.scenarioRepresentativeArtifactIdLabel,
                        },
                        {
                          key: "scenarioLatestUpload",
                          label: copy.latestUpload,
                          value: selectedArtifact.scenarioLatestUploadAtLabel,
                        },
                        {
                          key: "matchesScenarioRepresentative",
                          label: copy.selectedMatchesRepresentative,
                          value: selectedArtifact.matchesScenarioRepresentative ? copy.yes : copy.no,
                        },
                        {
                          key: "matchesScenarioLatestUpload",
                          label: copy.selectedMatchesLatestUpload,
                          value: selectedArtifact.matchesScenarioLatestUpload ? copy.yes : copy.no,
                        },
                        {
                          key: "linkedFailedFlow",
                          label: copy.linkedFailedFlow,
                          value: selectedArtifact.linkedFailedFlowDrilldownTo ? (
                            <AdminLocalizedLink to={selectedArtifact.linkedFailedFlowDrilldownTo}>
                              {copy.openLinkedFailedFlow}
                            </AdminLocalizedLink>
                          ) : (
                            copy.traceUnavailable
                          ),
                        },
                        {
                          key: "linkedLifecycle",
                          label: copy.linkedLifecycle,
                          value: selectedArtifact.linkedLifecycleDrilldownTo ? (
                            <AdminLocalizedLink to={selectedArtifact.linkedLifecycleDrilldownTo}>
                              {copy.openLinkedLifecycle}
                            </AdminLocalizedLink>
                          ) : (
                            copy.lifecycleUnavailable
                          ),
                        },
                        { key: "auditScope", label: copy.auditScope, value: selectedArtifact.auditScopeLabel },
                        { key: "sameDeviceContext", label: copy.sameDeviceContext, value: selectedArtifact.sameDeviceContextLabel },
                        { key: "fallback", label: copy.fallback, value: selectedArtifact.fallbackReasonLabel },
                      ],
                    },
                  ]}
                />

                {selectedArtifact ? (
                  <div
                    style={{
                      padding: "12px 14px",
                      borderRadius: "16px",
                      background: "rgba(248, 250, 252, 0.92)",
                      border: "1px solid rgba(148, 163, 184, 0.2)",
                      display: "grid",
                      gap: "8px",
                    }}
                  >
                    <strong>{copy.workspaceScenarioState}</strong>
                    <span style={{ color: "#475569" }}>{copy.workspaceScenarioStateBody}</span>
                    <div
                      style={{
                        display: "grid",
                        gap: "10px",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      }}
                    >
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.artifactCount}</div>
                        <div>{selectedArtifact.scenarioArtifactCountLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.representativeArtifact}</div>
                        <div>{selectedArtifact.scenarioRepresentativeArtifactIdLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.latestUpload}</div>
                        <div>{selectedArtifact.scenarioLatestUploadAtLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.sourceConnection}</div>
                        <div>{selectedArtifact.scenarioSourceConnectionLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.coverageState}</div>
                        <div>{selectedArtifact.scenarioCoverageStateLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.downloadOnly}</div>
                        <div>{selectedArtifact.scenarioDownloadOnlyCountLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.archiveReadyCount}</div>
                        <div>{selectedScenarioPivot?.archiveReadyCountLabel ?? "-"}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.incidentAttachedCount}</div>
                        <div>{selectedScenarioPivot?.incidentAttachedCountLabel ?? "-"}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.blockedArtifacts}</div>
                        <div>{selectedArtifact.scenarioBlockedCountLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.fallbackSummary}</div>
                        <div>{selectedArtifact.scenarioFallbackSummaryLabel}</div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.latestTrace}</div>
                        <div>{selectedArtifact.scenarioLatestTraceLabel}</div>
                      </div>
                    </div>
                    <span style={{ color: "#475569", lineHeight: 1.6 }}>{selectedScenarioPivot?.investigationHint}</span>
                    <span style={{ color: "#475569", lineHeight: 1.6 }}>{selectedArtifact.binaryReviewHint}</span>
                    <span style={{ color: "#475569", lineHeight: 1.6 }}>{selectedArtifact.archiveStateHint}</span>
                    <span style={{ color: "#475569", lineHeight: 1.6 }}>{selectedArtifact.incidentAttachmentHint}</span>
                    <span style={{ color: "#475569", lineHeight: 1.6 }}>{selectedArtifact.scenarioCompareHint}</span>
                  </div>
                ) : null}

                {renderPhotoCompareBlock({
                  body: copy.photoCompareBody,
                  nextActionLabel: selectedArtifactPhotoCompareSummary?.compareNextActionLabel,
                  summary: selectedArtifactPhotoCompareSummary,
                })}

                {selectedArtifact ? (
                  <div
                    style={{
                      padding: "12px 14px",
                      borderRadius: "16px",
                      background: "rgba(15, 23, 42, 0.96)",
                      border: "1px solid rgba(148, 163, 184, 0.18)",
                      display: "grid",
                      gap: "10px",
                    }}
                  >
                    <strong style={{ color: "#e2e8f0" }}>{copy.photoReviewPackageTitle}</strong>
                    <span style={{ color: "#cbd5e1", lineHeight: 1.6 }}>{copy.photoReviewPackageBody}</span>
                    <div
                      style={{
                        display: "grid",
                        gap: "10px",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        color: "#e2e8f0",
                      }}
                    >
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.handoffRoute}</div>
                        <div>
                          <AdminLocalizedLink to={uploadedPhotoReviewPackageExport.route}>
                            {uploadedPhotoReviewPackageExport.route}
                          </AdminLocalizedLink>
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.handoffReopenRoute}</div>
                        <div>
                          <AdminLocalizedLink to={uploadedPhotoReviewPackageExport.reopenRoute}>
                            {uploadedPhotoReviewPackageExport.reopenRoute}
                          </AdminLocalizedLink>
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.reviewRepresentativeArtifact}</div>
                        <div>{uploadedPhotoReviewPackageExport.evidenceSummary.representativeArtifactId ?? copy.handoffUnavailable}</div>
                        <div style={{ marginTop: "4px", color: "#cbd5e1" }}>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.representativeSelectionParityLabel}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.handoffPhotoArtifact}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.selectedArtifact
                            ? `${uploadedPhotoReviewPackageExport.evidenceSummary.selectedArtifact.artifactId} (${uploadedPhotoReviewPackageExport.evidenceSummary.selectedArtifact.sourceScenarioLabel})`
                            : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.reviewScenarioSummary}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.selectedScenarioSummary
                            ? `${uploadedPhotoReviewPackageExport.evidenceSummary.selectedScenarioSummary.label} / ${uploadedPhotoReviewPackageExport.evidenceSummary.selectedScenarioSummary.artifactCountLabel} / ${uploadedPhotoReviewPackageExport.evidenceSummary.selectedScenarioSummary.latestUploadAtLabel}`
                            : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.reviewTimelineRows}</div>
                        <div>{String(uploadedPhotoReviewPackageExport.evidenceSummary.timelineRows.length)}</div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.selectedMatchesRepresentative}</div>
                        <div>{uploadedPhotoReviewPackageExport.evidenceSummary.selectedMatchesRepresentative ? copy.yes : copy.no}</div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.selectedMatchesLatestUpload}</div>
                        <div>{uploadedPhotoReviewPackageExport.evidenceSummary.selectedMatchesLatestUpload ? copy.yes : copy.no}</div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.sourceConnection}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.selectedScenarioSummary?.sourceConnectionLabel ??
                            copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.coverageState}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.selectedScenarioSummary?.coverageStateLabel ??
                            copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.fallbackSummary}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.selectedScenarioSummary?.fallbackSummaryLabel ??
                            copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.handoffTraceLink}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.requestTraceDrilldownTo ? (
                            <AdminLocalizedLink to={uploadedPhotoReviewPackageExport.evidenceSummary.requestTraceDrilldownTo}>
                              {copy.openTrace}
                            </AdminLocalizedLink>
                          ) : (
                            copy.traceUnavailable
                          )}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.handoffRootCauseWorkspace}</div>
                        <div style={{ display: "grid", gap: "4px", justifyItems: "start" }}>
                          <div>{uploadedPhotoReviewPackageExport.evidenceSummary.incidentCompareArtifactId ?? copy.handoffUnavailable}</div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.incidentCompareArtifactId ? (
                            <button
                              disabled={!uploadedPhotoReviewPackageExport.evidenceSummary.incidentCompareArtifactId}
                              onClick={() => openIncidentCompareWorkspace(uploadedPhotoReviewPackageExport.evidenceSummary.incidentCompareArtifactId)}
                              style={{
                                ...secondaryActionButtonStyle,
                                opacity: uploadedPhotoReviewPackageExport.evidenceSummary.incidentCompareArtifactId ? 1 : 0.45,
                              }}
                              type="button"
                            >
                              {copy.openIncidentCompare}
                            </button>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: "0.88rem" }}>{copy.handoffUnavailable}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.handoffFailedFlow}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.linkedFailedFlowDrilldownTo ? (
                            <AdminLocalizedLink to={uploadedPhotoReviewPackageExport.evidenceSummary.linkedFailedFlowDrilldownTo}>
                              {copy.openLinkedFailedFlow}
                            </AdminLocalizedLink>
                          ) : (
                            copy.traceUnavailable
                          )}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.linkedLifecycle}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.linkedLifecycleDrilldownTo ? (
                            <AdminLocalizedLink to={uploadedPhotoReviewPackageExport.evidenceSummary.linkedLifecycleDrilldownTo}>
                              {copy.openLinkedLifecycle}
                            </AdminLocalizedLink>
                          ) : (
                            copy.lifecycleUnavailable
                          )}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.handoffPhotoCompare}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.selectedArtifact
                            ? `${uploadedPhotoReviewPackageExport.evidenceSummary.selectedArtifact.compareReadyLabel} / ${uploadedPhotoReviewPackageExport.evidenceSummary.selectedArtifact.originalDownloadIssueLabel}`
                            : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.deliverySource}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.selectedArtifact?.deliverySourceLabel ?? copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.fallback}</div>
                        <div>
                          {uploadedPhotoReviewPackageExport.evidenceSummary.selectedArtifact?.fallbackReasonLabel ?? copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#94a3b8", fontSize: "0.82rem" }}>{copy.reviewPackageNextAction}</div>
                        <div>{uploadedPhotoReviewPackageExport.evidenceSummary.nextActionLabel}</div>
                      </div>
                    </div>
                    <div>
                      <button
                        onClick={() => downloadJsonArtifact(uploadedPhotoReviewExportFilename, uploadedPhotoReviewPackageExport)}
                        style={secondaryActionButtonStyle}
                        type="button"
                      >
                        {copy.exportPhotoReviewPackage}
                      </button>
                    </div>
                    <strong style={{ color: "#e2e8f0" }}>{copy.photoReviewPackageSchemaTitle}</strong>
                    <span style={{ color: "#cbd5e1", lineHeight: 1.6 }}>{copy.photoReviewPackageSchemaBody}</span>
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#e2e8f0",
                        fontSize: "0.82rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {JSON.stringify(uploadedPhotoReviewPackageExport, null, 2)}
                    </pre>
                  </div>
                ) : null}

                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    disabled={!canIssueMediaAccess(selectedArtifact.previewAvailability) || mediaAccessState.status === "loading"}
                    onClick={() => void issueMediaAccess(selectedArtifact.artifactId, "preview")}
                    style={{
                      ...actionButtonStyle,
                      opacity: canIssueMediaAccess(selectedArtifact.previewAvailability) ? 1 : 0.45,
                    }}
                    type="button"
                  >
                    {copy.preview}
                  </button>
                  <button
                    disabled={!canIssueMediaAccess(selectedArtifact.downloadAvailability) || mediaAccessState.status === "loading"}
                    onClick={() => void issueMediaAccess(selectedArtifact.artifactId, "download")}
                    style={{
                      ...secondaryActionButtonStyle,
                      opacity: canIssueMediaAccess(selectedArtifact.downloadAvailability) ? 1 : 0.45,
                    }}
                    type="button"
                  >
                    {copy.download}
                  </button>
                  {selectedArtifact.requestTraceDrilldownTo ? (
                    <AdminLocalizedLink style={{ alignSelf: "center", fontWeight: 700 }} to={selectedArtifact.requestTraceDrilldownTo}>
                      {copy.openTrace}
                    </AdminLocalizedLink>
                  ) : (
                    <span style={{ alignSelf: "center", color: "#64748b", fontSize: "0.88rem" }}>{copy.traceUnavailable}</span>
                  )}
                  <button
                    disabled={!(selectedIncidentArtifact?.artifactId ?? recommendedIncidentArtifactId)}
                    onClick={() => openIncidentCompareWorkspace(selectedIncidentArtifact?.artifactId ?? recommendedIncidentArtifactId)}
                    style={{
                      ...secondaryActionButtonStyle,
                      opacity: selectedIncidentArtifact?.artifactId ?? recommendedIncidentArtifactId ? 1 : 0.45,
                    }}
                    type="button"
                  >
                    {copy.openIncidentCompare}
                  </button>
                  {selectedArtifact.linkedFailedFlowDrilldownTo ? (
                    <AdminLocalizedLink style={{ alignSelf: "center", fontWeight: 700 }} to={selectedArtifact.linkedFailedFlowDrilldownTo}>
                      {copy.openLinkedFailedFlow}
                    </AdminLocalizedLink>
                  ) : null}
                  {selectedArtifact.linkedLifecycleDrilldownTo ? (
                    <AdminLocalizedLink style={{ alignSelf: "center", fontWeight: 700 }} to={selectedArtifact.linkedLifecycleDrilldownTo}>
                      {copy.openLinkedLifecycle}
                    </AdminLocalizedLink>
                  ) : null}
                  {mediaAccessState.status === "ready" &&
                  mediaAccessState.result === "allowed" &&
                  mediaAccessState.url &&
                  mediaAccessState.artifactId === selectedArtifact.artifactId ? (
                    <button onClick={() => openGrantedArtifact(mediaAccessState.url)} style={secondaryActionButtonStyle} type="button">
                      {copy.openArtifact}
                    </button>
                  ) : null}
                </div>

                {renderMediaAccessFeedbackBlock({
                  artifactId: selectedArtifact.artifactId,
                  copy,
                  mediaAccessFeedback,
                  mediaAccessState,
                })}
              </div>
            ) : (
              <AdminEmptyState title={copy.noMediaArtifacts} body={copy.noMediaArtifactsBody} />
            )}
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.mediaArtifactsTitle, copy.mediaArtifactsBody)}
            <AdminDataTable
              columns={[
                { id: "artifactId", label: copy.artifactId, renderCell: (row) => row.artifactId },
                { id: "source", label: copy.source, renderCell: (row) => row.sourceScenarioLabel },
                { id: "linkedEntity", label: copy.linked, renderCell: (row) => row.linkedEntityLabel },
                { id: "relation", label: copy.relation, renderCell: (row) => row.relationTypeLabel },
                { id: "role", label: copy.role, renderCell: (row) => row.artifactRoleLabel },
                { id: "mediaKind", label: copy.mediaKind, renderCell: (row) => row.mediaKindLabel },
                { id: "contentType", label: copy.contentType, renderCell: (row) => row.contentTypeBucketLabel },
                { id: "sizeBucket", label: copy.size, renderCell: (row) => row.sizeBucketLabel },
                { id: "createdAt", label: copy.created, renderCell: (row) => row.uploadedAtLabel },
                { id: "confirmedAt", label: copy.confirmed, renderCell: (row) => row.confirmedAtLabel },
                { id: "status", label: copy.status, renderCell: (row) => row.storageStatusLabel },
                { id: "preview", label: copy.preview, renderCell: (row) => row.previewAvailabilityLabel },
                { id: "download", label: copy.download, renderCell: (row) => row.downloadAvailabilityLabel },
                { id: "binaryReviewPath", label: copy.binaryReviewPath, renderCell: (row) => row.binaryReviewPathLabel },
                { id: "archiveState", label: copy.archiveState, renderCell: (row) => row.archiveStateLabel },
                {
                  id: "incidentAttachment",
                  label: copy.incidentAttachment,
                  renderCell: (row) => row.incidentAttachmentStateLabel,
                },
                { id: "latestTrace", label: copy.latestTrace, renderCell: (row) => row.latestTraceLabel },
                { id: "fallback", label: copy.fallback, renderCell: (row) => row.fallbackReasonLabel },
                {
                  id: "inspect",
                  label: copy.inspect,
                  renderCell: (row) =>
                    row.artifactId === selectedArtifact?.artifactId ? (
                      <span style={{ color: "#0f766e", fontWeight: 700 }}>{copy.selected}</span>
                    ) : (
                      <button
                        onClick={() => focusMediaWorkspace(row.artifactId, row.sourceScenarioKey)}
                        style={secondaryActionButtonStyle}
                        type="button"
                      >
                        {copy.inspect}
                      </button>
                    ),
                },
              ]}
              emptyState={<AdminEmptyState title={copy.noMediaArtifacts} body={copy.noScenarioArtifacts} />}
              getRowId={(row) => row.artifactId}
              onRowClick={(row) => focusMediaWorkspace(row.artifactId, row.sourceScenarioKey)}
              rows={filteredMediaArtifacts}
            />
          </section>

          <section ref={incidentWorkspaceSectionRef} style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.incidentWorkspaceTitle, copy.incidentWorkspaceBody)}
            <AdminOperatorExplanationBlock
              screen="users_detail"
              slot="users_detail_incident_workspace"
            />
            {selectedIncidentArtifact ? (
              <div
                style={{
                  ...cardStyle,
                  display: "grid",
                  gap: "14px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ display: "grid", gap: "4px" }}>
                    <strong>{copy.incidentWorkspaceTitle}</strong>
                    <span style={{ color: "#475569" }}>{selectedIncidentArtifact.artifactId}</span>
                  </div>
                  <span style={{ color: "#0f766e", fontWeight: 700 }}>{copy.selected}</span>
                </div>

                <AdminDetailPanel
                  title={copy.incidentWorkspaceTitle}
                  sections={[
                    {
                      title: copy.incidentWorkspaceBody,
                      items: [
                        ...buildIncidentSameDeviceHeaderRows({
                          labels: {
                            artifactId: copy.artifactId,
                            deviceInstallId: copy.deviceInstallId,
                            release: copy.release,
                            appVersion: copy.appVersion,
                            platform: copy.platform,
                            runtimeSummary: copy.runtimeSummary,
                            capturedAt: copy.capturedAt,
                            captureTrigger: copy.captureTrigger,
                            captureWindow: copy.captureWindow,
                            bundleHash: copy.bundleHash,
                            manifestVersion: copy.manifestVersion,
                          },
                          source: selectedIncidentArtifact,
                        }),
                        { key: "supportability", label: copy.supportability, value: selectedIncidentArtifact.supportabilityStateLabel },
                        { key: "previewReadiness", label: copy.previewReadiness, value: selectedIncidentArtifact.previewReadinessLabel },
                        { key: "downloadReadiness", label: copy.downloadReadiness, value: selectedIncidentArtifact.downloadReadinessLabel },
                        { key: "handoffReadiness", label: copy.handoffReadiness, value: selectedIncidentArtifact.handoffReadinessLabel },
                        { key: "previewHeadline", label: copy.previewHeadline, value: selectedIncidentArtifact.previewHeadlineLabel },
                        { key: "topCategory", label: copy.topCategory, value: selectedIncidentArtifact.previewTopCategoryLabel },
                        { key: "latestTrace", label: copy.latestTrace, value: selectedIncidentArtifact.latestTraceLabel },
                        { key: "lastErrorCode", label: copy.lastError, value: selectedIncidentArtifact.latestErrorCodeLabel },
                        {
                          key: "linkedPhotoEvidence",
                          label: copy.linkedPhotoEvidence,
                          value: incidentWorkspacePhotoArtifact ? (
                            `${incidentWorkspacePhotoArtifact.artifactId} (${incidentWorkspacePhotoArtifact.sourceScenarioLabel})`
                          ) : (
                            copy.noLinkedPhotoEvidence
                          ),
                        },
                        {
                          key: "traceAlignment",
                          label: copy.traceAlignment,
                          value: formatIncidentTraceJoinLabel(
                            incidentEvidenceWorkspace,
                            locale,
                            incidentWorkspacePhotoArtifact?.relationTypeLabel ?? copy.contextualTrace,
                          ),
                        },
                        {
                          key: "trace",
                          label: copy.traceLink,
                          value: selectedIncidentArtifact.requestTraceDrilldownTo ? (
                            <AdminLocalizedLink to={selectedIncidentArtifact.requestTraceDrilldownTo}>
                              {copy.openTrace}
                            </AdminLocalizedLink>
                          ) : (
                            copy.traceUnavailable
                          ),
                        },
                      ],
                    },
                  ]}
                />

                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "16px",
                    background: "rgba(255, 247, 237, 0.72)",
                    border: "1px solid rgba(251, 146, 60, 0.18)",
                    display: "grid",
                    gap: "10px",
                  }}
                >
                  <strong>{copy.photoLinkedIncidentTitle}</strong>
                  <span style={{ color: "#475569", lineHeight: 1.6 }}>{copy.photoLinkedIncidentBody}</span>
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    }}
                  >
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentState}</div>
                      <div>{formatPhotoLinkedIncidentStateLabel(incidentEvidenceWorkspace.photoLinkageState, locale)}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.relation}</div>
                      <div>{incidentWorkspacePhotoArtifact?.relationTypeLabel ?? copy.traceUnavailable}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.incidentAttachment}</div>
                      <div>{incidentWorkspacePhotoArtifact?.incidentAttachmentStateLabel ?? copy.noLinkedPhotoEvidence}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentHint}</div>
                      <div>{incidentWorkspacePhotoArtifact?.incidentAttachmentHint ?? copy.handoffUnavailable}</div>
                    </div>
                  </div>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.nextAction}</div>
                    <div>{formatPhotoLinkedIncidentNextAction(incidentEvidenceWorkspace, incidentWorkspacePhotoArtifact, locale)}</div>
                  </div>
                </div>

                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "16px",
                    background: "rgba(240, 253, 250, 0.82)",
                    border: "1px solid rgba(20, 184, 166, 0.16)",
                    display: "grid",
                    gap: "10px",
                  }}
                >
                  <strong>{copy.diagnosisTitle}</strong>
                  <span style={{ color: "#475569", lineHeight: 1.6 }}>{copy.diagnosisBody}</span>
                  <p style={{ margin: 0, color: "#0f172a", fontWeight: 700 }}>
                    {selectedIncidentArtifact.diagnosisSummaryLabel}
                  </p>
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    }}
                  >
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.previewHeadline}</div>
                      <div>{selectedIncidentArtifact.previewHeadlineLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.failureSignal}</div>
                      <div>{selectedIncidentArtifact.primaryFailureSignalLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.diagnosisConfidence}</div>
                      <div>{selectedIncidentArtifact.diagnosisConfidenceLabel}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.traceAlignment}</div>
                      <div>
                        {formatIncidentTraceJoinLabel(
                          incidentEvidenceWorkspace,
                          locale,
                          incidentWorkspacePhotoArtifact?.relationTypeLabel ?? copy.contextualTrace,
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.diagnosisNextAction}</div>
                    <div>{selectedIncidentArtifact.recommendedActionLabel}</div>
                  </div>
                </div>

                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "16px",
                    background: "rgba(248, 250, 252, 0.92)",
                    border: "1px solid rgba(148, 163, 184, 0.2)",
                    display: "grid",
                    gap: "10px",
                  }}
                >
                  <strong>{copy.deliveryPathState}</strong>
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    }}
                  >
                    {buildSelectedIncidentWorkspaceProvenanceRows({
                      labels: {
                        deviceInstallId: copy.deviceInstallId,
                        release: copy.release,
                        appVersion: copy.appVersion,
                        platform: copy.platform,
                        runtimeSummary: copy.runtimeSummary,
                        capturedAt: copy.capturedAt,
                        captureTrigger: copy.captureTrigger,
                        captureWindow: copy.captureWindow,
                        deliveryPathState: copy.deliveryPathState,
                        supportability: copy.supportability,
                        manifestVersion: copy.manifestVersion,
                        redactionVersion: copy.redactionVersion,
                        bundleHash: copy.bundleHash,
                        deliverySource: copy.deliverySource,
                      },
                      source: selectedIncidentArtifact,
                      deliverySourceLabel: formatIncidentLogBundleDeliverySourceLabel({
                        locale,
                        accessState: incidentAccessState,
                        artifactId: selectedIncidentArtifact.artifactId,
                        downloadIssue: undefined,
                      }),
                    }).map((row) => (
                      <div key={`selected_incident_provenance_${row.key}`}>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{row.label}</div>
                        <div>{row.value}</div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.producerPathNextAction}</div>
                    <div>{selectedIncidentArtifact.producerPathNextActionLabel}</div>
                  </div>
                </div>

                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "16px",
                    background: "rgba(240, 249, 255, 0.88)",
                    border: "1px solid rgba(14, 165, 233, 0.16)",
                    display: "grid",
                    gap: "10px",
                  }}
                >
                  <strong>{copy.manifestCompareTitle}</strong>
                  <span style={{ color: "#475569", lineHeight: 1.6 }}>{copy.manifestCompareBody}</span>
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    }}
                  >
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.manifestCompareReady}</div>
                      <div>{incidentManifestCompareSummary?.compareReadyLabel ?? copy.handoffUnavailable}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.deliverySource}</div>
                      <div>{incidentManifestCompareSummary?.deliverySourceLabel ?? copy.handoffUnavailable}</div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gap: "4px" }}>
                    {incidentManifestCompareSummary
                      ? incidentManifestCompareSummary.parityRows.map((row) => (
                          <span key={`manifest_compare_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                        ))
                      : <span>{copy.handoffUnavailable}</span>}
                  </div>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.nextAction}</div>
                    <div>{incidentManifestCompareSummary?.compareNextActionLabel ?? copy.handoffUnavailable}</div>
                  </div>
                </div>

                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "16px",
                    background: "rgba(248, 250, 252, 0.92)",
                    border: "1px solid rgba(148, 163, 184, 0.2)",
                    display: "grid",
                    gap: "10px",
                  }}
                >
                  <strong>{copy.preview}</strong>
                  <span style={{ color: "#475569", lineHeight: 1.6 }}>{copy.incidentPreviewBody}</span>
                {selectedIncidentArtifact.previewLines.length > 0 ? (
                    <div style={{ display: "grid", gap: "10px" }}>
                      {selectedIncidentArtifact.previewLines.map((line, index) => (
                        <article
                          key={`${selectedIncidentArtifact.artifactId}_${line.timestampLabel}_${index}`}
                          style={{
                            padding: "12px 14px",
                            borderRadius: "14px",
                            background: "#fff",
                            border: "1px solid rgba(148, 163, 184, 0.2)",
                            display: "grid",
                            gap: "8px",
                          }}
                        >
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            <strong>{line.levelLabel}</strong>
                            <span style={{ color: "#475569" }}>{line.timestampLabel}</span>
                            <span style={{ color: "#475569" }}>{line.categoryLabel}</span>
                          </div>
                          <div style={{ display: "grid", gap: "4px" }}>
                            <div>
                              <span style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.messageKey}: </span>
                              <span>{line.messageKeyLabel}</span>
                            </div>
                            <div>
                              <span style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.redactedContext}: </span>
                              <span>{line.redactedContextLabel}</span>
                            </div>
                            <div>
                              <span style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.latestTrace}: </span>
                              <span>{`${line.requestIdLabel} / ${line.correlationIdLabel}`}</span>
                            </div>
                            <div>
                              <span style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.lastError}: </span>
                              <span>{line.errorCodeLabel}</span>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: "#475569" }}>
                      {copy.noIncidentPreview} {selectedIncidentArtifact.previewReadinessLabel}
                    </span>
                  )}
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    {incidentWorkspacePhotoArtifact ? (
                      <button
                        disabled={
                          !canIssueMediaAccess(incidentWorkspacePhotoArtifact.previewAvailability) ||
                          mediaAccessState.status === "loading"
                        }
                        onClick={() => void issueMediaAccess(incidentWorkspacePhotoArtifact.artifactId, "preview")}
                        style={{
                          ...actionButtonStyle,
                          opacity: canIssueMediaAccess(incidentWorkspacePhotoArtifact.previewAvailability) ? 1 : 0.45,
                        }}
                        type="button"
                      >
                        {copy.previewPhotoEvidence}
                      </button>
                    ) : null}
                    {incidentWorkspacePhotoArtifact ? (
                      <button
                        disabled={
                          !canIssueMediaAccess(incidentWorkspacePhotoArtifact.downloadAvailability) ||
                          mediaAccessState.status === "loading"
                        }
                        onClick={() => void issueMediaAccess(incidentWorkspacePhotoArtifact.artifactId, "download")}
                        style={{
                          ...secondaryActionButtonStyle,
                          opacity: canIssueMediaAccess(incidentWorkspacePhotoArtifact.downloadAvailability) ? 1 : 0.45,
                        }}
                        type="button"
                      >
                        {copy.downloadPhotoOriginal}
                      </button>
                    ) : null}
                    {incidentWorkspacePhotoArtifact ? (
                      <button
                        onClick={() =>
                          focusMediaWorkspace(
                            incidentWorkspacePhotoArtifact.artifactId,
                            incidentWorkspacePhotoArtifact.sourceScenarioKey,
                          )
                        }
                        style={secondaryActionButtonStyle}
                        type="button"
                      >
                        {copy.openPhotoWorkspace}
                      </button>
                    ) : null}
                    {selectedIncidentArtifact.requestTraceDrilldownTo ? (
                      <AdminLocalizedLink style={{ alignSelf: "center", fontWeight: 700 }} to={selectedIncidentArtifact.requestTraceDrilldownTo}>
                        {copy.openTrace}
                      </AdminLocalizedLink>
                    ) : null}
                    <button
                      disabled={
                        selectedIncidentArtifact.downloadReadiness !== "ready" ||
                        incidentAccessState.status === "loading"
                      }
                      onClick={() => void issueIncidentLogAccess(selectedIncidentArtifact.artifactId, "download")}
                      style={{
                        ...secondaryActionButtonStyle,
                        opacity: selectedIncidentArtifact.downloadReadiness === "ready" ? 1 : 0.45,
                      }}
                      type="button"
                    >
                      {copy.downloadLogBundle}
                    </button>
                    {mediaAccessState.status === "ready" &&
                    mediaAccessState.result === "allowed" &&
                    mediaAccessState.url &&
                    incidentWorkspacePhotoArtifact &&
                    mediaAccessState.artifactId === incidentWorkspacePhotoArtifact.artifactId ? (
                      <button onClick={() => openGrantedArtifact(mediaAccessState.url)} style={secondaryActionButtonStyle} type="button">
                        {copy.openArtifact}
                      </button>
                    ) : null}
                    {incidentAccessState.status === "ready" &&
                    incidentAccessState.result === "allowed" &&
                    incidentAccessState.url &&
                    incidentAccessState.artifactId === selectedIncidentArtifact.artifactId ? (
                      <button onClick={() => openGrantedArtifact(incidentAccessState.url)} style={secondaryActionButtonStyle} type="button">
                        {copy.openArtifact}
                      </button>
                    ) : null}
                  </div>
                  {renderMediaAccessFeedbackBlock({
                    artifactId: incidentWorkspacePhotoArtifact?.artifactId,
                    copy,
                    mediaAccessFeedback,
                    mediaAccessState,
                  })}
                  {renderIncidentLogAccessFeedbackBlock({
                    artifactId: selectedIncidentArtifact.artifactId,
                    bundleDeliverySourceLabel: formatIncidentLogBundleDeliverySourceLabel({
                      locale,
                      accessState: incidentAccessState,
                      artifactId: selectedIncidentArtifact.artifactId,
                      downloadIssue: undefined,
                    }),
                    copy,
                    incidentAccessFeedback,
                    incidentAccessState,
                  })}
                </div>

                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "16px",
                    background: "rgba(248, 250, 252, 0.92)",
                    border: "1px solid rgba(148, 163, 184, 0.2)",
                    display: "grid",
                    gap: "10px",
                  }}
                >
                  <strong>{copy.handoffPackageTitle}</strong>
                  <span style={{ color: "#475569", lineHeight: 1.6 }}>{copy.handoffPackageBody}</span>
                  <div
                    style={{
                      padding: "12px 14px",
                      borderRadius: "16px",
                      background: "rgba(255, 255, 255, 0.88)",
                      border: "1px solid rgba(148, 163, 184, 0.18)",
                      display: "grid",
                      gap: "10px",
                    }}
                  >
                    <strong>{copy.handoffPackageSchemaTitle}</strong>
                    <div
                      style={{
                        display: "grid",
                        gap: "10px",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      }}
                    >
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffRoute}</div>
                        <div>
                          <AdminLocalizedLink to={incidentHandoffPackageExport.route}>
                            {incidentHandoffPackageExport.route}
                          </AdminLocalizedLink>
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffReopenRoute}</div>
                        <div>
                          <AdminLocalizedLink to={incidentHandoffPackageExport.reopenRoute}>
                            {incidentHandoffPackageExport.reopenRoute}
                          </AdminLocalizedLink>
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffPhotoContext}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoSourceScenarioLabel} / ${incidentHandoffPackageExport.evidenceSummary.relationLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoArtifactRoleLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoArchiveStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoIncidentAttachmentStateLabel}`
                            : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                              ? `${incidentHandoffPackageExport.evidenceSummary.photoArtifact.sourceScenarioLabel} / ${incidentHandoffPackageExport.evidenceSummary.photoArtifact.relationTypeLabel} / ${incidentHandoffPackageExport.evidenceSummary.photoArtifact.artifactRoleLabel} / ${incidentHandoffPackageExport.evidenceSummary.photoArtifact.archiveStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.photoArtifact.incidentAttachmentStateLabel}`
                            : copy.noLinkedPhotoEvidence}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffPhotoCompare}</div>
                        <div style={{ display: "grid", gap: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoReviewRows.map((row) => (
                                <span key={row.key}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                              ? incidentHandoffPackageExport.evidenceSummary.photoArtifact.photoCompareRows.map((row) => (
                                <span key={row.key}>{`${row.label}: ${row.value}`}</span>
                              ))
                              : <span>{copy.noLinkedPhotoEvidence}</span>}
                        </div>
                        <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoAccessIssuanceRows.map((row) => (
                                <span key={`photo_access_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                              ? incidentHandoffPackageExport.evidenceSummary.photoArtifact.photoAccessIssuanceRows.map((row) => (
                                <span key={`photo_access_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                              ))
                              : null}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffFailedFlow}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.failedFlowLink ? (
                            <AdminLocalizedLink to={incidentHandoffPackageExport.evidenceSummary.failedFlowLink}>
                              {incidentHandoffPackageExport.evidenceSummary.requestTrace?.requestId ??
                                incidentHandoffPackageExport.evidenceSummary.requestTrace?.correlationId ??
                                copy.openTrace}
                            </AdminLocalizedLink>
                          ) : (
                            copy.traceUnavailable
                          )}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffLogManifest}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary?.captureTriggerLabel ?? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.captureTriggerLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary?.capturedAtBucketLabel ?? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.capturedAtBucketLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary?.manifestVersionLabel ?? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.manifestVersionLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary?.redactionVersionLabel ?? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.redactionVersionLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary?.bundleHashPresenceLabel ?? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.bundleHashPresenceLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary?.deliveryPathStateLabel ?? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.deliveryPathStateLabel}`
                            : copy.noIncidentLogs}
                        </div>
                        <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.sameDeviceLogAccessIssuanceRows.map((row) => (
                                <span key={`same_device_manifest_access_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                              ? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.accessIssuanceRows.map((row) => (
                                <span key={`same_device_manifest_access_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                              ))
                              : null}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffSameDeviceHeader}</div>
                        <div style={{ display: "grid", gap: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.sameDeviceHeaderSummaryRows.map((row) => (
                                <span key={row.key}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                              ? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.sameDeviceHeaderRows.map((row) => (
                                <span key={row.key}>{`${row.label}: ${row.value}`}</span>
                              ))
                              : <span>{copy.noIncidentLogs}</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffReplayState}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.replayStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.replayNextActionLabel}`
                            : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                              ? `${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.replayStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.replayNextActionLabel}`
                            : copy.noIncidentLogs}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffReplayExcerpt}</div>
                        <div style={{ display: "grid", gap: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.excerptReplayLines.map((line, index) => (
                                <span key={`${line.timestampLabel}_${index}`}>
                                  {`${line.timestampLabel} / ${line.levelLabel} / ${line.categoryLabel} / ${line.messageKeyLabel} / ${line.redactedContextLabel} / ${line.requestIdLabel} / ${line.correlationIdLabel}${line.scenarioKeyLabel ? ` / ${line.scenarioKeyLabel}` : ""}`}
                                </span>
                              ))
                            : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                              ? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.excerptReplayLines.map((line, index) => (
                                <span key={`${line.timestampLabel}_${index}`}>
                                  {`${line.timestampLabel} / ${line.levelLabel} / ${line.categoryLabel} / ${line.messageKeyLabel} / ${line.redactedContextLabel} / ${line.requestIdLabel} / ${line.correlationIdLabel}${line.scenarioKeyLabel ? ` / ${line.scenarioKeyLabel}` : ""}`}
                                </span>
                              ))
                              : <span>{copy.noIncidentLogs}</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffBundleContract}</div>
                        <div style={{ display: "grid", gap: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? (
                                <>
                                  <span>{incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.bundleContractSummary.bundleBaseName}</span>
                                  <span>{incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.bundleContractSummary.manifestFilename}</span>
                                  <span>{incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.bundleContractSummary.payloadFilename}</span>
                                  <span>{incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.bundleContractSummary.compareSummaryFilename}</span>
                                  <span>{incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.bundleDeliverySourceLabel}</span>
                                  {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.bundleContractSummary.manifestFieldRows.map((row) => (
                                    <span key={row.key}>{`${row.label}: ${row.value}`}</span>
                                  ))}
                                </>
                              )
                            : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                              ? (
                                  <>
                                    <span>{incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.bundleContractSummary.bundleBaseName}</span>
                                    <span>{incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.bundleContractSummary.manifestFilename}</span>
                                    <span>{incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.bundleContractSummary.payloadFilename}</span>
                                    <span>{incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.bundleContractSummary.compareSummaryFilename}</span>
                                    <span>{incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.bundleDeliverySourceLabel}</span>
                                    {incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.bundleContractSummary.manifestFieldRows.map((row) => (
                                      <span key={row.key}>{`${row.label}: ${row.value}`}</span>
                                    ))}
                                  </>
                                )
                            : <span>{copy.noIncidentLogs}</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffLineSchema}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.bundleContractSummary.payloadFormatLabel
                            : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                              ? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.bundleContractSummary.payloadFormatLabel
                            : copy.noIncidentLogs}
                        </div>
                        <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.bundleContractSummary.lineSchemaLabels.map((label) => (
                                <span key={label}>{label}</span>
                              ))
                            : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                              ? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.bundleContractSummary.lineSchemaLabels.map((label) => (
                                <span key={label}>{label}</span>
                              ))
                              : null}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffRootCauseWorkspace}</div>
                        <div style={{ display: "grid", gap: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.readingOrderRows.map((row) => (
                                <span key={row.key}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : <span>{copy.handoffUnavailable}</span>}
                        </div>
                        <div style={{ display: "grid", gap: "4px", marginTop: "8px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.routeRows.map((row) => (
                                <span key={`root_cause_route_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : null}
                        </div>
                        <div style={{ marginTop: "8px" }}>
                          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffPhotoCompare}</div>
                          <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                            {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary
                              ? (
                                  <>
                                    <span>{`${copy.photoCompareReady}: ${incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.photoCompareReadyLabel}`}</span>
                                    <span>{`${copy.download}: ${incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.photoOriginalDownloadIssueLabel}`}</span>
                                    <span>{`${copy.deliverySource}: ${incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.photoDeliverySourceLabel}`}</span>
                                  </>
                                )
                              : <span>{copy.noLinkedPhotoEvidence}</span>}
                          </div>
                          <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                            {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary &&
                            incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.photoReviewRows.length > 0
                              ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.photoReviewRows.map((row) => (
                                  <span key={`root_cause_photo_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                                ))
                              : <span>{copy.noLinkedPhotoEvidence}</span>}
                          </div>
                          <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                            {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary &&
                            incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.photoAccessIssuanceRows.length > 0
                              ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.photoAccessIssuanceRows.map((row) => (
                                  <span key={`root_cause_photo_access_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                                ))
                              : null}
                          </div>
                        </div>
                        <div style={{ marginTop: "8px" }}>
                          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffLogManifest}</div>
                          <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                            {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary &&
                            incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.sameDeviceLogRows.length > 0
                              ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.sameDeviceLogRows.map((row) => (
                                  <span key={`root_cause_log_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                                ))
                              : <span>{copy.noIncidentLogs}</span>}
                          </div>
                          <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                            {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary &&
                            incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.sameDeviceLogAccessIssuanceRows.length > 0
                              ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.sameDeviceLogAccessIssuanceRows.map((row) => (
                                  <span key={`root_cause_log_access_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                                ))
                              : null}
                          </div>
                        </div>
                        <div style={{ marginTop: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.parityDecisionLabel} / ${incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.deliveryPathStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.bundleDeliverySourceLabel} / ${incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.producerPathNextActionLabel}`
                            : copy.handoffUnavailable}
                        </div>
                        <div style={{ marginTop: "8px" }}>
                          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentState}</div>
                          <div>
                            {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary
                              ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.photoLinkageStateLabel
                              : copy.handoffUnavailable}
                          </div>
                        </div>
                        <div style={{ marginTop: "8px" }}>
                          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentHint}</div>
                          <div>
                            {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary
                              ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.photoLinkageHint
                              : copy.handoffUnavailable}
                          </div>
                        </div>
                        <div style={{ marginTop: "8px" }}>
                          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentNextAction}</div>
                          <div>
                            {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary
                              ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.photoLinkageNextActionLabel
                              : copy.handoffUnavailable}
                          </div>
                        </div>
                        <div style={{ display: "grid", gap: "4px", marginTop: "8px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.sourceSurfaceRows.map((row) => (
                                <span key={`source_surface_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : null}
                        </div>
                        <div style={{ display: "grid", gap: "4px", marginTop: "8px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary &&
                          incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.missingSourceRows.length > 0
                            ? incidentHandoffPackageExport.evidenceSummary.rootCauseWorkspaceSummary.missingSourceRows.map((row) => (
                                <span key={`missing_source_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : null}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffBrowserDirectBaseline}</div>
                        <div style={{ display: "grid", gap: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.evidencePackRows.map((row) => (
                                <span key={row.key}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : <span>{copy.handoffUnavailable}</span>}
                        </div>
                        <div style={{ marginTop: "8px" }}>
                          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffPhotoCompare}</div>
                          <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                            {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary &&
                            incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoReviewRows.length > 0
                              ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoReviewRows.map((row) => (
                                  <span key={`browser_direct_photo_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                                ))
                              : <span>{copy.noLinkedPhotoEvidence}</span>}
                          </div>
                          <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                            {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary &&
                            incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoAccessIssuanceRows.length > 0
                              ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoAccessIssuanceRows.map((row) => (
                                  <span key={`browser_direct_photo_access_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                                ))
                              : null}
                          </div>
                        </div>
                        <div style={{ marginTop: "8px" }}>
                          <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffLogManifest}</div>
                          <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                            {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary &&
                            incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.sameDeviceLogRows.length > 0
                              ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.sameDeviceLogRows.map((row) => (
                                  <span key={`browser_direct_log_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                                ))
                              : <span>{copy.noIncidentLogs}</span>}
                          </div>
                          <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                            {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary &&
                            incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.sameDeviceLogAccessIssuanceRows.length > 0
                              ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.sameDeviceLogAccessIssuanceRows.map((row) => (
                                  <span key={`browser_direct_log_access_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                                ))
                              : null}
                          </div>
                        </div>
                        <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.sameDeviceHeaderSummaryRows.map((row) => (
                                <span key={`browser_direct_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : null}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffRequestTraceLinkage}</div>
                        <div style={{ display: "grid", gap: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.requestTraceLinkageRows.map((row) => (
                                <span key={row.key}>{`${row.label}: ${row.value}`}</span>
                              ))
                            : <span>{copy.handoffUnavailable}</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffBrowserDirectState}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.supportabilityStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.previewReadinessLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.downloadReadinessLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.deliveryPathStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.handoffParityResultLabel}`
                            : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentState}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoLinkageStateLabel
                            : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentHint}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoLinkageHint
                            : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentNextAction}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoLinkageNextActionLabel
                            : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffPhotoCompare}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoCompareReadyLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoOriginalDownloadIssueLabel}`
                            : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                              ? `${incidentHandoffPackageExport.evidenceSummary.photoArtifact.compareReadyLabel} / ${incidentHandoffPackageExport.evidenceSummary.photoArtifact.originalDownloadIssueLabel}`
                              : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.deliverySource}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoDeliverySourceLabel
                            : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                              ? incidentHandoffPackageExport.evidenceSummary.photoArtifact.deliverySourceLabel
                              : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.captureTrigger}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.captureTriggerLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.captureWindowLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.capturedAtOperatorValueLabel}`
                            : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                              ? `${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.captureTriggerLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.captureWindowLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.capturedAtOperatorValueLabel}`
                              : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.supportability}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.supportabilityStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.previewReadinessLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.downloadReadinessLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.deliveryPathStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.handoffParityResultLabel}`
                            : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                              ? `${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.supportabilityStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.previewReadinessLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.downloadReadinessLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.deliveryPathStateLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.handoffParityResultLabel}`
                            : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.topCategory}</div>
                        <div>
                          {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.previewTopCategoryLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.excerptLineCountBucketLabel}`
                            : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                              ? `${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.previewTopCategoryLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.excerptLineCountBucketLabel}`
                              : copy.handoffUnavailable}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffChecklist}</div>
                        <div style={{ display: "grid", gap: "4px" }}>
                          {incidentHandoffPackageExport.evidenceSummary.verificationChecklist.length > 0
                            ? incidentHandoffPackageExport.evidenceSummary.verificationChecklist.map((item) => (
                                <span key={item.id}>{item.label}</span>
                              ))
                            : <span>{copy.handoffUnavailable}</span>}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffOperatorNote}</div>
                      <div>{incidentHandoffPackageExport.evidenceSummary.operatorNote ?? copy.handoffUnavailable}</div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.diagnosisTitle}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.diagnosisSummaryLabel
                          : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                            ? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.diagnosisSummaryLabel
                            : copy.handoffUnavailable}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.diagnosisConfidence}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.previewHeadlineLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.primaryFailureSignalLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.latestErrorCodeLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.diagnosisConfidenceLabel}`
                          : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.previewHeadlineLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.primaryFailureSignalLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.latestErrorCodeLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.diagnosisConfidenceLabel}`
                            : copy.handoffUnavailable}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.producerPathNextAction}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary?.producerPathNextActionLabel ??
                          incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary?.producerPathNextActionLabel ??
                          copy.handoffUnavailable}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.latestTrace}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.latestRequestIdLabel} / ${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.latestCorrelationIdLabel}`
                          : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.latestRequestIdLabel} / ${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.latestCorrelationIdLabel}`
                            : copy.handoffUnavailable}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    }}
                  >
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffStatus}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.statusLabel}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffPhotoArtifact}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoArtifactId} (${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoSourceScenarioLabel})`
                          : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                            ? `${incidentHandoffPackageExport.evidenceSummary.photoArtifact.artifactId} (${incidentHandoffPackageExport.evidenceSummary.photoArtifact.sourceScenarioLabel})`
                          : copy.noLinkedPhotoEvidence}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffPhotoDownload}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoOriginalDownloadIssueLabel
                          : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                            ? incidentHandoffPackageExport.evidenceSummary.photoArtifact.originalDownloadIssueLabel
                          : copy.traceUnavailable}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffPhotoCompare}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoCompareReadyLabel
                          : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                            ? incidentHandoffPackageExport.evidenceSummary.photoArtifact.compareReadyLabel
                            : copy.handoffUnavailable}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.deliverySource}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.photoDeliverySourceLabel
                          : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                            ? incidentHandoffPackageExport.evidenceSummary.photoArtifact.deliverySourceLabel
                            : copy.handoffUnavailable}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.fallback}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.fallbackReasonLabel
                          : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                            ? incidentHandoffPackageExport.evidenceSummary.photoArtifact.fallbackReasonLabel
                          : copy.handoffUnavailable}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.nextAction}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.reviewNextActionLabel
                          : incidentHandoffPackageExport.evidenceSummary.photoArtifact
                            ? incidentHandoffPackageExport.evidenceSummary.photoArtifact.reviewNextActionLabel
                          : copy.handoffUnavailable}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffLogArtifact}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? `${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.artifactId} (${incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.deviceInstallId})`
                          : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                            ? `${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.artifactId} (${incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.deviceInstallId})`
                          : copy.noIncidentLogs}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffLogDownload}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.fullBundleDownloadIssueLabel
                          : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                            ? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.fullBundleDownloadIssueLabel
                          : copy.traceUnavailable}
                      </div>
                      <div style={{ display: "grid", gap: "4px", marginTop: "4px" }}>
                        {incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary
                          ? incidentHandoffPackageExport.evidenceSummary.browserDirectAcceptanceSummary.sameDeviceLogAccessIssuanceRows.map((row) => (
                              <span key={`same_device_download_access_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                            ))
                          : incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary
                            ? incidentHandoffPackageExport.evidenceSummary.sameDeviceLogManifestSummary.accessIssuanceRows.map((row) => (
                              <span key={`same_device_download_access_${row.key}`}>{`${row.label}: ${row.value}`}</span>
                            ))
                            : null}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffTraceLink}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.requestTrace?.drilldownTo ? (
                          <AdminLocalizedLink to={incidentHandoffPackageExport.evidenceSummary.requestTrace.drilldownTo}>
                            {incidentHandoffPackageExport.evidenceSummary.requestTrace.requestId ??
                              incidentHandoffPackageExport.evidenceSummary.requestTrace.correlationId ??
                              copy.openTrace}
                          </AdminLocalizedLink>
                        ) : (
                          copy.traceUnavailable
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffRelation}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.relationLabel}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentState}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.photoLinkageStateLabel}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentHint}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.photoLinkageHint}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.photoLinkedIncidentNextAction}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.photoLinkageNextActionLabel}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>{copy.handoffNextAction}</div>
                      <div>
                        {incidentHandoffPackageExport.evidenceSummary.nextActionLabel}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "16px",
                    background: "rgba(15, 23, 42, 0.96)",
                    border: "1px solid rgba(148, 163, 184, 0.18)",
                    display: "grid",
                    gap: "10px",
                  }}
                >
                  <strong style={{ color: "#e2e8f0" }}>{copy.handoffPackageSchemaTitle}</strong>
                  <span style={{ color: "#cbd5e1", lineHeight: 1.6 }}>{copy.handoffPackageSchemaBody}</span>
                  <div>
                    <button
                      disabled={incidentHandoffPackagePreview.status === "empty"}
                      onClick={() =>
                        downloadJsonArtifact(
                          incidentHandoffExportFilename,
                          incidentHandoffPackageExport,
                        )
                      }
                      style={{
                        ...secondaryActionButtonStyle,
                        opacity: incidentHandoffPackagePreview.status === "empty" ? 0.45 : 1,
                      }}
                      type="button"
                    >
                      {copy.exportHandoffPackage}
                    </button>
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      color: "#e2e8f0",
                      fontSize: "0.82rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {JSON.stringify(incidentHandoffPackageExport, null, 2)}
                  </pre>
                </div>
              </div>
            ) : (
              <AdminEmptyState title={copy.noIncidentLogs} body={copy.noIncidentLogsBody} />
            )}
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.incidentLogsTitle, copy.incidentLogsBody)}
            <AdminDataTable
              columns={[
                { id: "artifactId", label: copy.artifactId, renderCell: (row) => row.artifactId },
                { id: "deviceInstallId", label: copy.deviceInstallId, renderCell: (row) => row.deviceInstallId },
                { id: "release", label: copy.release, renderCell: (row) => row.releaseLabel },
                { id: "appVersion", label: copy.appVersion, renderCell: (row) => row.appVersionLabel },
                { id: "platform", label: copy.platform, renderCell: (row) => row.platformLabel },
                {
                  id: "runtimeContext",
                  label: pickAdminDashboardLocaleText(locale, { en: "Runtime summary", ko: "런타임 요약" }),
                  renderCell: (row) => row.runtimeSummaryLabel,
                  width: "240px",
                },
                { id: "captureTrigger", label: copy.captureTrigger, renderCell: (row) => row.captureTriggerLabel },
                { id: "captureWindow", label: copy.captureWindow, renderCell: (row) => row.captureWindowLabel },
                { id: "capturedAt", label: copy.capturedAt, renderCell: (row) => row.capturedAtOperatorValueLabel },
                { id: "capturedAtBucket", label: copy.timeBucket, renderCell: (row) => row.capturedAtBucketLabel },
                { id: "supportability", label: copy.supportability, renderCell: (row) => row.supportabilityStateLabel },
                { id: "excerptLines", label: copy.excerptLines, renderCell: (row) => row.excerptLineCountBucketLabel },
                { id: "previewReadiness", label: copy.previewReadiness, renderCell: (row) => row.previewReadinessLabel },
                { id: "downloadReadiness", label: copy.downloadReadiness, renderCell: (row) => row.downloadReadinessLabel },
                { id: "handoffReadiness", label: copy.handoffReadiness, renderCell: (row) => row.handoffReadinessLabel },
                { id: "bundleHash", label: copy.bundleHash, renderCell: (row) => row.bundleHashPresenceLabel },
                { id: "manifestVersion", label: copy.manifestVersion, renderCell: (row) => row.manifestVersionLabel },
                { id: "redactionVersion", label: copy.redactionVersion, renderCell: (row) => row.redactionVersionLabel },
                { id: "latestTrace", label: copy.latestTrace, renderCell: (row) => row.latestTraceLabel },
                { id: "lastErrorCode", label: copy.lastError, renderCell: (row) => row.latestErrorCodeLabel },
                {
                  id: "trace",
                  label: copy.traceLink,
                  renderCell: (row) =>
                    row.requestTraceDrilldownTo ? (
                      <AdminLocalizedLink to={row.requestTraceDrilldownTo}>{copy.openTrace}</AdminLocalizedLink>
                    ) : (
                      copy.traceUnavailable
                    ),
                },
                {
                  id: "inspect",
                  label: copy.inspect,
                  renderCell: (row) =>
                    row.artifactId === selectedIncidentArtifact?.artifactId ? (
                      <span style={{ color: "#0f766e", fontWeight: 700 }}>{copy.selected}</span>
                    ) : (
                      <button
                        onClick={() => focusIncidentWorkspace(row.artifactId)}
                        style={secondaryActionButtonStyle}
                        type="button"
                      >
                        {copy.inspect}
                      </button>
                    ),
                },
                { id: "hint", label: copy.operatorHint, renderCell: (row) => row.investigationHint, width: "280px" },
              ]}
              emptyState={<AdminEmptyState title={copy.noIncidentLogs} body={copy.noIncidentLogsBody} />}
              getRowId={(row) => row.artifactId}
              onRowClick={(row) => focusIncidentWorkspace(row.artifactId)}
              rows={state.viewModel.incidentLogArtifacts}
            />
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            {renderSectionTitle(copy.aiJobs, copy.aiJobsBody)}
            <AdminDataTable
              columns={[
                { id: "jobId", label: copy.jobId, renderCell: (row) => row.jobId },
                { id: "jobType", label: copy.type, renderCell: (row) => row.jobTypeLabel },
                { id: "status", label: copy.status, renderCell: (row) => row.statusLabel },
                { id: "attemptCount", label: copy.attempts, renderCell: (row) => row.attemptCountLabel, align: "right" },
                { id: "lastErrorCode", label: copy.lastError, renderCell: (row) => row.lastErrorCodeLabel },
                { id: "provider", label: copy.provider, renderCell: (row) => row.providerLabel },
                { id: "queuedAt", label: copy.queued, renderCell: (row) => row.queuedAtLabel },
                { id: "startedAt", label: copy.started, renderCell: (row) => row.startedAtLabel },
                { id: "finishedAt", label: copy.finished, renderCell: (row) => row.finishedAtLabel },
                { id: "duration", label: copy.duration, renderCell: (row) => row.durationLabel },
                { id: "linkedFileId", label: copy.file, renderCell: (row) => row.linkedFileIdLabel },
                { id: "linkedRecordId", label: copy.record, renderCell: (row) => row.linkedRecordIdLabel },
              ]}
              emptyState={<AdminEmptyState title={copy.noAiJobs} body={copy.noAiJobsBody} />}
              getRowId={(row) => row.jobId}
              rows={state.viewModel.aiJobs}
            />
          </section>
        </>
      ) : null}
    </section>
  );
}
